import { useContext } from "react";
import styled, { ThemeContext, useTheme } from "styled-components/macro";
import { spacingTokens } from "../../designTokens";

export interface IIconProps {
  fill?: string; // This should be a hexcode.
  width?: number;
  height?: number;
  stroke?: string;
}

export const SuccessIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useContext(ThemeContext);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
    >
      <path
        fill={fill || theme.tertiaryBG}
        fillRule="evenodd"
        d="M12 1c6.075 0 11 4.925 11 11s-4.925 11-11 11S1 18.075 1 12 5.925 1 12 1zM5.775 12.473c-.994-.994.517-2.506 1.51-1.511l3.08 3.079 6.349-6.348c.993-.994 2.505.517 1.51 1.51l-7.103 7.104c-.418.417-1.094.417-1.511 0l-3.835-3.834zM12 3.137c-4.895 0-8.863 3.968-8.863 8.863S7.105 20.862 12 20.862s8.863-3.967 8.863-8.862S16.895 3.137 12 3.137z"
      />
    </svg>
  );
};

export const InfoIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
    >
      <path
        fill={fill}
        fillRule="evenodd"
        d="M12 1l.288.004C18.23 1.157 23 6.022 23 12c0 6.075-4.925 11-11 11S1 18.075 1 12 5.925 1 12 1zm.016 1.9l-.314.008c-2.298.09-4.48 1.052-6.098 2.696-1.72 1.692-2.693 4-2.703 6.412-.01 2.412.943 4.729 2.65 6.434 1.704 1.705 4.021 2.66 6.432 2.65l.315-.008c2.297-.09 4.48-1.052 6.097-2.697 1.72-1.691 2.694-4 2.704-6.412.01-2.41-.944-4.728-2.65-6.433-1.704-1.706-4.021-2.66-6.433-2.65zM12 10.442c.702 0 1.27.57 1.27 1.271v5.036c0 .702-.568 1.271-1.27 1.271-.702 0-1.271-.569-1.271-1.271v-5.036c0-.701.569-1.271 1.27-1.271zm0-4.442c.844 0 1.528.684 1.528 1.528S12.844 9.054 12 9.054s-1.528-.683-1.528-1.527c0-.844.684-1.528 1.528-1.528z"
      />
    </svg>
  );
};

const IconAndMessageContainer = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.secondaryTextColor};
  font-size: ${({ theme }) => theme.fontSizes.xs};
  gap: ${({ theme }) => theme.resolve("spacing_sm")};
`;

export const InfoIconWithMessage = ({ message }: { message: string }) => {
  const theme = useTheme();

  return (
    <IconAndMessageContainer>
      <InfoIcon width={20} height={20} fill={theme.tertiaryIconColor} />
      {message}
    </IconAndMessageContainer>
  );
};

export const HelpIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ? fill : theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.57538 4.57537C6.47498 2.6758 9.10067 1.5 12 1.5C14.8993 1.5 17.525 2.6758 19.4246 4.57538L19.0711 4.92893L19.4246 4.57538C21.3242 6.47498 22.5 9.10066 22.5 12C22.5 14.8993 21.3242 17.525 19.4246 19.4246C17.525 21.3242 14.8993 22.5 12 22.5C9.10066 22.5 6.47498 21.3242 4.57538 19.4246L4.92893 19.0711L4.57538 19.4246C2.6758 17.525 1.5 14.8993 1.5 12C1.5 9.10067 2.6758 6.47498 4.57537 4.57538L4.57538 4.57537ZM12 2.5C9.37653 2.5 7.00222 3.56278 5.28248 5.28249C3.56277 7.00222 2.5 9.37653 2.5 12C2.5 14.6235 3.56278 16.9978 5.28248 18.7175C7.00222 20.4372 9.37654 21.5 12 21.5C14.6235 21.5 16.9978 20.4372 18.7175 18.7175C20.4372 16.9978 21.5 14.6235 21.5 12C21.5 9.37654 20.4372 7.00222 18.7175 5.28248C16.9978 3.56278 14.6235 2.5 12 2.5Z"
        fill={fill}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.5 9.3125C8.5 7.37951 10.067 5.8125 12 5.8125C13.933 5.8125 15.5 7.37951 15.5 9.3125C15.5 11.0757 14.1961 12.5344 12.5 12.7771V14.3125C12.5 14.5886 12.2761 14.8125 12 14.8125C11.7239 14.8125 11.5 14.5886 11.5 14.3125V12.3125C11.5 12.0364 11.7239 11.8125 12 11.8125C13.3807 11.8125 14.5 10.6932 14.5 9.3125C14.5 7.93179 13.3807 6.8125 12 6.8125C10.6193 6.8125 9.5 7.93179 9.5 9.3125C9.5 9.58864 9.27614 9.8125 9 9.8125C8.72386 9.8125 8.5 9.58864 8.5 9.3125Z"
        fill={fill}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 18C12.5523 18 13 17.5523 13 17C13 16.4477 12.5523 16 12 16C11.4477 16 11 16.4477 11 17C11 17.5523 11.4477 18 12 18Z"
        fill={fill}
      />
    </svg>
  );
};

export const ErrorIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
    >
      <path
        fill={fill}
        fillRule="evenodd"
        d="M13.296 12l2.918 2.918c.358.357.36.935 0 1.296-.359.357-.939.357-1.296 0L12 13.296l-2.918 2.918c-.357.358-.935.36-1.296 0-.357-.359-.357-.939 0-1.296L10.704 12 7.786 9.082c-.358-.357-.36-.935 0-1.296.359-.357.939-.357 1.296 0L12 10.704l2.918-2.918c.357-.358.935-.36 1.296 0 .357.359.357.939 0 1.296L13.296 12zM12 23C5.925 23 1 18.075 1 12S5.925 1 12 1s11 4.925 11 11-4.925 11-11 11zm0-1.833c5.063 0 9.167-4.104 9.167-9.167S17.063 2.833 12 2.833 2.833 6.937 2.833 12 6.937 21.167 12 21.167z"
      />
    </svg>
  );
};

export const ThumbsUpIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useContext(ThemeContext);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
    >
      <path
        fill={fill || theme.primaryIconColor}
        d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-1.91l-.01-.01L23 10z"
      />
    </svg>
  );
};

export const ThumbsDownIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useContext(ThemeContext);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
    >
      <path
        fill={fill || theme.primaryIconColor}
        d="M15 3H6c-.83 0-1.54.5-1.84 1.22l-3.02 7.05C1.05 11.5 1 11.74 1 12v1.91l.01.01L1 14c0 1.1.9 2 2 2h6.31l-.95 4.57-.03.32c0 .41.17.79.44 1.06L9.83 23l6.59-6.59c.36-.36.58-.86.58-1.41V5c0-1.1-.9-2-2-2zm4 0v12h4V3h-4z"
      />
    </svg>
  );
};

export const WarningIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useContext(ThemeContext);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
    >
      <path
        fill={fill || theme.warningTextColor}
        fillRule="evenodd"
        d="M9.568 3.853c1.302-2.315 3.792-2.315 5.095 0l7.816 13.866c1.273 2.286.03 4.457-2.606 4.457H4.387c-2.634 0-3.908-2.141-2.606-4.457zm3.184 1.071c-.464-.839-.926-.666-1.303 0L3.663 18.79c-.377.724-.26 1.216.724 1.216h15.458c.724 0 1.187-.377.724-1.216zm.463 12.91c0 1.467-2.199 1.467-2.199 0s2.2-1.467 2.2 0zm-.896-9.755c.492 0 .868.376.868.868v6.137c0 .319-.26.579-.579.579h-1.013c-.319 0-.579-.232-.579-.55V8.947c0-.492.377-.868.869-.868z"
      />
    </svg>
  );
};

export const CheckIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useContext(ThemeContext);
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
    >
      <path
        fill={fill || theme.primaryIconColor}
        fillRule="evenodd"
        d="M19.686 6.324c-.418-.432-1.06-.432-1.479 0l-8.748 9.048-3.666-3.759c-.418-.432-1.061-.432-1.48 0-.418.433-.418 1.098 0 1.53l4.407 4.524c.193.2.482.333.74.333.257 0 .546-.133.739-.333l9.487-9.78c.419-.432.419-1.13 0-1.563z"
        clipRule="evenodd"
      />
    </svg>
  );
};
export const HamburgerIcon = ({
  fill,
  width = 18,
  height = 18,
}: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 26 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1 1H25"
        stroke={finalFill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M1 11H25"
        stroke={finalFill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M1 21H25"
        stroke={finalFill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const XIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useContext(ThemeContext);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
    >
      <path
        fill={fill || theme.primaryIconColor}
        fillRule="evenodd"
        d="M17.329 5.08c.44-.44 1.152-.44 1.591 0 .44.44.44 1.152 0 1.591L13.591 12l5.33 5.328c.408.408.437 1.052.087 1.494l-.088.098c-.214.213-.5.33-.796.33-.296 0-.581-.117-.795-.33L12 13.59 6.671 18.92c-.183.183-.42.294-.67.322l-.125.007c-.296 0-.582-.115-.796-.329-.44-.44-.44-1.152 0-1.592L10.408 12 5.08 6.671c-.409-.408-.438-1.051-.088-1.493l.088-.098c.44-.44 1.152-.44 1.591 0L12 10.408z"
      />
    </svg>
  );
};

export const CalendarIcon = ({
  width = 24,
  height = 24,
  fill = "#0F1D2B",
}: IIconProps) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8 2V6"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M16 2V6"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M19 4H5C3.89543 4 3 4.89543 3 6V20C3 21.1046 3.89543 22 5 22H19C20.1046 22 21 21.1046 21 20V6C21 4.89543 20.1046 4 19 4Z"
        stroke="#0F1D2B"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3 10H21"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const ClockIcon = ({ fill }: IIconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="12"
    height="12"
    viewBox="0 0 18 18"
  >
    <path
      fill={fill}
      fillRule="evenodd"
      d="M9 0C4.029 0 0 4.029 0 9s4.029 9 9 9 9-4.029 9-9-4.029-9-9-9zm0 3.24c.398 0 .72.322.72.72v4.624l3.386 1.957c.345.199.457.635.259.98-.198.344-.634.456-.979.258L8.72 9.664c-.029-.011-.054-.03-.08-.045-.214-.125-.36-.353-.36-.619V3.96c0-.398.323-.72.72-.72H9z"
    />
  </svg>
);

export const VisibleIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 20 13"
    >
      <path
        fill={finalFill}
        fillRule="evenodd"
        d="M10 0c5.941 0 10 6.47 10 6.47s-2.941 6.471-10 6.471c-7 0-10-6.47-10-6.47S4.118 0 10 0zm0 2.354c-2.294 0-4.117 1.823-4.117 4.117S7.706 10.588 10 10.588s4.117-1.823 4.117-4.117S12.294 2.354 10 2.354zm0 2.352c.975 0 1.765.79 1.765 1.765 0 .975-.79 1.764-1.765 1.764-.975 0-1.765-.79-1.765-1.764 0-.975.79-1.765 1.765-1.765z"
      />
    </svg>
  );
};

export const NonVisibleIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
    >
      <path
        fill={finalFill}
        fillRule="evenodd"
        d="M4.575 20.839c-.195.195-.511.195-.707 0l-.707-.707c-.195-.196-.195-.512 0-.707l2.56-2.56C3.16 14.973 2 12.47 2 12.47S6.118 6 12 6c1.35 0 2.604.335 3.735.851l3.69-3.69c.195-.195.511-.195.707 0l.707.707c.195.196.195.512 0 .707L17.535 7.88C20.318 9.79 22 12.471 22 12.471s-2.941 6.47-10 6.47c-1.75 0-3.25-.404-4.516-1.01l-2.909 2.908zm10.777-10.777l-1.725 1.725c.089.21.138.441.138.684 0 .975-.79 1.764-1.765 1.764-.243 0-.474-.049-.684-.137l-1.724 1.724c.674.483 1.505.766 2.408.766 2.294 0 4.117-1.823 4.117-4.117 0-.904-.283-1.734-.765-2.409zM12 8.354c-2.294 0-4.117 1.823-4.117 4.117 0 .655.148 1.271.414 1.818l1.943-1.944.001-.02c.07-.865.765-1.553 1.633-1.614l1.944-1.943c-.547-.266-1.163-.414-1.818-.414z"
      />
    </svg>
  );
};

export const SearchIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
    >
      <path
        fill={finalFill}
        fillRule="evenodd"
        d="M19.757 18.573l-3.945-3.96c1.015-1.164 1.57-2.628 1.57-4.153 0-3.562-3.001-6.46-6.69-6.46C7.001 4 4 6.898 4 10.46s3.002 6.46 6.691 6.46c1.385 0 2.705-.403 3.834-1.169l3.974 3.99c.166.167.39.259.629.259.226 0 .441-.083.605-.235.346-.322.357-.856.024-1.192zM10.69 5.685c2.727 0 4.946 2.142 4.946 4.775 0 2.633-2.219 4.775-4.946 4.775s-4.945-2.142-4.945-4.775c0-2.633 2.218-4.775 4.945-4.775z"
      />
    </svg>
  );
};

export const OpportunitiesIcon = ({ fill, width, height }: IIconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={width}
    height={height}
    viewBox="0 0 24 24"
  >
    <path
      fill={fill}
      fillRule="evenodd"
      d="M3.89 2.895C2.913 4.462 2.364 6.33 2.364 8.2c0 5.607 4.573 10.129 10.244 10.129 3.78 0 7.318-2.11 9.085-5.427h.001c.366-.724 1.403-.181 1.036.542-1.89 3.557-5.488 5.848-9.511 6.029v4.04h3.048c.793 0 .793 1.205 0 1.205H8.951c-.793 0-.793-1.206 0-1.206H12v-4.039C5.963 19.173 1.146 14.229 1.146 8.2c0-2.11.61-4.16 1.707-5.969.427-.663 1.464-.06 1.036.664zM12.61 0c4.573 0 8.293 3.678 8.293 8.2 0 4.522-3.72 8.2-8.293 8.2-4.574 0-8.293-3.678-8.293-8.2 0-4.522 3.72-8.2 8.293-8.2zm0 1.206c-3.903 0-7.074 3.135-7.074 6.994 0 3.858 3.171 6.994 7.074 6.994 3.902 0 7.073-3.136 7.073-6.994 0-3.859-3.171-6.994-7.073-6.994zm.255 2.201h.001l1.122 2.364 2.643.316c.24.078.321.315.16.473l-1.923 1.812.481 2.522c.08.236-.24.394-.4.316l-2.324-1.261-2.324 1.26c-.24.079-.481-.078-.4-.315l.481-2.522L8.458 6.56c-.16-.158-.08-.394.16-.473l2.644-.316 1.122-2.364c.08-.236.4-.236.481 0z"
    />
  </svg>
);

export const AnalyticsIcon = ({ fill, width, height }: IIconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={width}
    height={height}
    viewBox="0 0 24 24"
  >
    <path
      fill={fill}
      fillRule="evenodd"
      d="M23 8.19c0 1.211-.982 2.192-2.191 2.192-.444 0-.858-.136-1.206-.366l-3.608 3.609c.215.34.343.735.343 1.166 0 1.21-.986 2.196-2.196 2.196-1.206 0-2.191-.986-2.191-2.196 0-.444.136-.858.36-1.201l-1.508-1.514c-.348.229-.757.366-1.201.366-.444 0-.858-.136-1.206-.366l-3.375 3.375c.229.343.366.757.366 1.206 0 1.206-.985 2.191-2.191 2.191-1.21 0-2.196-.985-2.196-2.191 0-1.21.985-2.196 2.196-2.196.444 0 .858.132 1.206.366l3.37-3.37c-.229-.348-.366-.762-.366-1.206 0-1.21.985-2.196 2.196-2.196 1.205 0 2.19.986 2.19 2.196 0 .444-.135.858-.365 1.206l1.509 1.509c.343-.234.762-.366 1.206-.366.462 0 .889.145 1.245.387l3.595-3.595c-.228-.343-.365-.757-.365-1.2 0-1.211.981-2.192 2.19-2.192C22.019 6 23 6.98 23 8.191z"
    />
  </svg>
);

/**
 * @param fill
 * @param width
 * @param height
 */
export const UsersIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const { primaryIconColor } = useContext(ThemeContext);
  const actualFill = fill || primaryIconColor;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
    >
      <path
        fill={actualFill}
        fillRule="evenodd"
        d="M6.301 14.803c.104 0 .205.004.305.014.287.028.513.28.513.574 0 .16-.065.31-.184.418-.12.11-.286.167-.453.153-.06-.005-.12-.008-.18-.008H4.286c-1.177 0-2.133.957-2.133 2.133v.237H1v-.236c0-1.812 1.474-3.286 3.285-3.285zm6.812-1.462c.693 0 1.367.201 1.948.583 1.011.664 1.616 1.778 1.616 2.981v1.42h-1.153v-1.42c0-1.33-1.082-2.412-2.412-2.412H10.89c-1.33 0-2.412 1.082-2.412 2.412v1.42H7.324l.002-1.42c0-.798.262-1.555.757-2.189.683-.874 1.706-1.375 2.806-1.375zm6.601 1.461c1.812 0 3.286 1.474 3.286 3.286v.236h-1.152v-.236c0-1.177-.957-2.134-2.134-2.134h-2.016c-.04 0-.079.001-.118.004-.156.009-.308-.046-.424-.155-.114-.107-.18-.26-.18-.419 0-.307.243-.56.554-.577.055-.003.111-.005.168-.005zm-14.42-6.22c1.51 0 2.737 1.228 2.737 2.738s-1.227 2.738-2.737 2.738-2.738-1.228-2.738-2.737c0-1.511 1.228-2.739 2.738-2.739zm13.412 0c1.51 0 2.739 1.228 2.739 2.738s-1.23 2.738-2.739 2.738c-1.51 0-2.738-1.228-2.738-2.737 0-1.511 1.227-2.739 2.738-2.739zM5.294 9.734c-.875 0-1.586.712-1.586 1.586 0 .875.711 1.586 1.586 1.586.875 0 1.586-.711 1.586-1.586 0-.874-.711-1.586-1.586-1.586zm13.412 0c-.875 0-1.586.712-1.586 1.586 0 .875.711 1.586 1.586 1.586.875 0 1.586-.711 1.586-1.586 0-.874-.711-1.586-1.586-1.586zM12 6c1.9 0 3.444 1.545 3.444 3.444 0 1.9-1.545 3.444-3.444 3.444-1.9 0-3.444-1.546-3.444-3.444C8.556 7.544 10.1 6 12 6zm0 1.152c-1.264 0-2.293 1.028-2.293 2.292 0 1.264 1.029 2.293 2.293 2.293 1.264 0 2.293-1.029 2.293-2.293 0-1.264-1.029-2.292-2.293-2.292z"
      />
    </svg>
  );
};

export const CaretLeft = ({ fill }: IIconProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      fill={fill}
      fillRule="evenodd"
      d="M14.729 4.094c.463-.459 1.211-.459 1.673 0 .464.46.464 1.206 0 1.665L10.11 12l6.29 6.24c.465.46.465 1.206 0 1.666l-.102.091c-.465.365-1.14.335-1.57-.091l-7.132-7.073c-.225-.224-.347-.523-.347-.833 0-.31.122-.609.347-.833z"
    />
  </svg>
);

export const ArrowRight = ({ width = 24, height = 24, fill }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3 12L21 12"
        stroke={fill}
        strokeWidth="2"
        strokeLinecap="round"
      />
      <path
        d="M15 18L21 12L15 6"
        stroke={fill}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
};

export const CaretRight = ({ width = 24, height = 24, fill }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="Icon / Action / Caret Right">
        <path
          id="Combined Shape"
          fillRule="evenodd"
          clipRule="evenodd"
          d="M7.59818 5.75957L13.889 12L7.59807 18.2405C7.13396 18.7002 7.13396 19.4462 7.59818 19.9061C8.05993 20.3647 8.80837 20.3647 9.27133 19.9061L16.4029 12.833C16.6283 12.6088 16.75 12.3105 16.75 12C16.75 11.6897 16.6281 11.3907 16.4029 11.1675L9.27144 4.09404C8.84147 3.6681 8.16595 3.63768 7.70144 4.00267L7.59821 4.09391C7.13396 4.55369 7.13396 5.2997 7.59818 5.75957ZM7.77413 4.27154C8.13917 3.90949 8.72991 3.90949 9.09539 4.27154L9.09539 4.27156C8.72991 3.90951 8.13918 3.90951 7.77413 4.27156C7.53643 4.50697 7.45333 4.83733 7.52482 5.13915C7.45332 4.83732 7.53642 4.50696 7.77413 4.27154ZM14.2447 12L7.77413 18.418C7.61891 18.5718 7.52962 18.766 7.50624 18.9663C7.52962 18.766 7.61892 18.5718 7.77413 18.4181L14.2447 12L14.2447 12ZM16.3934 11.5691C16.3507 11.4885 16.2952 11.4128 16.2269 11.345L16.2269 11.345C16.2952 11.4128 16.3507 11.4885 16.3934 11.5691Z"
          fill={fill}
        />
      </g>
    </svg>
  );
};

export const LoadingIcon = ({ width, height, fill }: IIconProps) => {
  const theme = useContext(ThemeContext);

  return (
    <svg
      width={width || "38"}
      height={height || "38"}
      viewBox="0 0 38 38"
      xmlns="http://www.w3.org/2000/svg"
      stroke={fill || (theme?.brandColor ?? "#0090e3")}
    >
      <g fill="none" fillRule="evenodd">
        <g transform="translate(1 1)" strokeWidth="2">
          <circle strokeOpacity=".5" cx="18" cy="18" r="18" />
          <path d="M36 18c0-9.94-8.06-18-18-18">
            <animateTransform
              attributeName="transform"
              type="rotate"
              from="0 18 18"
              to="360 18 18"
              dur="1s"
              repeatCount="indefinite"
            />
          </path>
        </g>
      </g>
    </svg>
  );
};

export const UserIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_10937_118344)">
        <path
          d="M8.00033 8.66667C9.84127 8.66667 11.3337 7.17428 11.3337 5.33333C11.3337 3.49238 9.84127 2 8.00033 2C6.15938 2 4.66699 3.49238 4.66699 5.33333C4.66699 7.17428 6.15938 8.66667 8.00033 8.66667Z"
          stroke={fill}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M13.3337 13.9998C13.3337 12.5853 12.7718 11.2288 11.7716 10.2286C10.7714 9.22841 9.41481 8.6665 8.00033 8.6665C6.58584 8.6665 5.22928 9.22841 4.22909 10.2286C3.2289 11.2288 2.66699 12.5853 2.66699 13.9998"
          stroke={fill}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_10937_118344">
          <rect width="16" height="16" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const EditIcon = ({ fill, width = 20, height = 20 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M17.645 5.67633C18.0856 5.23585 18.3332 4.63839 18.3333 4.01538C18.3333 3.39237 18.0859 2.79484 17.6454 2.35425C17.205 1.91366 16.6075 1.66609 15.9845 1.66602C15.3615 1.66594 14.764 1.91335 14.3234 2.35383L3.20169 13.478C3.00821 13.6709 2.86512 13.9084 2.78503 14.1697L1.68419 17.7963C1.66266 17.8684 1.66103 17.945 1.67949 18.0179C1.69794 18.0908 1.73579 18.1574 1.78902 18.2105C1.84225 18.2636 1.90888 18.3014 1.98183 18.3197C2.05477 18.3381 2.13133 18.3363 2.20336 18.3147L5.83086 17.2147C6.09183 17.1353 6.32934 16.9931 6.52253 16.8005L17.645 5.67633Z"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.5 4.16699L15.8333 7.50033"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const TranslationsIcon = ({ fill, width, height }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryTextColor;
  return (
    <svg
      width={width || "20"}
      height={height || "21"}
      viewBox="0 0 20 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1.36365 3.89551H11.1854"
        stroke={fill}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.78253 1.86328V3.89537"
        stroke={fill}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.5081 18.7975L14.5722 9.31445L18.6364 18.7975"
        stroke={fill}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.8627 15.749H17.2816"
        stroke="#0F1D2B"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.15331 3.89551C9.15331 3.89551 8.29475 7.17274 6.2455 9.6481C4.19626 12.1235 2.04102 13.3786 2.04102 13.3786"
        stroke={fill}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.81466 11.3468C8.81466 11.3468 7.61075 10.4895 6.33805 8.96547C5.06535 7.4414 4.4118 6.2666 4.4118 6.2666"
        stroke={fill}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const PDFIcon = ({ fill, width, height }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_38275_36640)">
        <path
          d="M4.69922 2C4.69922 1.17157 5.37079 0.5 6.19922 0.5H16.1992C16.2587 0.5 16.3157 0.523628 16.3578 0.565686L22.8335 7.04142C22.8756 7.08348 22.8992 7.14052 22.8992 7.2V22C22.8992 22.8284 22.2276 23.5 21.3992 23.5H6.19922C5.37079 23.5 4.69922 22.8284 4.69922 22V2Z"
          stroke={fill}
        />
        <path
          d="M16.1992 0.300781V5.20078C16.1992 6.30535 17.0946 7.20078 18.1992 7.20078H23.0992"
          stroke={fill}
        />
        <path
          d="M14 10.5996H2C1.44772 10.5996 1 11.0473 1 11.5996V19.5996C1 20.1519 1.44772 20.5996 2 20.5996H14C14.5523 20.5996 15 20.1519 15 19.5996V11.5996C15 11.0473 14.5523 10.5996 14 10.5996Z"
          fill="#DD2025"
        />
        <path
          d="M2.49939 17.6V13.2363H4.22098C4.55195 13.2363 4.83391 13.2995 5.06686 13.426C5.29981 13.551 5.47737 13.725 5.59953 13.948C5.72311 14.1696 5.7849 14.4253 5.7849 14.715C5.7849 15.0048 5.7224 15.2605 5.5974 15.4821C5.4724 15.7037 5.29129 15.8763 5.05408 15.9998C4.81828 16.1234 4.53277 16.1852 4.19754 16.1852H3.10024V15.4459H4.04839C4.22595 15.4459 4.37226 15.4153 4.48731 15.3542C4.60379 15.2917 4.69044 15.2058 4.74726 15.0964C4.8055 14.9856 4.83462 14.8585 4.83462 14.715C4.83462 14.5701 4.8055 14.4437 4.74726 14.3358C4.69044 14.2264 4.60379 14.1419 4.48731 14.0822C4.37084 14.0211 4.22311 13.9906 4.04413 13.9906H3.42197V17.6H2.49939ZM7.93103 17.6H6.38415V13.2363H7.94381C8.38273 13.2363 8.76057 13.3237 9.07734 13.4984C9.3941 13.6717 9.63771 13.921 9.80816 14.2463C9.98004 14.5716 10.066 14.9608 10.066 15.4139C10.066 15.8684 9.98004 16.2591 9.80816 16.5858C9.63771 16.9125 9.39268 17.1632 9.07307 17.3379C8.75489 17.5126 8.37421 17.6 7.93103 17.6ZM7.30674 16.8095H7.89268C8.1654 16.8095 8.39481 16.7612 8.58089 16.6646C8.76839 16.5666 8.90901 16.4153 9.00276 16.2108C9.09793 16.0048 9.14552 15.7392 9.14552 15.4139C9.14552 15.0915 9.09793 14.828 9.00276 14.6234C8.90901 14.4189 8.7691 14.2683 8.58302 14.1717C8.39694 14.0751 8.16753 14.0268 7.89481 14.0268H7.30674V16.8095ZM10.7494 17.6V13.2363H13.6386V13.997H11.672V15.0368H13.4468V15.7974H11.672V17.6H10.7494Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_38275_36640">
          <rect width={width} height={height} fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const CaretDownIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const { primaryIconColor } = useContext(ThemeContext);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
    >
      <path
        fill={fill || primaryIconColor}
        fillRule="evenodd"
        d="M18.24 7.598c.46-.464 1.206-.464 1.666 0l.091.103c.365.465.335 1.14-.091 1.57l-7.073 7.132c-.224.225-.523.347-.833.347-.31 0-.609-.122-.833-.347L4.094 9.27c-.459-.463-.459-1.211 0-1.673.46-.464 1.206-.464 1.665 0L12 13.89z"
      />
    </svg>
  );
};

export const CaretUpIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
    >
      <g fill={finalFill} fillRule="evenodd">
        <g fill="#0F1D2B">
          <g>
            <path
              d="M12 7.25c.31 0 .609.122.833.347l7.073 7.132c.459.463.459 1.211 0 1.673-.46.464-1.206.464-1.665 0L12 10.11l-6.24 6.29c-.46.465-1.206.465-1.666 0l-.091-.102c-.365-.465-.335-1.14.091-1.57l7.073-7.132c.224-.225.523-.347.833-.347z"
              transform="translate(-1014 -974) translate(1014 974)"
            />
          </g>
        </g>
      </g>
    </svg>
  );
};

export const LocationIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const { primaryIconColor } = useContext(ThemeContext);
  const actualFill = fill || primaryIconColor;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
    >
      <path
        fill={actualFill}
        fillRule="evenodd"
        d="M6.117 13.59l.035.05.132.19 5.068 7.145c.148.209.39.333.65.333.258 0 .5-.124.649-.333l5.055-7.139c.049-.067.095-.132.142-.19l.031-.046v-.001c.915-1.39 1.288-3.056 1.051-4.695-.237-1.638-1.068-3.138-2.341-4.222C15.316 3.597 13.686 3 12 3c-1.686 0-3.315.597-4.588 1.682-1.274 1.084-2.105 2.583-2.343 4.222-.236 1.639.137 3.305 1.052 4.695l-.004-.01zM12 7.308c.714 0 1.399.284 1.904.788.504.505.788 1.19.788 1.904s-.284 1.4-.788 1.904c-.505.505-1.19.788-1.904.788s-1.399-.283-1.904-.788c-.504-.505-.788-1.19-.788-1.904s.284-1.399.788-1.904c.505-.504 1.19-.788 1.904-.788z"
      />
    </svg>
  );
};

export const DeleteIcon = ({ width = 16, height = 16 }: IIconProps) => {
  const theme = useTheme();
  const strokeColor = theme.resolve(
    "color_buttonIcon_icon_destructive_enabled"
  );
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        d="M2 3.9987H14M12.6667 3.9987V13.332C12.6667 13.9987 12 14.6654 11.3333 14.6654H4.66667C4 14.6654 3.33333 13.9987 3.33333 13.332V3.9987M5.33333 3.9987V2.66536C5.33333 1.9987 6 1.33203 6.66667 1.33203H9.33333C10 1.33203 10.6667 1.9987 10.6667 2.66536V3.9987M6.66667 7.33203V11.332M9.33333 7.33203V11.332"
        stroke={strokeColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const SoldByIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox={`0 0 24 24`}
    >
      <g fill={fill} fillRule="evenodd">
        <g fill={fill}>
          <g>
            <path
              d="M20.634 12.731l-7.872-7.919c-.225-.225-.54-.405-.9-.405L4.44 4.002c-.765-.044-1.394.54-1.44 1.26v.18l.45 7.38c0 .314.18.63.404.854l7.828 7.92c.27.269.584.404.944.404s.72-.135.945-.405l7.018-6.974c.54-.54.54-1.35.045-1.89zm-10.077-1.17c-.72.72-1.844.72-2.564 0s-.72-1.845 0-2.564c.72-.72 1.845-.72 2.564 0 .72.72.72 1.89 0 2.564z"
              transform="translate(-185 -1019) translate(185 1019)"
            />
          </g>
        </g>
      </g>
    </svg>
  );
};

export const ManufacturedByIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox={`0 0 24 24`}
    >
      <g fill="none" fillRule="evenodd">
        <g fill={finalFill}>
          <g>
            <path
              d="M7.388 9.493c.33 0 .612.26.612.613v6.823l4.635-3.506c.4-.305.988-.023.988.494v3.012l4.612-3.506c.424-.306 1.012-.023.99.495v8.941c0 .33-.26.612-.613.612H4.73c-.33 0-.612-.259-.612-.612V10.106c0-.33.259-.612.612-.612zm.847 10.142l-1.505.001c-.118 0-.212.094-.212.212v1.176c0 .118.094.211.212.211h1.505c.118 0 .212-.093.212-.211v-1.177c0-.118-.094-.212-.212-.212zm4.189.001h-1.506c-.117 0-.211.094-.211.212v1.176c0 .118.094.211.211.211h1.506c.117 0 .211-.093.211-.211v-1.176c0-.118-.094-.212-.211-.212zm4.212 0H15.13c-.117 0-.211.094-.211.212v1.176c0 .118.094.211.211.211h1.506c.118 0 .211-.093.211-.211v-1.176c0-.118-.093-.212-.211-.212zM14.893 2.742c.542-2.33 4.965-2.353 4.965 1.035S18.211 7.753 16 7.753c-2.188 0-6.188-1.459-7.789-1.53-1.246-.047-1.552 1.412-1.623 2.024-.024.165-.141.282-.306.282h-.517c-.142 0-.283-.14-.33-.305-.071-1.036-.118-4.447 2.87-4.024 4.635.636 6.047.895 6.588-1.458z"
              transform="translate(-234 -1019) translate(234 1019)"
            />
          </g>
        </g>
      </g>
    </svg>
  );
};

export const NavQuotesIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox={`0 0 24 24`}
    >
      <g fill="none" fillRule="evenodd">
        <g fill={finalFill}>
          <g>
            <path
              d="M2 20.827V3.174C2 2.526 2.526 2 3.174 2h13.208c.648 0 1.173.526 1.174 1.174v7.313l-.89.889V3.174c0-.158-.127-.284-.284-.285H3.174c-.158 0-.284.128-.285.285v17.653c0 .157.128.284.285.284h13.208c.157 0 .284-.127.285-.284v-2.854l.889-.889v3.743c-.001.647-.526 1.172-1.174 1.173H3.174C2.526 22 2 21.474 2 20.827zm13.114-2.871l5.694-5.694-1.73-1.726-5.692 5.69 1.728 1.73zm6.529-6.528c.476-.478.476-1.252-.001-1.729-.478-.477-1.252-.477-1.73 0l-.268.27 1.729 1.73.27-.27zm-9.196 7.663l1.731-.342c.115-.021.22-.078.303-.16l.067-.067-1.73-1.73-.066.068c-.083.083-.138.188-.16.303l-.342 1.73c-.011.056.006.112.046.152.04.04.097.057.151.046zM7.43 6.067c0-.025-.01-.05-.028-.068-.018-.018-.043-.028-.068-.028H4.8c-.053 0-.097.043-.097.096V8.6c0 .053.044.096.097.096h2.533c.025 0 .05-.01.068-.028.018-.018.028-.043.028-.068V6.067zm7.424 1.74h-6.3v.89h6.3v-.89zm-7.424 2.927c0-.025-.01-.05-.028-.068-.018-.018-.043-.029-.068-.029H4.8c-.053 0-.097.044-.097.097v2.533c0 .053.044.096.097.096h2.533c.025 0 .05-.01.068-.029.018-.017.028-.042.028-.067v-2.533zm7.424 1.74h-6.3v.89h6.3v-.89zm-7.52 2.83H4.8c-.053 0-.097.043-.097.096v2.533c0 .053.044.097.097.097h2.533c.025 0 .05-.01.068-.029.017-.017.028-.043.028-.068V15.4c0-.025-.01-.05-.028-.067-.018-.019-.043-.029-.068-.029zm1.222 2.726h2.742v-.89H8.554v.89z"
              transform="translate(-578 -1019) translate(578 1019)"
            />
          </g>
        </g>
      </g>
    </svg>
  );
};

export const SettingsIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
    >
      <g fill={fill} fillRule="evenodd">
        <g fill={fill}>
          <g>
            <path
              d="M12.926 1c.666 0 1.232.479 1.342 1.136l.257 1.525c.538.161 1.057.377 1.554.639l1.278-.91c.229-.165.5-.253.785-.253.364 0 .704.143.96.4l1.37 1.37c.474.469.533 1.209.148 1.751l-.91 1.273c.252.478.46.979.62 1.498l1.544.262c.657.11 1.136.675 1.127 1.343v1.935c0 .666-.478 1.232-1.135 1.342l-1.545.257c-.156.515-.367 1.015-.62 1.498l.91 1.278c.386.543.321 1.283-.147 1.751l-1.37 1.37c-.258.257-.598.4-.96.4-.286 0-.562-.087-.791-.253l-1.273-.91c-.478.252-.979.46-1.498.62l-.262 1.545c-.11.657-.676 1.135-1.342 1.135h-1.94c-.667 0-1.232-.478-1.342-1.135l-.257-1.545c-.501-.156-.989-.354-1.453-.597l-1.291.92c-.23.165-.5.252-.786.252-.363 0-.704-.142-.96-.4l-1.37-1.37c-.474-.469-.533-1.208-.147-1.75l.906-1.26c-.258-.477-.47-.979-.63-1.493l-1.563-.267C1.478 14.247 1 13.681 1 13.015v-1.934c0-.667.478-1.233 1.135-1.343l1.526-.257c.156-.52.358-1.02.61-1.503l-.919-1.291c-.386-.544-.321-1.284.147-1.752l1.374-1.37c.253-.257.598-.4.961-.4.285 0 .561.087.79.253l1.265.91c.478-.257.979-.469 1.493-.63l.267-1.562c.11-.657.676-1.135 1.342-1.135zm.006 1.238h-1.934c-.06 0-.11.042-.12.102l-.33 1.944c-.042.24-.22.432-.455.496-.675.175-1.323.45-1.92.81-.212.123-.478.114-.68-.028L5.907 4.43c-.018-.014-.041-.023-.069-.023-.023 0-.056.005-.087.036l-1.37 1.37c-.041.042-.046.111-.014.157l1.145 1.608c.142.198.155.46.032.672-.354.601-.616 1.254-.786 1.93-.06.239-.258.418-.5.46l-1.917.321c-.06.01-.102.06-.102.12v1.934c0 .06.042.11.102.12l1.944.33c.24.041.432.22.496.455.175.675.45 1.323.809 1.92.124.212.115.478-.028.68l-1.13 1.586c-.032.05-.028.115.013.156l1.37 1.37c.033.033.07.037.088.037.023 0 .045-.01.069-.023l1.608-1.145c.106-.078.235-.115.359-.115.105 0 .216.027.312.082.593.34 1.228.602 1.885.772.234.06.413.254.455.497l.321 1.93c.01.06.06.101.12.101h1.934c.06 0 .11-.04.12-.1l.326-1.931c.036-.24.216-.437.455-.497.675-.175 1.323-.442 1.925-.8.212-.124.478-.115.676.028l1.595 1.135c.018.014.041.023.069.023.023 0 .056-.005.087-.037l1.37-1.37c.041-.041.046-.11.014-.156l-1.135-1.6c-.143-.201-.152-.463-.028-.675.36-.602.626-1.25.8-1.926.06-.234.254-.413.497-.454l1.93-.322c.06-.01.102-.06.102-.12l.002.003v-1.934c0-.06-.041-.11-.101-.12l-1.93-.326c-.24-.036-.437-.216-.497-.455-.176-.675-.442-1.323-.8-1.926-.124-.211-.115-.477.027-.675l1.135-1.595c.032-.05.028-.115-.013-.156l-1.37-1.37c-.033-.033-.07-.037-.088-.037-.023 0-.045.01-.069.023l-1.585 1.14c-.202.142-.464.15-.676.027-.615-.363-1.277-.634-1.97-.809-.24-.06-.42-.258-.46-.5l-.322-1.917c-.01-.06-.06-.102-.12-.102zm-.93 5.012c2.62 0 4.748 2.128 4.748 4.747 0 2.62-2.128 4.748-4.747 4.748-2.62 0-4.748-2.128-4.748-4.748 0-2.619 2.128-4.747 4.748-4.747zm0 1.242c-1.934 0-3.506 1.572-3.506 3.506 0 1.935 1.572 3.507 3.507 3.507 1.934 0 3.506-1.572 3.506-3.507 0-1.934-1.572-3.506-3.506-3.506z"
              transform="translate(-326 -596) translate(326 596)"
            />
          </g>
        </g>
      </g>
    </svg>
  );
};

export const LeadsIcon = ({ fill }: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
    >
      <g fill="none" fillRule="evenodd">
        <g fill={finalFill}>
          <g>
            <path
              d="M19.417 9.639c.289.045.505.29.513.583v1.982c0 .141-.07.273-.186.35l-5.645 4.852v3.335c.007.178-.094.345-.256.42h-.001l-3.265 1.633-.21.046c-.082 0-.164-.025-.233-.07-.139-.085-.226-.234-.233-.396v-4.968l-5.645-4.852c-.116-.077-.186-.209-.186-.35v-1.982c.008-.293.224-.538.513-.583zm-.42.933H5.003v1.423l5.644 4.827c.11.086.177.213.187.35v4.456l2.332-1.166v-3.29c.01-.137.078-.264.187-.35l5.644-4.827v-1.423zm-3.032-5.83c.946 0 1.798.57 2.157 1.445.36.874.156 1.88-.516 2.544-.672.665-1.68.86-2.55.49-.87-.367-1.433-1.225-1.423-2.17.013-1.28 1.053-2.31 2.332-2.31zM5.938 5.36c.75-.31 1.612-.139 2.186.435.573.574.745 1.436.435 2.186S7.517 9.22 6.705 9.22C5.597 9.22 4.7 8.32 4.7 7.213c0-.811.488-1.542 1.238-1.853zm10.027.314c-.368-.006-.725.136-.988.395-.264.259-.412.612-.412.981 0 .773.627 1.4 1.4 1.4.773 0 1.4-.627 1.4-1.4 0-.369-.149-.722-.411-.981-.264-.26-.62-.401-.989-.395zm-9.26.466l-.116.006c-.537.059-.956.514-.956 1.067 0 .593.48 1.074 1.072 1.074.593 0 1.074-.48 1.074-1.074 0-.592-.48-1.073-1.074-1.073zm3.983-2.354c.575-.239 1.237-.107 1.677.333.44.44.573 1.103.334 1.678-.238.575-.799.95-1.422.95-.85 0-1.54-.689-1.54-1.54 0-.622.376-1.183.95-1.421zm.589.815l-.099.008c-.288.047-.507.297-.507.598 0 .336.271.607.606.607.335 0 .606-.271.606-.607 0-.335-.271-.606-.606-.606zm2.21-3.381c.575-.239 1.237-.107 1.677.334.44.44.573 1.102.334 1.677-.238.575-.8.95-1.422.95-.85 0-1.54-.689-1.54-1.539 0-.622.376-1.184.95-1.422zm.589.816c-.16 0-.315.063-.429.177-.114.114-.177.268-.177.429 0 .16.063.315.177.429.114.114.268.177.429.177.335 0 .606-.271.606-.606 0-.335-.271-.606-.606-.606z"
              transform="translate(-428 -1019) translate(428 1019)"
            />
          </g>
        </g>
      </g>
    </svg>
  );
};

export const StackIcon = ({ fill }: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
    >
      <g fill="none" fillRule="evenodd">
        <g fill={finalFill}>
          <g>
            <path
              d="M11.557.925c4.023 0 7.299 1.015 7.608 2.629l.017.122.006.13v16.387c0 1.717-3.23 2.83-7.337 2.88l-.294.002c-4.024 0-7.3-1.015-7.608-2.629l-.018-.122-.006-.13V3.806c0-1.758 3.387-2.882 7.632-2.882zm6.584 15.187l-.058.039c-1.349.826-3.84 1.306-6.526 1.306-2.552 0-4.94-.435-6.327-1.191l-.191-.11-.068-.044v4.083c0 .83 2.827 1.782 6.295 1.833l.29.002c3.41 0 6.264-.873 6.561-1.706l.02-.073.005-.057-.001-4.082zm-13.17-5.5v3.962c0 .855 2.986 1.836 6.586 1.836 3.409 0 6.263-.872 6.56-1.705l.02-.073.005-.058-.001-3.961-.058.038c-1.287.788-3.615 1.262-6.161 1.303l-.365.003c-2.552 0-4.94-.435-6.327-1.19l-.191-.111-.068-.044zm13.17-5.268l-.058.039c-1.349.826-3.84 1.306-6.526 1.306-2.552 0-4.94-.435-6.327-1.191l-.191-.11-.068-.044v3.732c0 .83 2.827 1.782 6.295 1.833l.29.002c3.41 0 6.264-.872 6.561-1.705l.02-.073.005-.057-.001-3.732zm-6.584-3.373c-3.6 0-6.586.982-6.586 1.836 0 .854 2.986 1.836 6.586 1.836s6.585-.982 6.585-1.836c0-.854-2.986-1.836-6.585-1.836zM8.524 2.985c.23.174.277.502.102.733l.007-.005.008.024c.02.068.074.142.15.199.038.029.079.051.117.066l.055.017.088.008c0 .001 0 .004-.005.009.174-.231.502-.277.733-.103.231.174.277.502.103.733-.143.189-.349.32-.59.376l-.124.022-.141.008c-.302 0-.609-.107-.866-.301-.258-.195-.445-.46-.527-.75-.094-.336-.037-.677.157-.934.174-.23.502-.276.733-.102z"
              transform="translate(-526 -1019) translate(526 1019)"
            />
          </g>
        </g>
      </g>
    </svg>
  );
};

export const QuoteIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
    >
      <g fill="none" fillRule="evenodd">
        <g fill={finalFill}>
          <g>
            <path
              d="M2 20.827V3.174C2 2.526 2.526 2 3.174 2h13.208c.648 0 1.173.526 1.174 1.174v7.313l-.89.889V3.174c0-.158-.127-.284-.284-.285H3.174c-.158 0-.284.128-.285.285v17.653c0 .157.128.284.285.284h13.208c.157 0 .284-.127.285-.284v-2.854l.889-.889v3.743c-.001.647-.526 1.172-1.174 1.173H3.174C2.526 22 2 21.474 2 20.827zm13.114-2.871l5.694-5.694-1.73-1.726-5.692 5.69 1.728 1.73zm6.529-6.528c.476-.478.476-1.252-.001-1.729-.478-.477-1.252-.477-1.73 0l-.268.27 1.729 1.73.27-.27zm-9.196 7.663l1.731-.342c.115-.021.22-.078.303-.16l.067-.067-1.73-1.73-.066.068c-.083.083-.138.188-.16.303l-.342 1.73c-.011.056.006.112.046.152.04.04.097.057.151.046zM7.43 6.067c0-.025-.01-.05-.028-.068-.018-.018-.043-.028-.068-.028H4.8c-.053 0-.097.043-.097.096V8.6c0 .053.044.096.097.096h2.533c.025 0 .05-.01.068-.028.018-.018.028-.043.028-.068V6.067zm7.424 1.74h-6.3v.89h6.3v-.89zm-7.424 2.927c0-.025-.01-.05-.028-.068-.018-.018-.043-.029-.068-.029H4.8c-.053 0-.097.044-.097.097v2.533c0 .053.044.096.097.096h2.533c.025 0 .05-.01.068-.029.018-.017.028-.042.028-.067v-2.533zm7.424 1.74h-6.3v.89h6.3v-.89zm-7.52 2.83H4.8c-.053 0-.097.043-.097.096v2.533c0 .053.044.097.097.097h2.533c.025 0 .05-.01.068-.029.017-.017.028-.043.028-.068V15.4c0-.025-.01-.05-.028-.067-.018-.019-.043-.029-.068-.029zm1.222 2.726h2.742v-.89H8.554v.89z"
              transform="translate(-774 -1018) translate(774 1018)"
            />
          </g>
        </g>
      </g>
    </svg>
  );
};

export const OrderIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
    >
      <g fill="none" fillRule="evenodd">
        <g fill={finalFill}>
          <g>
            <path
              d="M12.951 3.298c.166-.098.366-.118.548-.056.182.061.33.198.402.376l2.6 4.329h3.797c.515-.006 1 .243 1.296.665.356.53.442 1.197.233 1.802l-2.553 8.264c-.277.747-.992 1.242-1.789 1.237H6.515c-.795.002-1.506-.492-1.782-1.237l-2.554-8.231c-.221-.613-.137-1.295.227-1.835.295-.423.78-.671 1.296-.665H7.5l2.6-4.335c.189-.314.596-.415.91-.226.316.187.42.595.233.91l-2.188 3.65h5.891l-2.18-3.663c-.12-.151-.168-.346-.132-.535.036-.189.152-.353.318-.45zM3.702 9.277c-.092-.025-.19.006-.252.08-.103.209-.103.455 0 .664l2.566 8.265c.103.18.292.292.499.3h10.95c.214 0 .412-.115.52-.3l2.566-8.251c.102-.21.102-.456 0-.665-.054-.07-.14-.105-.226-.093h-3.006l.565 1.004c.12.151.167.346.132.535-.036.189-.152.353-.319.45-.165.098-.365.118-.547.056-.182-.061-.33-.199-.403-.376l-1.004-1.67H8.257l-1.03 1.656c-.206.261-.573.33-.858.162-.287-.167-.406-.52-.28-.827l.592-.99zm12.287 6.649c.368 0 .665.297.665.664 0 .368-.297.665-.665.665H8.011c-.368 0-.665-.297-.665-.665 0-.367.297-.664.665-.664zm0-2.66c.368 0 .665.297.665.665 0 .367-.297.665-.665.665H8.011c-.368 0-.665-.298-.665-.665 0-.368.297-.665.665-.665z"
              transform="translate(-820 -1017) translate(820 1017)"
            />
          </g>
        </g>
      </g>
    </svg>
  );
};

export const PlusIcon = ({ fill, width, height }: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M9.899 3.13a.929.929 0 0 0-.828.924v5.017H4.054a.929.929 0 0 0-.929.929l.005.101c.05.466.445.828.924.828h5.017v5.017c0 .513.416.929.929.929l.101-.005a.929.929 0 0 0 .828-.924v-5.018l5.017.001c.513 0 .929-.416.929-.929l-.005-.101a.929.929 0 0 0-.924-.828h-5.018l.001-5.017A.929.929 0 0 0 10 3.125l-.101.005z"
        fill={finalFill}
      />
    </svg>
  );
};

export const ShareIcon = ({ fill, width, height }: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 21 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.6665 7C16.0472 7 17.1665 5.88071 17.1665 4.5C17.1665 3.11929 16.0472 2 14.6665 2C13.2858 2 12.1665 3.11929 12.1665 4.5C12.1665 5.88071 13.2858 7 14.6665 7Z"
        stroke="#0F1D2B"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.6665 12.8333C6.04722 12.8333 7.1665 11.714 7.1665 10.3333C7.1665 8.95254 6.04722 7.83325 4.6665 7.83325C3.28579 7.83325 2.1665 8.95254 2.1665 10.3333C2.1665 11.714 3.28579 12.8333 4.6665 12.8333Z"
        stroke="#0F1D2B"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M14.6665 18.6665C16.0472 18.6665 17.1665 17.5472 17.1665 16.1665C17.1665 14.7858 16.0472 13.6665 14.6665 13.6665C13.2858 13.6665 12.1665 14.7858 12.1665 16.1665C12.1665 17.5472 13.2858 18.6665 14.6665 18.6665Z"
        stroke="#0F1D2B"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.8252 11.5916L12.5169 14.9082"
        stroke={finalFill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.5085 5.7583L6.8252 9.07497"
        stroke={finalFill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const LightCaretRightIcon = ({
  width = 16,
  height = 16,
  fill,
}: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.disabledButtonTextColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11 8c0 .158-.06.316-.182.437l-4.754 4.715a.626.626 0 0 1-.881 0 .613.613 0 0 1 0-.873L9.496 8 5.183 3.721a.613.613 0 0 1 0-.873.626.626 0 0 1 .88 0l4.755 4.715c.121.12.182.279.182.437z"
        fill={fill}
      />
    </svg>
  );
};

export const FacebookIcon = ({
  width = 26,
  height = 26,
  fill = "#1877F2",
}: IIconProps) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
    >
      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
      <g
        id="SVGRepo_tracerCarrier"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></g>
      <g id="SVGRepo_iconCarrier">
        <path
          fill={fill}
          d="M15 8a7 7 0 00-7-7 7 7 0 00-1.094 13.915v-4.892H5.13V8h1.777V6.458c0-1.754 1.045-2.724 2.644-2.724.766 0 1.567.137 1.567.137v1.723h-.883c-.87 0-1.14.54-1.14 1.093V8h1.941l-.31 2.023H9.094v4.892A7.001 7.001 0 0015 8z"
        ></path>
        <path
          fill="#ffffff"
          d="M10.725 10.023L11.035 8H9.094V6.687c0-.553.27-1.093 1.14-1.093h.883V3.87s-.801-.137-1.567-.137c-1.6 0-2.644.97-2.644 2.724V8H5.13v2.023h1.777v4.892a7.037 7.037 0 002.188 0v-4.892h1.63z"
        ></path>
      </g>
    </svg>
  );
};

export const LinkedinIcon = ({
  width = 26,
  height = 26,
  fill = "#0A66C2",
}: IIconProps) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
    >
      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
      <g
        id="SVGRepo_tracerCarrier"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></g>
      <g id="SVGRepo_iconCarrier">
        <path
          fill={fill}
          d="M12.225 12.225h-1.778V9.44c0-.664-.012-1.519-.925-1.519-.926 0-1.068.724-1.068 1.47v2.834H6.676V6.498h1.707v.783h.024c.348-.594.996-.95 1.684-.925 1.802 0 2.135 1.185 2.135 2.728l-.001 3.14zM4.67 5.715a1.037 1.037 0 01-1.032-1.031c0-.566.466-1.032 1.032-1.032.566 0 1.031.466 1.032 1.032 0 .566-.466 1.032-1.032 1.032zm.889 6.51h-1.78V6.498h1.78v5.727zM13.11 2H2.885A.88.88 0 002 2.866v10.268a.88.88 0 00.885.866h10.226a.882.882 0 00.889-.866V2.865a.88.88 0 00-.889-.864z"
        ></path>
      </g>
    </svg>
  );
};
export const WhatsappIcon = ({
  width = 26,
  height = 26,
  fill = "#000000",
}: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.disabledButtonTextColor;
  return (
    <svg
      width={width}
      height={height}
      fill={fill}
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
      <g
        id="SVGRepo_tracerCarrier"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></g>
      <g id="SVGRepo_iconCarrier">
        <path d="M11.42 9.49c-.19-.09-1.1-.54-1.27-.61s-.29-.09-.42.1-.48.6-.59.73-.21.14-.4 0a5.13 5.13 0 0 1-1.49-.92 5.25 5.25 0 0 1-1-1.29c-.11-.18 0-.28.08-.38s.18-.21.28-.32a1.39 1.39 0 0 0 .18-.31.38.38 0 0 0 0-.33c0-.09-.42-1-.58-1.37s-.3-.32-.41-.32h-.4a.72.72 0 0 0-.5.23 2.1 2.1 0 0 0-.65 1.55A3.59 3.59 0 0 0 5 8.2 8.32 8.32 0 0 0 8.19 11c.44.19.78.3 1.05.39a2.53 2.53 0 0 0 1.17.07 1.93 1.93 0 0 0 1.26-.88 1.67 1.67 0 0 0 .11-.88c-.05-.07-.17-.12-.36-.21z"></path>
        <path d="M13.29 2.68A7.36 7.36 0 0 0 8 .5a7.44 7.44 0 0 0-6.41 11.15l-1 3.85 3.94-1a7.4 7.4 0 0 0 3.55.9H8a7.44 7.44 0 0 0 5.29-12.72zM8 14.12a6.12 6.12 0 0 1-3.15-.87l-.22-.13-2.34.61.62-2.28-.14-.23a6.18 6.18 0 0 1 9.6-7.65 6.12 6.12 0 0 1 1.81 4.37A6.19 6.19 0 0 1 8 14.12z"></path>
      </g>
    </svg>
  );
};

export const TwitterIcon = ({
  width = 26,
  height = 26,
  fill = "#000000",
}: IIconProps) => {
  return (
    <svg
      width={width}
      height={height}
      fill={fill}
      xmlns="http://www.w3.org/2000/svg"
      x="0px"
      y="0px"
      viewBox="0 0 30 30"
    >
      <path d="M26.37,26l-8.795-12.822l0.015,0.012L25.52,4h-2.65l-6.46,7.48L11.28,4H4.33l8.211,11.971L12.54,15.97L3.88,26h2.65 l7.182-8.322L19.42,26H26.37z M10.23,6l12.34,18h-2.1L8.12,6H10.23z"></path>
    </svg>
  );
};

/**
 * A simple colored SVG rectangle. Used for image placeholders, for example.
 * Not really an icon, but it works the same way so this file is a good place
 * for it.
 */
export const SvgRectangle = ({ fill, width = 50, height = 50 }: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.tertiaryBG;
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox={`0 0 ${width} ${height}`}>
      <rect fill={finalFill} width={"100%"} height={"100%"} x={0} y={0} />
    </svg>
  );
};

export const HomeIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 20 18"
    >
      <g fill={finalFill} fillRule="evenodd">
        <g fill="#000">
          <path
            d="M21.98 12.736c-.16-.203-.31-.428-.49-.599-1.25-1.283-2.502-2.566-3.752-3.838-.96-.973-1.921-1.957-2.882-2.93-.64-.652-1.27-1.304-1.91-1.945-.57-.567-1.361-.567-1.921.01-.971.984-1.931 1.978-2.902 2.962-.02.021-.04.032-.09.075L4.721 9.88c-.82.823-1.64 1.657-2.45 2.491-.1.107-.181.257-.271.385v.278c.26.396.52.791.78 1.176.23.332.43.364.71.086.476-.488.953-.976 1.431-1.463 0 2.387-.01 4.785 0 7.172 0 .577.33.983.79.983 1.552.011 3.112 0 4.663 0 .01.011.03.011.08 0v-5.805c.07 0 .12-.01.17-.01h2.822c.14 0 .18.042.18.213-.01 1.775 0 3.56 0 5.335V21H18.288c.57 0 .89-.385.89-1.09v-6.966l1.141 1.16c.09.086.17.182.26.257.21.182.4.17.57-.075.29-.417.57-.834.851-1.262-.02-.107-.02-.192-.02-.288"
            transform="translate(-2 -3)"
          />
        </g>
      </g>
    </svg>
  );
};

export const MenuIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={width}
      height={height}
    >
      <path fill="none" d="M0 0H24V24H0z" />
      <path
        d="M21 18v2H3v-2h18zM17.404 3.904L22 8.5l-4.596 4.596-1.414-1.414L19.172 8.5 15.99 5.318l1.414-1.414zM12 11v2H3v-2h9zm0-7v2H3V4h9z"
        fill={finalFill}
      />
    </svg>
  );
};

export const ActionsIcon = ({ fill, width = 16 }: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={width}
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="8" cy="4.5" r="1" fill={finalFill} />
      <circle cx="8" cy="8.5" r="1" fill={finalFill} />
      <circle cx="8" cy="12.5" r="1" fill={finalFill} />
    </svg>
  );
};

export const DistributorsIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={width}
      height={height}
    >
      <path
        d="M12.6171 1.73696C12.2437 1.74035 11.8843 1.87275 11.606 2.10935C11.3277 2.34595 11.1494 2.67063 11.1046 3.02287C11.0597 3.37511 11.1512 3.73078 11.3621 4.02363C11.573 4.31648 11.8887 4.52647 12.2506 4.61441V8.33247C11.7084 8.38424 11.184 8.54487 10.7121 8.80373C10.2401 9.06259 9.83152 9.41377 9.51326 9.83399L7.64582 8.80921C7.74572 8.4664 7.7106 8.10076 7.54704 7.7808C7.38349 7.46083 7.10273 7.20856 6.75738 7.07117C6.41202 6.93379 6.02579 6.92072 5.67105 7.03451C5.31632 7.14829 5.01744 7.38103 4.83042 7.68918C4.6434 7.99732 4.58109 8.35973 4.65516 8.7084C4.72922 9.05706 4.93459 9.36811 5.23276 9.58323C5.53094 9.79835 5.90145 9.9028 6.27487 9.87698C6.64829 9.85115 6.99898 9.69677 7.26121 9.44289L9.12865 10.4663C8.90471 10.9383 8.78891 11.45 8.78891 11.9678C8.78891 12.4855 8.90471 12.9973 9.12865 13.4693L4.88598 15.7958C4.62374 15.5419 4.27306 15.3876 3.89964 15.3618C3.52622 15.336 3.1557 15.4404 2.85753 15.6555C2.55935 15.8706 2.35399 16.1816 2.27992 16.5303C2.20585 16.879 2.26817 17.2414 2.45519 17.5495C2.6422 17.8577 2.94109 18.0904 3.29582 18.2042C3.65055 18.318 4.03679 18.305 4.38214 18.1676C4.72749 18.0302 5.00826 17.7779 5.17181 17.4579C5.33536 17.1379 5.37048 16.7723 5.27058 16.4295L9.51326 14.1016C9.83152 14.5218 10.2401 14.873 10.7121 15.1319C11.184 15.3907 11.7084 15.5514 12.2506 15.6031V18.8602C11.888 18.9491 11.5721 19.1606 11.3619 19.455C11.1517 19.7495 11.0618 20.1066 11.109 20.4595C11.1562 20.8124 11.3372 21.1368 11.6182 21.3719C11.8991 21.6071 12.2607 21.7368 12.6352 21.7368C13.0096 21.7368 13.3712 21.6071 13.6522 21.3719C13.9331 21.1368 14.1141 20.8124 14.1613 20.4595C14.2085 20.1066 14.1186 19.7495 13.9084 19.455C13.6983 19.1606 13.3823 18.9491 13.0198 18.8602V15.6031C13.562 15.5514 14.0864 15.3907 14.5583 15.1319C15.0302 14.873 15.4388 14.5218 15.7571 14.1016L19.229 16.007C19.1286 16.3498 19.1632 16.7155 19.3264 17.0357C19.4895 17.3559 19.77 17.6086 20.1152 17.7464C20.4604 17.8842 20.8467 17.8977 21.2016 17.7843C21.5565 17.6708 21.8557 17.4383 22.0431 17.1303C22.2305 16.8223 22.2931 16.4599 22.2194 16.1111C22.1456 15.7624 21.9405 15.4511 21.6424 15.2357C21.3444 15.0203 20.9739 14.9156 20.6004 14.9412C20.2269 14.9668 19.8761 15.121 19.6136 15.3748L16.1417 13.4693C16.3656 12.9973 16.4814 12.4855 16.4814 11.9678C16.4814 11.45 16.3656 10.9383 16.1417 10.4663L17.7432 9.58854C18.0054 9.84242 18.3561 9.99672 18.7295 10.0225C19.103 10.0484 19.4735 9.94392 19.7717 9.7288C20.0698 9.51368 20.2752 9.20263 20.3493 8.85396C20.4233 8.50529 20.361 8.14297 20.174 7.83483C19.987 7.52668 19.6881 7.29386 19.3334 7.18007C18.9786 7.06629 18.5924 7.07935 18.247 7.21674C17.9017 7.35412 17.6209 7.6064 17.4574 7.92637C17.2938 8.24633 17.2587 8.61197 17.3586 8.95477L15.7571 9.83399C15.4388 9.41377 15.0302 9.06259 14.5583 8.80373C14.0864 8.54487 13.562 8.38424 13.0198 8.33247V4.61441C13.3847 4.5258 13.7027 4.31312 13.9135 4.01663C14.1244 3.72015 14.2135 3.36045 14.1638 3.00576C14.1142 2.65107 13.9293 2.326 13.6443 2.09215C13.3592 1.8583 12.9937 1.73192 12.6171 1.73696ZM12.6156 2.46488C12.7185 2.4624 12.8209 2.47958 12.9167 2.51537C13.0125 2.55116 13.0997 2.60483 13.1733 2.67324C13.2468 2.74165 13.3052 2.82341 13.3449 2.91363C13.3846 3.00386 13.4048 3.10074 13.4043 3.19853C13.4044 3.29453 13.3845 3.38966 13.3459 3.47837C13.3073 3.56707 13.2506 3.64765 13.1792 3.71555C13.1078 3.78346 13.0229 3.83727 12.9296 3.87402C12.8362 3.91077 12.7362 3.92973 12.6351 3.92973C12.5341 3.92973 12.434 3.91077 12.3407 3.87402C12.2473 3.83727 12.1625 3.78346 12.0911 3.71555C12.0196 3.64765 11.963 3.56707 11.9244 3.47837C11.8857 3.38966 11.8659 3.29453 11.8659 3.19853C11.8651 3.00739 11.9431 2.82353 12.0832 2.68639C12.2234 2.54925 12.4145 2.46973 12.6156 2.46488ZM6.14492 7.68311C6.24782 7.68082 6.35017 7.69814 6.44588 7.7341C6.5416 7.77007 6.62874 7.82394 6.70215 7.89248C6.77556 7.96103 6.83375 8.04283 6.87325 8.13313C6.91276 8.22342 6.93279 8.3204 6.93216 8.41819C6.93252 8.51437 6.91289 8.60966 6.87439 8.69862C6.83589 8.78758 6.77929 8.86843 6.70783 8.93657C6.63636 9.0047 6.55145 9.05873 6.45795 9.09562C6.36444 9.1325 6.2642 9.1515 6.16295 9.1515C6.06171 9.1515 5.96146 9.1325 5.86796 9.09562C5.77446 9.05873 5.68954 9.0047 5.61808 8.93657C5.54661 8.86843 5.49001 8.78758 5.45151 8.69862C5.41302 8.60966 5.39339 8.51437 5.39374 8.41819C5.3925 8.22657 5.47054 8.04211 5.61104 7.90462C5.75154 7.76713 5.94327 7.6876 6.14492 7.68311ZM18.8219 7.83002C18.9248 7.82754 19.0271 7.84472 19.1229 7.88051C19.2187 7.9163 19.306 7.96998 19.3795 8.03839C19.4531 8.10679 19.5114 8.18855 19.5511 8.27878C19.5908 8.369 19.611 8.4658 19.6106 8.56359C19.6107 8.65959 19.5908 8.75472 19.5522 8.84343C19.5135 8.93213 19.4569 9.01271 19.3855 9.08062C19.314 9.14852 19.2292 9.20241 19.1359 9.23916C19.0425 9.27591 18.9424 9.29479 18.8414 9.29479C18.7403 9.29479 18.6403 9.27591 18.5469 9.23916C18.4536 9.20241 18.3688 9.14852 18.2973 9.08062C18.2259 9.01271 18.1692 8.93213 18.1306 8.84343C18.092 8.75472 18.0721 8.65959 18.0722 8.56359C18.0713 8.37246 18.1493 8.18867 18.2895 8.05153C18.4296 7.9144 18.6207 7.83487 18.8219 7.83002ZM12.545 9.04606C12.9564 9.0346 13.3661 9.1017 13.7498 9.24338C14.1335 9.38505 14.4833 9.59839 14.7786 9.87082C15.0739 10.1433 15.3087 10.4693 15.469 10.8294C15.6293 11.1896 15.7119 11.5767 15.712 11.9678C15.7127 12.3521 15.6336 12.7327 15.4793 13.088C15.325 13.4432 15.0985 13.7661 14.8127 14.0381C14.527 14.31 14.1875 14.5259 13.8139 14.6731C13.4402 14.8203 13.0397 14.896 12.6351 14.896C12.2306 14.896 11.8301 14.8203 11.4564 14.6731C11.0827 14.5259 10.7433 14.31 10.4575 14.0381C10.1717 13.7661 9.94521 13.4432 9.79091 13.088C9.63661 12.7327 9.55756 12.3521 9.55829 11.9678C9.55833 11.2074 9.87027 10.4769 10.4279 9.93143C10.9855 9.38594 11.7449 9.06834 12.545 9.04606ZM20.6893 15.6673C20.7922 15.6644 20.8946 15.6811 20.9906 15.7166C21.0865 15.752 21.174 15.8055 21.2478 15.8736C21.3217 15.9418 21.3804 16.0232 21.4204 16.1133C21.4605 16.2034 21.4811 16.3003 21.481 16.3981C21.4803 16.5914 21.399 16.7767 21.2548 16.9131C21.1106 17.0496 20.9154 17.1262 20.7118 17.1262C20.5083 17.1262 20.3131 17.0496 20.1689 16.9131C20.0247 16.7767 19.9433 16.5914 19.9426 16.3981C19.9425 16.2079 20.0205 16.0251 20.1599 15.8887C20.2993 15.7522 20.4892 15.6728 20.6893 15.6673ZM3.76669 16.0897C3.86958 16.0869 3.97204 16.1037 4.06798 16.1391C4.16392 16.1746 4.2514 16.2279 4.32523 16.2961C4.39906 16.3642 4.45774 16.4457 4.4978 16.5358C4.53786 16.6259 4.55847 16.7227 4.55843 16.8205C4.55879 16.9167 4.53916 17.012 4.50067 17.1009C4.46217 17.1899 4.40557 17.2708 4.33411 17.339C4.26265 17.4071 4.17773 17.4611 4.08422 17.498C3.99072 17.5349 3.89047 17.5539 3.78922 17.5539C3.68797 17.5539 3.58772 17.5349 3.49422 17.498C3.40071 17.4611 3.31579 17.4071 3.24433 17.339C3.17286 17.2708 3.11626 17.1899 3.07777 17.1009C3.03927 17.012 3.01965 16.9167 3.02001 16.8205C3.01992 16.6303 3.09786 16.4476 3.23728 16.3112C3.37669 16.1747 3.56661 16.0953 3.76669 16.0897ZM12.6156 19.5409C12.7185 19.5384 12.8209 19.5556 12.9167 19.5914C13.0125 19.6272 13.0997 19.6809 13.1733 19.7493C13.2468 19.8177 13.3052 19.8995 13.3449 19.9897C13.3846 20.0799 13.4048 20.1768 13.4043 20.2746C13.4044 20.3706 13.3845 20.4656 13.3459 20.5543C13.3073 20.643 13.2506 20.7236 13.1792 20.7915C13.1078 20.8594 13.0229 20.9133 12.9296 20.9501C12.8362 20.9868 12.7362 21.0057 12.6351 21.0057C12.5341 21.0057 12.434 20.9868 12.3407 20.9501C12.2473 20.9133 12.1625 20.8594 12.0911 20.7915C12.0196 20.7236 11.963 20.643 11.9244 20.5543C11.8857 20.4656 11.8659 20.3706 11.8659 20.2746C11.8651 20.0834 11.9431 19.8996 12.0832 19.7624C12.2234 19.6253 12.4145 19.5458 12.6156 19.5409Z"
        fill={finalFill}
      />
    </svg>
  );
};

export const FiltersIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill={fill}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fill={fill}
        fillRule="evenodd"
        clipRule="evenodd"
        d="M5.77963 8.69155H2.73984C2.33984 8.69155 2 8.37155 2 7.92339C2 7.47608 2.33984 7.13454 2.73984 7.13454H5.77963C6.12042 5.34364 7.60006 4 9.40001 4C11.2 4 12.7 5.34372 13.0398 7.11385H21.2602C21.6602 7.11385 22 7.45454 22 7.9027C22 8.35001 21.6602 8.69155 21.2602 8.69155H13.0398C12.7 10.4825 11.1797 11.8261 9.40001 11.8261C7.61946 11.8261 6.11946 10.4825 5.77963 8.69155ZM11.6174 7.90229C11.6174 6.60113 10.6173 5.55656 9.39702 5.55656C8.17663 5.55656 7.17663 6.60113 7.17663 7.90229C7.17663 9.20344 8.17663 10.248 9.39702 10.248C10.6174 10.248 11.6174 9.20344 11.6174 7.90229Z"
      />
      <path
        fill={fill}
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18.2204 14.9607H21.2602C21.6602 14.9607 22 15.3024 22 15.7289C22 16.1762 21.6602 16.5177 21.2602 16.5177H18.2204C17.8796 18.3086 16.3999 19.6523 14.6 19.6523C12.8 19.6523 11.3 18.3085 10.9602 16.5384H2.73984C2.33984 16.5384 2 16.1761 2 15.7496C2 15.3022 2.33984 14.9607 2.73984 14.9607H10.9602C11.3 13.1698 12.8203 11.8262 14.6 11.8262C16.3805 11.8262 17.8805 13.1698 18.2204 14.9607ZM12.3803 15.7488C12.3803 17.0499 13.3804 18.0945 14.6007 18.0945C15.8211 18.0945 16.8211 17.0282 16.8211 15.7488C16.8211 14.4476 15.8211 13.403 14.6007 13.403C13.3803 13.403 12.3803 14.4476 12.3803 15.7488Z"
      />
    </svg>
  );
};

// New filter icon from Figma - uses design tokens for colors
export const FiltersIconV2 = ({
  fill,
  stroke,
  width = 20,
  height = 20,
}: IIconProps) => {
  const theme = useTheme();
  const finalStroke =
    stroke || fill || theme.resolve("color_buttonIcon_icon_enabled");

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M17.5 3.33325H11.6667"
        stroke={finalStroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8.33333 3.33325H2.5"
        stroke={finalStroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17.5 10H10"
        stroke={finalStroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.66667 10H2.5"
        stroke={finalStroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17.5 16.6667H13.3333"
        stroke={finalStroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 16.6667H2.5"
        stroke={finalStroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M11.6667 1.66675V5.00008"
        stroke={finalStroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.66666 8.33325V11.6666"
        stroke={finalStroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.3333 15V18.3333"
        stroke={finalStroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const CartIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill={finalFill}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M8.667 20.8c0 1.21-1 2.2-2.223 2.2a2.208 2.208 0 0 1-2.21-2.2c0-1.21.988-2.2 2.21-2.2 1.223 0 2.223.99 2.223 2.2zm8.889-2.2c-1.223 0-2.212.99-2.212 2.2 0 1.21.99 2.2 2.212 2.2 1.222 0 2.222-.99 2.222-2.2 0-1.21-1-2.2-2.222-2.2zm.44-5.5c.505 0 .995-.17 1.39-.484.394-.313.669-.75.778-1.239L22 4.3H6.444V3.2c0-.583-.234-1.143-.65-1.556A2.234 2.234 0 0 0 4.222 1H2v2.2h2.222v12.1c0 .583.234 1.143.651 1.556.417.412.982.644 1.571.644h13.334c0-.584-.234-1.143-.651-1.556a2.234 2.234 0 0 0-1.571-.644H6.444v-2.2h11.552z" />
      <path
        d="M12.4 12h1.195V9.595H16V8.4h-2.405V6H12.4v2.4H10v1.195h2.4V12z"
        // This may look weird if the background is not white. Should it be hardcoded?
        fill={theme.primaryBG}
      />
    </svg>
  );
};

export const CartOutlineIcon = ({
  fill,
  width = 20,
  height = 20,
}: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryTextColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 21 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2.20834 2.04175H3.87501L6.09168 12.3917C6.17299 12.7708 6.3839 13.1096 6.6881 13.35C6.9923 13.5903 7.37077 13.717 7.75834 13.7084H15.9083C16.2877 13.7078 16.6554 13.5778 16.9509 13.3399C17.2463 13.1021 17.4518 12.7705 17.5333 12.4001L18.9083 6.20841H4.76668M8.00001 17.8334C8.00001 18.2937 7.62691 18.6667 7.16668 18.6667C6.70644 18.6667 6.33334 18.2937 6.33334 17.8334C6.33334 17.3732 6.70644 17.0001 7.16668 17.0001C7.62691 17.0001 8.00001 17.3732 8.00001 17.8334ZM17.1667 17.8334C17.1667 18.2937 16.7936 18.6667 16.3333 18.6667C15.8731 18.6667 15.5 18.2937 15.5 17.8334C15.5 17.3732 15.8731 17.0001 16.3333 17.0001C16.7936 17.0001 17.1667 17.3732 17.1667 17.8334Z"
        stroke={finalFill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const StorefrontIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill={fill}
      stroke={fill}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.12 21H20.28C20.6783 21 21 20.6783 21 20.28V8.13H9.12V21ZM20.28 3H9.12V6.69H21V3.72C21 3.32175 20.6783 3 20.28 3ZM3 3.72V20.28C3 20.6783 3.32175 21 3.72 21H7.68V3H3.72C3.32175 3 3 3.32175 3 3.72Z"
        fill={fill}
      />
    </svg>
  );
};

export const PackagingIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill={fill}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.2325 3.29531C12.0833 3.23551 11.9167 3.23551 11.7675 3.29531L4.3075 6.27906L7.3125 7.48031L15.005 4.40406L12.2325 3.29531ZM16.6875 5.07781L8.995 8.15406L12 9.35531L19.6925 6.27906L16.6875 5.07781ZM20.75 7.20281L12.625 10.4528V20.3553L20.75 17.1053V7.20406V7.20281ZM11.375 20.3566V10.4516L3.25 7.20281V17.1066L11.375 20.3566ZM11.3038 2.13406C11.7507 1.95531 12.2493 1.95531 12.6962 2.13406L21.6075 5.69906C21.7234 5.7455 21.8227 5.82555 21.8926 5.92893C21.9626 6.0323 22 6.15425 22 6.27906V17.1066C21.9998 17.3563 21.9249 17.6003 21.7847 17.8071C21.6446 18.0139 21.4457 18.1739 21.2138 18.2666L12.2325 21.8591C12.0833 21.9189 11.9167 21.9189 11.7675 21.8591L2.7875 18.2666C2.55531 18.1741 2.35618 18.0141 2.2158 17.8074C2.07543 17.6006 2.00026 17.3565 2 17.1066V6.27906C2.00003 6.15425 2.03743 6.0323 2.10738 5.92893C2.17734 5.82555 2.27664 5.7455 2.3925 5.69906L11.3038 2.13406Z"
        fill={fill}
      />
    </svg>
  );
};

const Protection = styled.span`
  svg {
    fill: none;
    path,
    circle {
      fill: none;
      stroke: black;
    }
  }
`;

export const EmailIcon = ({ fill }: IIconProps) => {
  const theme = useTheme();
  const stroke = fill ?? theme.primaryIconColor;
  return (
    <Protection>
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M22 12V4.5H12H2V12V19.5H12"
          stroke={stroke}
          fill="none"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M23 18.5H16"
          stroke={stroke}
          fill="none"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M20.5 16L23 18.5L20.5 21"
          stroke={stroke}
          fill="none"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M2 4.5L12 12L22 4.5"
          stroke={stroke}
          fill="none"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Protection>
  );
};

/**
 * This looks like a email icon but is specifically used for contact requests
 **/
export const ContactRequestIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M21 6H21.5C21.5 5.72386 21.2761 5.5 21 5.5V6ZM12 12.7826L11.6991 13.1819C11.8772 13.3162 12.1228 13.3162 12.3009 13.1819L12 12.7826ZM3 6V5.5C2.72386 5.5 2.5 5.72386 2.5 6H3ZM21 19V19.5C21.2761 19.5 21.5 19.2761 21.5 19H21ZM3 19H2.5C2.5 19.2761 2.72386 19.5 3 19.5V19ZM20.6991 5.6007L11.6991 12.3833L12.3009 13.1819L21.3009 6.3993L20.6991 5.6007ZM12.3009 12.3833L3.30093 5.6007L2.69907 6.3993L11.6991 13.1819L12.3009 12.3833ZM3 6.5H21V5.5H3V6.5ZM20.5 6V19H21.5V6H20.5ZM21 18.5H3V19.5H21V18.5ZM3.5 19V6H2.5V19H3.5Z"
        fill={fill}
      />
    </svg>
  );
};

export const LabFlaskIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useContext(ThemeContext);
  const finalFill = fill || theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill={finalFill}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M14.136 4h.593c.296 0 .296-2 0-2H11.95"
        style={{ fill: "none" }}
        stroke={finalFill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.766 4h-.593c-.296 0-.296-2 0-2h2.778"
        style={{ fill: "none" }}
        stroke={finalFill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.952 3.5V9l-6.5 10c-.667 1.167-1.1 3.5 2.5 3.5h6"
        style={{ fill: "none" }}
        stroke={finalFill}
        strokeLinejoin="round"
      />
      <path
        d="M13.952 3.5V9l6.5 10c.667 1.167 1.1 3.5-2.5 3.5h-6"
        style={{ fill: "none" }}
        stroke={finalFill}
        strokeLinejoin="round"
      />
      <path
        d="M5.951 15.21c.611.756 2.2 2.155 3.667 1.701 1.833-.567 6-2.979 7.833-2.411"
        style={{ fill: "none" }}
        stroke={finalFill}
        strokeLinecap="round"
      />
      <path
        d="M17.951 20h-4.5"
        style={{ fill: "none" }}
        stroke={finalFill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export function RegistrationIcon({
  fill,
  width = 24,
  height = 24,
}: IIconProps) {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M18 8V16" stroke={fill} strokeLinecap="round" />
      <path d="M22 12L14 12" stroke={fill} strokeLinecap="round" />
      <circle cx="8.5" cy="7.5" r="4" stroke={fill} />
      <path
        d="M14 20C14 19.2121 13.8577 18.4319 13.5813 17.7039C13.3049 16.9759 12.8998 16.3145 12.3891 15.7574C11.8784 15.2002 11.272 14.7583 10.6048 14.4567C9.93747 14.1552 9.22227 14 8.5 14C7.77773 14 7.06253 14.1552 6.39524 14.4567C5.72795 14.7583 5.12163 15.2002 4.61091 15.7574C4.10019 16.3145 3.69506 16.9759 3.41866 17.7039C3.14226 18.4319 3 19.2121 3 20"
        stroke={fill}
        strokeLinecap="round"
      />
    </svg>
  );
}

export const CompanyIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.5 3C4.5 2.72386 4.72386 2.5 5 2.5H19C19.2761 2.5 19.5 2.72386 19.5 3V20.5H21C21.2761 20.5 21.5 20.7239 21.5 21C21.5 21.2761 21.2761 21.5 21 21.5H3C2.72386 21.5 2.5 21.2761 2.5 21C2.5 20.7239 2.72386 20.5 3 20.5H4.5V3ZM5.5 20.5H9.5V15C9.5 14.7239 9.72386 14.5 10 14.5H14C14.2761 14.5 14.5 14.7239 14.5 15V20.5H18.5V3.5H5.5V20.5ZM13.5 20.5V15.5H10.5V20.5H13.5ZM8.5 7C8.5 6.72386 8.72386 6.5 9 6.5H11C11.2761 6.5 11.5 6.72386 11.5 7C11.5 7.27614 11.2761 7.5 11 7.5H9C8.72386 7.5 8.5 7.27614 8.5 7ZM12.5 7C12.5 6.72386 12.7239 6.5 13 6.5H15C15.2761 6.5 15.5 6.72386 15.5 7C15.5 7.27614 15.2761 7.5 15 7.5H13C12.7239 7.5 12.5 7.27614 12.5 7ZM8.5 11C8.5 10.7239 8.72386 10.5 9 10.5H11C11.2761 10.5 11.5 10.7239 11.5 11C11.5 11.2761 11.2761 11.5 11 11.5H9C8.72386 11.5 8.5 11.2761 8.5 11ZM12.5 11C12.5 10.7239 12.7239 10.5 13 10.5H15C15.2761 10.5 15.5 10.7239 15.5 11C15.5 11.2761 15.2761 11.5 15 11.5H13C12.7239 11.5 12.5 11.2761 12.5 11Z"
        fill={fill}
      />
    </svg>
  );
};

export const ProductsV2Icon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M11.75 1.56699C11.9047 1.47767 12.0953 1.47767 12.25 1.56699L20.9103 6.56699C21.065 6.6563 21.1603 6.82137 21.1603 7V17C21.1603 17.1786 21.065 17.3437 20.9103 17.433L12.25 22.433C12.0953 22.5223 11.9047 22.5223 11.75 22.433L3.08975 17.433C2.93505 17.3437 2.83975 17.1786 2.83975 17V7C2.83975 6.82137 2.93505 6.6563 3.08975 6.56699L11.75 1.56699ZM12 2.57735L3.83975 7.28867V16.7113L12 21.4227L20.1603 16.7113V7.28868L12 2.57735ZM16 12C16 14.2091 14.2091 16 12 16C9.79086 16 8 14.2091 8 12C8 9.79086 9.79086 8 12 8C14.2091 8 16 9.79086 16 12Z"
        fill={fill}
      />
    </svg>
  );
};

export const DownloadIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 22 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M11 13V1M11 13L7 9M11 13L15 9M1 15L1.621 17.485C1.72915 17.9177 1.97882 18.3018 2.33033 18.5763C2.68184 18.8508 3.11501 18.9999 3.561 19H18.439C18.885 18.9999 19.3182 18.8508 19.6697 18.5763C20.0212 18.3018 20.2708 17.9177 20.379 17.485L21 15"
        stroke={fill}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const LinkIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.5443 10.4565C12.7252 9.63768 11.6144 9.17773 10.4563 9.17773C9.29815 9.17773 8.18741 9.63768 7.3683 10.4565L4.2793 13.5444C3.46018 14.3636 3 15.4745 3 16.6329C3 17.7914 3.46018 18.9023 4.2793 19.7215C5.09842 20.5406 6.20939 21.0007 7.3678 21.0007C8.52621 21.0007 9.63718 20.5406 10.4563 19.7215L12.0003 18.1775"
        stroke={fill}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.4563 13.5443C11.2754 14.3631 12.3861 14.823 13.5443 14.823C14.7024 14.823 15.8132 14.3631 16.6323 13.5443L19.7213 10.4563C20.5404 9.63718 21.0006 8.52621 21.0006 7.3678C21.0006 6.20939 20.5404 5.09842 19.7213 4.2793C18.9022 3.46018 17.7912 3 16.6328 3C15.4744 3 14.3634 3.46018 13.5443 4.2793L12.0003 5.8233"
        stroke={fill}
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

/**
 * This icon looks like 3 boxes stacked in a triangle.
 */
export const AttributesNavIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12.8143 2.53576C12.9335 2.48808 13.0665 2.48808 13.1857 2.53576L17.1857 4.13576C17.3755 4.21169 17.5 4.39555 17.5 4.6V9.4C17.5 9.60445 17.3755 9.78831 17.1857 9.86424L13.1857 11.4642C13.0665 11.5119 12.9335 11.5119 12.8143 11.4642L8.8143 9.86424C8.62448 9.78831 8.5 9.60445 8.5 9.4V4.6C8.5 4.39555 8.62448 4.21169 8.8143 4.13576L12.8143 2.53576ZM9.5 5.30474V9.06148L12.5 10.2615V6.35474L9.5 5.30474ZM13.5 6.35474V10.2615L16.5 9.06148V5.30474L13.5 6.35474ZM15.5757 4.56878L13 5.47026L10.4243 4.56878L13 3.53852L15.5757 4.56878ZM6.8143 10.5358C6.93351 10.4881 7.06649 10.4881 7.1857 10.5358L11.1857 12.1358C11.3755 12.2117 11.5 12.3955 11.5 12.6V17.4C11.5 17.6045 11.3755 17.7883 11.1857 17.8642L7.1857 19.4642C7.06649 19.5119 6.93351 19.5119 6.8143 19.4642L2.8143 17.8642C2.62448 17.7883 2.5 17.6045 2.5 17.4V12.6C2.5 12.3955 2.62448 12.2117 2.8143 12.1358L6.8143 10.5358ZM3.5 13.3047V17.0615L6.5 18.2615V14.3547L3.5 13.3047ZM7.5 14.3547V18.2615L10.5 17.0615V13.3047L7.5 14.3547ZM9.57566 12.5688L7 13.4703L4.42434 12.5688L7 11.5385L9.57566 12.5688ZM12.8143 13.1358L16.8143 11.5358C16.9335 11.4881 17.0665 11.4881 17.1857 11.5358L21.1857 13.1358C21.3755 13.2117 21.5 13.3955 21.5 13.6V18.4C21.5 18.6045 21.3755 18.7883 21.1857 18.8642L17.1857 20.4642C17.0665 20.5119 16.9335 20.5119 16.8143 20.4642L12.8143 18.8642C12.6245 18.7883 12.5 18.6045 12.5 18.4V13.6C12.5 13.3955 12.6245 13.2117 12.8143 13.1358ZM13.5 14.3047V18.0615L16.5 19.2615V15.3547L13.5 14.3047ZM17.5 15.3547V19.2615L20.5 18.0615V14.3047L17.5 15.3547ZM19.5757 13.5688L17 14.4703L14.4243 13.5688L17 12.5385L19.5757 13.5688Z"
        fill={fill}
      />
    </svg>
  );
};

export const RestoreIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M2 15L4.5 3H19.5L22 15"
        stroke={fill}
        style={{ fill: "none" }}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 15H7.45455L8.36365 18H15.6363L16.5455 15H22V21.5H2V15Z"
        stroke={fill}
        style={{ fill: "none" }}
        strokeLinejoin="round"
      />
      <path
        d="M9 10L12 7L15 10"
        stroke={fill}
        style={{ fill: "none" }}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12 13V7"
        stroke={fill}
        style={{ fill: "none" }}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const ResetSessionIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill={fill}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M12 4C14.06 4 15.93 4.83 17.28 6.18L15 8.46H22V1.46L18.69 4.77C16.99 3.07 14.64 2 12 2C7.03 2 3 6.03 3 11H5C5 7.13 8.13 4 12 4Z" />
      <path d="M19 13C19 16.87 15.87 20 12 20C9.94 20 8.07 19.17 6.72 17.82L9 15.54H2V22.54L5.31 19.23C7.01 20.93 9.36 22 12 22C16.97 22 21 17.97 21 13H19Z" />
    </svg>
  );
};

export const ArchiveIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.resolve("color_icon_error");
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M21 3H3C2.44772 3 2 3.44772 2 4V7C2 7.55228 2.44772 8 3 8H21C21.5523 8 22 7.55228 22 7V4C22 3.44772 21.5523 3 21 3Z"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 8V19C4 19.5304 4.21071 20.0391 4.58579 20.4142C4.96086 20.7893 5.46957 21 6 21H18C18.5304 21 19.0391 20.7893 19.4142 20.4142C19.7893 20.0391 20 19.5304 20 19V8"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.5 17L14.5 12"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M9.5 12L14.5 17"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// TODO the fill on this on hover makes the squares completely dark.
export const TemplatesNavIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.navLinkColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 17"
      fill="transparent"
      xmlns="http://www.w3.org/2000/svg"
      style={{ fill: "none" }}
    >
      <path
        d="M6 2.33301H2.66667C2.29848 2.33301 2 2.63148 2 2.99967V6.33301C2 6.7012 2.29848 6.99967 2.66667 6.99967H6C6.36819 6.99967 6.66667 6.7012 6.66667 6.33301V2.99967C6.66667 2.63148 6.36819 2.33301 6 2.33301Z"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <path
        d="M13.3334 2.33301H10C9.63185 2.33301 9.33337 2.63148 9.33337 2.99967V6.33301C9.33337 6.7012 9.63185 6.99967 10 6.99967H13.3334C13.7016 6.99967 14 6.7012 14 6.33301V2.99967C14 2.63148 13.7016 2.33301 13.3334 2.33301Z"
        stroke={fill}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M13.3334 9.66602H10C9.63185 9.66602 9.33337 9.96449 9.33337 10.3327V13.666C9.33337 14.0342 9.63185 14.3327 10 14.3327H13.3334C13.7016 14.3327 14 14.0342 14 13.666V10.3327C14 9.96449 13.7016 9.66602 13.3334 9.66602Z"
        stroke={fill}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 9.66602H2.66667C2.29848 9.66602 2 9.96449 2 10.3327V13.666C2 14.0342 2.29848 14.3327 2.66667 14.3327H6C6.36819 14.3327 6.66667 14.0342 6.66667 13.666V10.3327C6.66667 9.96449 6.36819 9.66602 6 9.66602Z"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
};

export const GoBackIconSmall = () => {
  const theme = useTheme();
  const fill = theme.navIconActive;

  return (
    <svg
      width="16"
      height="17"
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ marginRight: spacingTokens.spacing_sm }}
    >
      <path
        d="M14 8.503H2M6 4.5l-4 4 4 4"
        stroke={fill}
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
};

export const ListsIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={width}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect x="4.5" y="4.5" width="15" height="5" rx="0.5" stroke={fill} />
      <rect x="10.5" y="16" width="3" height="3" rx="0.5" stroke={fill} />
      <rect x="16.5" y="15.5" width="4" height="4" rx="2" stroke={fill} />
      <rect x="7.5" y="6.5" width="1" height="1" rx="0.5" stroke={fill} />
      <rect x="11.5" y="6.5" width="1" height="1" rx="0.5" stroke={fill} />
      <rect x="15.5" y="6.5" width="1" height="1" rx="0.5" stroke={fill} />
      <rect x="3.5" y="15.5" width="4" height="4" rx="2" stroke={fill} />
      <path
        d="M11.5 10V13.5H12.5V10H11.5ZM12 13H8V14H12V13ZM5.5 15.5V16H6.5V15.5H5.5ZM11.5 13.5V16H12.5V13.5H11.5ZM12 14H16V13H12V14ZM17.5 15.5V16H18.5V15.5H17.5ZM16 14C16.8284 14 17.5 14.6716 17.5 15.5H18.5C18.5 14.1193 17.3807 13 16 13V14ZM8 13C6.61929 13 5.5 14.1193 5.5 15.5H6.5C6.5 14.6716 7.17157 14 8 14V13Z"
        fill={fill}
      />
    </svg>
  );
};

export const ReferenceEntitiesIcon = ({
  width = 20,
  height = 20,
  stroke,
}: IIconProps) => {
  const theme = useTheme();
  const finalStroke = stroke ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10 16.6667H4.16667C3.72464 16.6667 3.30072 16.4912 2.98816 16.1786C2.67559 15.866 2.5 15.4421 2.5 15.0001V3.33341C2.5 2.89139 2.67559 2.46746 2.98816 2.1549C3.30072 1.84234 3.72464 1.66675 4.16667 1.66675H15.8333C16.2754 1.66675 16.6993 1.84234 17.0118 2.1549C17.3244 2.46746 17.5 2.89139 17.5 3.33341V9.16675M2.5 7.50008H17.5M8.33333 1.66675V16.6667M15.835 16.6667C15.3929 16.6667 14.969 16.4912 14.6564 16.1786C14.3439 15.866 14.1683 15.4421 14.1683 15.0001C14.1683 14.5581 14.3439 14.1341 14.6564 13.8216C14.969 13.509 15.3929 13.3334 15.835 13.3334M15.835 16.6667C16.277 16.6667 16.7009 16.4912 17.0135 16.1786C17.326 15.866 17.5016 15.4421 17.5016 15.0001C17.5016 14.5581 17.326 14.1341 17.0135 13.8216C16.7009 13.509 16.277 13.3334 15.835 13.3334M15.835 16.6667V17.9167M15.835 13.3334V12.0834M18.3595 13.5417L17.277 14.1667M14.3923 15.8334L13.3089 16.4584M13.3089 13.5417L14.3923 14.1667M17.277 15.8334L18.3604 16.4584"
        stroke={finalStroke}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const ViewListIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M20.5 3H3.5C2.67157 3 2 3.67157 2 4.5V19.5C2 20.3284 2.67157 21 3.5 21H20.5C21.3284 21 22 20.3284 22 19.5V4.5C22 3.67157 21.3284 3 20.5 3Z"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2 7H22"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 12H18"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 16H18"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 12H7"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6 16H7"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const ViewGridIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M19.5 3H4.5C3.67157 3 3 3.67157 3 4.5V19.5C3 20.3284 3.67157 21 4.5 21H19.5C20.3284 21 21 20.3284 21 19.5V4.5C21 3.67157 20.3284 3 19.5 3Z"
        stroke={fill}
        strokeLinejoin="round"
      />
      <path
        d="M10.5 6.5H6.5V10.5H10.5V6.5Z"
        stroke={fill}
        strokeLinejoin="round"
      />
      <path
        d="M17.5 6.5H13.5V10.5H17.5V6.5Z"
        stroke={fill}
        strokeLinejoin="round"
      />
      <path
        d="M10.5 13.5H6.5V17.5H10.5V13.5Z"
        stroke={fill}
        strokeLinejoin="round"
      />
      <path
        d="M17.5 13.5H13.5V17.5H17.5V13.5Z"
        stroke={fill}
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const ImportIcon = ({ width = 17, height = 18 }: IIconProps) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 17 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.668 4C15.668 5.38071 12.6832 6.5 9.0013 6.5C5.3194 6.5 2.33464 5.38071 2.33464 4M15.668 4C15.668 2.61929 12.6832 1.5 9.0013 1.5C5.3194 1.5 2.33464 2.61929 2.33464 4M15.668 4V9M2.33464 4V10.6667M6.5088 11.3192C7.2788 11.4358 8.11963 11.5 9.0013 11.5C12.683 11.5 15.668 10.3808 15.668 9M15.668 9V14C15.668 15.3808 12.683 16.5 9.0013 16.5C8.7913 16.5 8.58297 16.4967 8.37797 16.4892M0.667969 14.8333H6.5013M6.5013 14.8333L4.0013 12.3333M6.5013 14.8333L4.0013 17.3333"
        stroke="#0F1D2B"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const SystemDefaultIcon = ({
  fill = "#9FA4AA",
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 25"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.89693 4.23855C2.76151 4.38084 2.75 4.46789 2.75 4.5C2.75 4.5321 2.76151 4.61915 2.89693 4.76144C3.03602 4.9076 3.27274 5.07334 3.62263 5.22885C4.32051 5.53902 5.33718 5.75 6.5 5.75C7.66283 5.75 8.6795 5.53902 9.37738 5.22885C9.72727 5.07334 9.96398 4.9076 10.1031 4.76144C10.2385 4.61915 10.25 4.5321 10.25 4.5C10.25 4.46789 10.2385 4.38084 10.1031 4.23855C9.96398 4.09239 9.72727 3.92665 9.37738 3.77114C8.67951 3.46098 7.66283 3.25 6.5 3.25C5.33718 3.25 4.3205 3.46098 3.62262 3.77115C3.27274 3.92665 3.03602 4.09239 2.89693 4.23855ZM10.25 6.47359C10.1641 6.51781 10.0761 6.55978 9.98659 6.59956C9.0558 7.01326 7.82247 7.25 6.5 7.25C5.17754 7.25 3.94421 7.01326 3.01341 6.59956C2.92389 6.55978 2.83593 6.5178 2.75 6.47359V7.5C2.75 7.5321 2.76151 7.61915 2.89693 7.76144C3.03602 7.9076 3.27274 8.07334 3.62263 8.22885C4.32051 8.53902 5.33718 8.75 6.5 8.75C7.66283 8.75 8.6795 8.53902 9.37738 8.22885C9.72727 8.07334 9.96398 7.9076 10.1031 7.76144C10.2385 7.61915 10.25 7.5321 10.25 7.5V6.47359ZM11.75 4.5C11.75 3.97982 11.5097 3.54074 11.1897 3.20447C10.8733 2.87207 10.451 2.60685 9.98659 2.40043C9.05579 1.98674 7.82247 1.75 6.5 1.75C5.17754 1.75 3.94422 1.98674 3.01342 2.40043C2.54896 2.60685 2.12667 2.87207 1.81034 3.20447C1.49033 3.54073 1.25 3.97982 1.25 4.5V10.5C1.25 11.0202 1.49033 11.4593 1.81034 11.7955C2.12667 12.1279 2.54896 12.3931 3.01341 12.5996C3.94421 13.0133 5.17754 13.25 6.5 13.25C7.82247 13.25 9.0558 13.0133 9.98659 12.5996C10.451 12.3931 10.8733 12.1279 11.1897 11.7955C11.5097 11.4593 11.75 11.0202 11.75 10.5V4.5ZM10.25 9.47359C10.1641 9.51781 10.0761 9.55978 9.98659 9.59956C9.0558 10.0133 7.82247 10.25 6.5 10.25C5.17754 10.25 3.94421 10.0133 3.01341 9.59956C2.92389 9.55978 2.83593 9.5178 2.75 9.47359V10.5C2.75 10.5321 2.76151 10.6192 2.89693 10.7614C3.03602 10.9076 3.27274 11.0733 3.62263 11.2288C4.32051 11.539 5.33718 11.75 6.5 11.75C7.66283 11.75 8.6795 11.539 9.37738 11.2288C9.72727 11.0733 9.96398 10.9076 10.1031 10.7614C10.2385 10.6192 10.25 10.5321 10.25 10.5V9.47359ZM15.25 3.5C15.25 3.08579 15.5858 2.75 16 2.75H19C20.5188 2.75 21.75 3.98121 21.75 5.5V8.5C21.75 8.91421 21.4142 9.25 21 9.25C20.5858 9.25 20.25 8.91421 20.25 8.5V5.5C20.25 4.80965 19.6903 4.25 19 4.25H16C15.5858 4.25 15.25 3.91421 15.25 3.5ZM17.5 13.25C16.8097 13.25 16.25 13.8097 16.25 14.5V15.7489L18.75 15.75V14.5C18.75 13.8097 18.1903 13.25 17.5 13.25ZM20.25 15.75V14.5C20.25 12.9812 19.0188 11.75 17.5 11.75C15.9812 11.75 14.75 12.9812 14.75 14.5V15.7489L13 15.75C12.5858 15.75 12.25 16.0858 12.25 16.5V22.5C12.25 22.9142 12.5858 23.25 13 23.25H22C22.4142 23.25 22.75 22.9142 22.75 22.5V16.5C22.75 16.0858 22.4142 15.75 22 15.75H20.25ZM13.75 17.25V21.75H21.25V17.25H13.75ZM3 15.75C3.41421 15.75 3.75 16.0858 3.75 16.5V19.5C3.75 20.1903 4.30965 20.75 5 20.75H8C8.41421 20.75 8.75 21.0858 8.75 21.5C8.75 21.9142 8.41421 22.25 8 22.25H5C3.48121 22.25 2.25 21.0188 2.25 19.5V16.5C2.25 16.0858 2.58579 15.75 3 15.75Z"
        fill={fill}
      />
    </svg>
  );
};

export const VideoIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.colors.darkText;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3 4.5C2.72386 4.5 2.5 4.72386 2.5 5V8.5H5.5V4.5H3ZM3 3.5C2.17157 3.5 1.5 4.17157 1.5 5V19C1.5 19.8284 2.17158 20.5 3 20.5H21C21.8284 20.5 22.5 19.8284 22.5 19V5C22.5 4.17158 21.8284 3.5 21 3.5H3ZM6.5 4.5V19.5H17.5V4.5H6.5ZM18.5 4.5V8.5L21.5 8.5V5C21.5 4.72385 21.2762 4.5 21 4.5H18.5ZM21.5 9.5L18.5 9.5V14.5H21.5V9.5ZM21.5 15.5H18.5V19.5H21C21.2762 19.5 21.5 19.2762 21.5 19V15.5ZM5.5 19.5V15.5H2.5V19C2.5 19.2762 2.72385 19.5 3 19.5H5.5ZM2.5 14.5H5.5V9.5H2.5V14.5ZM10.2576 9.06268C10.4166 8.97457 10.6109 8.97967 10.765 9.076L14.765 11.576C14.9112 11.6674 15 11.8276 15 12C15 12.1724 14.9112 12.3326 14.765 12.424L10.765 14.924C10.6109 15.0203 10.4166 15.0254 10.2576 14.9373C10.0986 14.8492 10 14.6818 10 14.5V9.5C10 9.31824 10.0986 9.15079 10.2576 9.06268ZM11 10.4021V13.5979L13.5566 12L11 10.4021Z"
        fill={fill}
      />
    </svg>
  );
};

export const RolesUserIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.navLinkColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M7.5 7.033a4.033 4.033 0 1 1 8.067 0 4.033 4.033 0 0 1-8.067 0zM11.533 2a5.033 5.033 0 1 0 0 10.067 5.033 5.033 0 0 0 0-10.067zM9.5 13.5c-1.586 0-3.24.424-4.49 1.758C3.766 16.586 3 18.731 3 22a.5.5 0 0 0 1 0c0-3.13.734-4.986 1.74-6.058 1-1.066 2.346-1.442 3.76-1.442a.5.5 0 0 0 0-1z"
        fill={fill}
      />
      <path
        d="m5.01 15.258-.073-.068.073.068zm.73.684.073.068-.073-.068zM11.533 2.9A4.133 4.133 0 0 0 7.4 7.033h.2A3.933 3.933 0 0 1 11.533 3.1v-.2zm4.134 4.133A4.133 4.133 0 0 0 11.533 2.9v.2a3.933 3.933 0 0 1 3.934 3.933h.2zm-4.134 4.134a4.133 4.133 0 0 0 4.134-4.134h-.2a3.933 3.933 0 0 1-3.934 3.934v.2zM7.4 7.033a4.133 4.133 0 0 0 4.133 4.134v-.2A3.933 3.933 0 0 1 7.6 7.033h-.2zm-.8 0A4.933 4.933 0 0 1 11.533 2.1v-.2A5.133 5.133 0 0 0 6.4 7.033h.2zm4.933 4.934A4.933 4.933 0 0 1 6.6 7.033h-.2a5.133 5.133 0 0 0 5.133 5.134v-.2zm4.934-4.934a4.933 4.933 0 0 1-4.934 4.934v.2a5.133 5.133 0 0 0 5.134-5.134h-.2zM11.533 2.1a4.933 4.933 0 0 1 4.934 4.933h.2A5.133 5.133 0 0 0 11.533 1.9v.2zm-6.45 13.226C6.308 14.02 7.931 13.6 9.5 13.6v-.2c-1.603 0-3.287.43-4.563 1.79l.146.136zM3.1 22c0-3.256.762-5.372 1.983-6.674l-.146-.136C3.67 16.543 2.9 18.717 2.9 22h.2zm.4.4a.4.4 0 0 1-.4-.4h-.2a.6.6 0 0 0 .6.6v-.2zm.4-.4a.4.4 0 0 1-.4.4v.2a.6.6 0 0 0 .6-.6h-.2zm1.767-6.126C4.637 16.97 3.9 18.855 3.9 22h.2c0-3.117.731-4.943 1.713-5.99l-.146-.136zM9.5 14.4c-1.432 0-2.808.38-3.833 1.474l.146.136c.974-1.04 2.29-1.41 3.687-1.41v-.2zm.4-.4a.4.4 0 0 1-.4.4v.2a.6.6 0 0 0 .6-.6h-.2zm-.4-.4c.22 0 .4.18.4.4h.2a.6.6 0 0 0-.6-.6v.2z"
        fill={fill}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M20.824 14.19a.552.552 0 0 0-.833 0l-4.92 5.277-2.063-2.193a.552.552 0 0 0-.832 0 .653.653 0 0 0 0 .893l2.479 2.639a.604.604 0 0 0 .416.194.604.604 0 0 0 .416-.194l5.337-5.705a.678.678 0 0 0 0-.912z"
        fill={fill}
      />
    </svg>
  );
};

export const DownloadRestrictedIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#i0wykjy2sa)">
        <g
          clipPath="url(#jb8fwx3vdb)"
          stroke={theme.colors.errorRedBorder}
          strokeLinecap="round"
        >
          <path
            d="m1.336 11.334.414 1.657A1.333 1.333 0 0 0 3.043 14h9.919a1.334 1.334 0 0 0 1.293-1.01l.414-1.657"
            strokeLinejoin="round"
          />
          <path d="m11.33 2.666-6.666 6M4.67 2.666l6.666 6" />
        </g>
      </g>
      <defs>
        <clipPath id="i0wykjy2sa">
          <path fill={fill} d="M0 0h16v16H0z" />
        </clipPath>
        <clipPath id="jb8fwx3vdb">
          <path fill={fill} d="M0 0h16v16H0z" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const AssetsIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.5 2H8.6c-.4 0-.8.2-1.1.5-.3.3-.5.7-.5 1.1v12.8c0 .4.2.8.5 1.1.3.3.7.5 1.1.5h9.8c.4 0 .8-.2 1.1-.5.3-.3.5-.7.5-1.1V6.5L15.5 2z"
        stroke={fill}
        style={{ fill: "none" }}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M3 7.6v12.8c0 .4.2.8.5 1.1.3.3.7.5 1.1.5h9.8M15 2v5h5"
        stroke={fill}
        style={{ fill: "none" }}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const FileIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M15.5 2H8.6c-.4 0-.8.2-1.1.5-.3.3-.5.7-.5 1.1v12.8c0 .4.2.8.5 1.1.3.3.7.5 1.1.5h9.8c.4 0 .8-.2 1.1-.5.3-.3.5-.7.5-1.1V6.5L15.5 2z"
        stroke={fill}
        style={{ fill: "none" }}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const DocumentIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.0454 1V6.4H15.3636V18.1C15.3636 18.5971 14.9515 19 14.4432 19H1.55681C1.04845 19 0.636353 18.5971 0.636353 18.1V1.9C0.636353 1.40294 1.04845 1 1.55681 1H10.0454ZM10.0454 1L15.3636 6.31818"
        stroke={theme.colors.darkText}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const CSVIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_38275_36526)">
        <path
          d="M4.69922 2C4.69922 1.17157 5.37079 0.5 6.19922 0.5H16.1992C16.2587 0.5 16.3157 0.523628 16.3578 0.565686L22.8335 7.04142C22.8756 7.08348 22.8992 7.14052 22.8992 7.2V22C22.8992 22.8284 22.2276 23.5 21.3992 23.5H6.19922C5.37079 23.5 4.69922 22.8284 4.69922 22V2Z"
          stroke={fill}
        />
        <path
          d="M16.1992 0.300781V5.20078C16.1992 6.30535 17.0946 7.20078 18.1992 7.20078H23.0992"
          stroke={fill}
        />
        <path
          d="M15 10.5996H2C1.44772 10.5996 1 11.0473 1 11.5996V19.5996C1 20.1519 1.44772 20.5996 2 20.5996H15C15.5523 20.5996 16 20.1519 16 19.5996V11.5996C16 11.0473 15.5523 10.5996 15 10.5996Z"
          fill="#079455"
        />
        <path
          d="M6.26357 14.7631H5.33033C5.31329 14.6424 5.27849 14.5352 5.22593 14.4414C5.17337 14.3462 5.1059 14.2653 5.02352 14.1985C4.94113 14.1317 4.84596 14.0806 4.738 14.0451C4.63147 14.0096 4.5157 13.9918 4.3907 13.9918C4.16485 13.9918 3.96812 14.0479 3.8005 14.1602C3.63289 14.271 3.50292 14.4329 3.41059 14.646C3.31826 14.8576 3.2721 15.1147 3.2721 15.4173C3.2721 15.7283 3.31826 15.9897 3.41059 16.2013C3.50434 16.413 3.63502 16.5728 3.80264 16.6808C3.97025 16.7887 4.16414 16.8427 4.38431 16.8427C4.50789 16.8427 4.62224 16.8263 4.72735 16.7937C4.83389 16.761 4.92835 16.7134 5.01073 16.6509C5.09312 16.587 5.1613 16.5096 5.21528 16.4187C5.27068 16.3278 5.30903 16.2241 5.33033 16.1076L6.26357 16.1119C6.23943 16.3121 6.17906 16.5053 6.08247 16.6914C5.98729 16.8761 5.85874 17.0415 5.69681 17.1879C5.5363 17.3327 5.34454 17.4478 5.12153 17.533C4.89994 17.6168 4.64923 17.6587 4.3694 17.6587C3.98019 17.6587 3.63218 17.5707 3.32536 17.3945C3.01997 17.2184 2.77849 16.9634 2.60093 16.6296C2.42479 16.2958 2.33673 15.8917 2.33673 15.4173C2.33673 14.9414 2.42622 14.5366 2.60519 14.2028C2.78417 13.869 3.02707 13.6147 3.33389 13.44C3.6407 13.2638 3.98587 13.1758 4.3694 13.1758C4.62224 13.1758 4.85661 13.2113 5.07252 13.2823C5.28985 13.3533 5.48232 13.457 5.64994 13.5934C5.81755 13.7283 5.95391 13.8938 6.05903 14.0898C6.16556 14.2859 6.23374 14.5103 6.26357 14.7631ZM9.29234 14.4904C9.27529 14.3185 9.20214 14.185 9.07288 14.0898C8.94362 13.9947 8.76819 13.9471 8.5466 13.9471C8.39603 13.9471 8.2689 13.9684 8.16521 14.011C8.06151 14.0522 7.98197 14.1097 7.92657 14.1836C7.87259 14.2575 7.8456 14.3413 7.8456 14.435C7.84276 14.5131 7.8591 14.5813 7.89461 14.6396C7.93154 14.6978 7.98197 14.7482 8.04589 14.7908C8.10981 14.832 8.18367 14.8683 8.26748 14.8995C8.35129 14.9293 8.44077 14.9549 8.53595 14.9762L8.92799 15.07C9.11833 15.1126 9.29305 15.1694 9.45214 15.2404C9.61123 15.3114 9.74901 15.3988 9.86549 15.5025C9.98197 15.6062 10.0722 15.7283 10.1361 15.869C10.2014 16.0096 10.2348 16.1708 10.2362 16.3526C10.2348 16.6197 10.1666 16.8512 10.0317 17.0472C9.89816 17.2418 9.70498 17.3931 9.45214 17.5011C9.20072 17.6076 8.89745 17.6609 8.54234 17.6609C8.19006 17.6609 7.88325 17.6069 7.62188 17.4989C7.36194 17.391 7.15881 17.2312 7.01251 17.0195C6.86762 16.8065 6.79163 16.543 6.78452 16.229H7.67728C7.68722 16.3754 7.72913 16.4975 7.80299 16.5955C7.87827 16.6921 7.97842 16.7653 8.10342 16.815C8.22984 16.8633 8.37259 16.8874 8.53168 16.8874C8.68793 16.8874 8.82359 16.8647 8.93864 16.8192C9.05512 16.7738 9.14532 16.7106 9.20924 16.6296C9.27316 16.5486 9.30512 16.4556 9.30512 16.3505C9.30512 16.2525 9.276 16.1701 9.21776 16.1033C9.16095 16.0366 9.07714 15.9798 8.96634 15.9329C8.85697 15.886 8.72274 15.8434 8.56364 15.805L8.0885 15.6857C7.7206 15.5962 7.43012 15.4563 7.21705 15.266C7.00399 15.0756 6.89816 14.8192 6.89958 14.4968C6.89816 14.2326 6.96847 14.0018 7.11052 13.8043C7.25399 13.6069 7.45072 13.4528 7.70072 13.342C7.95072 13.2312 8.23481 13.1758 8.55299 13.1758C8.87685 13.1758 9.15952 13.2312 9.401 13.342C9.6439 13.4528 9.83282 13.6069 9.96776 13.8043C10.1027 14.0018 10.1723 14.2305 10.1766 14.4904H9.29234ZM11.6452 13.2354L12.6998 16.5508H12.7403L13.7971 13.2354H14.8199L13.3156 17.5991H12.1267L10.6203 13.2354H11.6452Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_38275_36526">
          <rect width={width} height={height} fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const XLSXIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_38275_36545)">
        <path
          d="M4.69922 2C4.69922 1.17157 5.37079 0.5 6.19922 0.5H16.1992C16.2587 0.5 16.3157 0.523628 16.3578 0.565686L22.8335 7.04142C22.8756 7.08348 22.8992 7.14052 22.8992 7.2V22C22.8992 22.8284 22.2276 23.5 21.3992 23.5H6.19922C5.37079 23.5 4.69922 22.8284 4.69922 22V2Z"
          stroke={fill}
        />
        <path
          d="M16.1992 0.300781V5.20078C16.1992 6.30535 17.0946 7.20078 18.1992 7.20078H23.0992"
          stroke={fill}
        />
        <path
          d="M18 10.5996H2C1.44772 10.5996 1 11.0473 1 11.5996V19.5996C1 20.1519 1.44772 20.5996 2 20.5996H18C18.5523 20.5996 19 20.1519 19 19.5996V11.5996C19 11.0473 18.5523 10.5996 18 10.5996Z"
          fill="#079455"
        />
        <path
          d="M3.28732 13.2354L4.16729 14.7227H4.20138L5.08561 13.2354H6.12752L4.79584 15.4173L6.15735 17.5991H5.09627L4.20138 16.1097H4.16729L3.2724 17.5991H2.21558L3.58135 15.4173L2.24115 13.2354H3.28732ZM6.71132 17.5991V13.2354H7.63391V16.8384H9.50465V17.5991H6.71132ZM12.4828 14.4904C12.4657 14.3185 12.3926 14.185 12.2633 14.0898C12.1341 13.9947 11.9586 13.9471 11.737 13.9471C11.5865 13.9471 11.4593 13.9684 11.3556 14.011C11.252 14.0522 11.1724 14.1097 11.117 14.1836C11.063 14.2575 11.036 14.3413 11.036 14.435C11.0332 14.5131 11.0495 14.5813 11.085 14.6396C11.122 14.6978 11.1724 14.7482 11.2363 14.7908C11.3002 14.832 11.3741 14.8683 11.4579 14.8995C11.5417 14.9293 11.6312 14.9549 11.7264 14.9762L12.1184 15.07C12.3088 15.1126 12.4835 15.1694 12.6426 15.2404C12.8017 15.3114 12.9395 15.3988 13.0559 15.5025C13.1724 15.6062 13.2626 15.7283 13.3265 15.869C13.3919 16.0096 13.4252 16.1708 13.4267 16.3526C13.4252 16.6197 13.3571 16.8512 13.2221 17.0472C13.0886 17.2418 12.8954 17.3931 12.6426 17.5011C12.3912 17.6076 12.0879 17.6609 11.7328 17.6609C11.3805 17.6609 11.0737 17.6069 10.8123 17.4989C10.5524 17.391 10.3493 17.2312 10.2029 17.0195C10.0581 16.8065 9.9821 16.543 9.97499 16.229H10.8677C10.8777 16.3754 10.9196 16.4975 10.9934 16.5955C11.0687 16.6921 11.1689 16.7653 11.2939 16.815C11.4203 16.8633 11.563 16.8874 11.7221 16.8874C11.8784 16.8874 12.014 16.8647 12.1291 16.8192C12.2456 16.7738 12.3358 16.7106 12.3997 16.6296C12.4636 16.5486 12.4956 16.4556 12.4956 16.3505C12.4956 16.2525 12.4664 16.1701 12.4082 16.1033C12.3514 16.0366 12.2676 15.9798 12.1568 15.9329C12.0474 15.886 11.9132 15.8434 11.7541 15.805L11.2789 15.6857C10.911 15.5962 10.6206 15.4563 10.4075 15.266C10.1944 15.0756 10.0886 14.8192 10.09 14.4968C10.0886 14.2326 10.1589 14.0018 10.301 13.8043C10.4444 13.6069 10.6412 13.4528 10.8912 13.342C11.1412 13.2312 11.4252 13.1758 11.7434 13.1758C12.0673 13.1758 12.35 13.2312 12.5914 13.342C12.8343 13.4528 13.0233 13.6069 13.1582 13.8043C13.2931 14.0018 13.3627 14.2305 13.367 14.4904H12.4828ZM14.9123 13.2354L15.7923 14.7227H15.8263L16.7106 13.2354H17.7525L16.4208 15.4173L17.7823 17.5991H16.7212L15.8263 16.1097H15.7923L14.8974 17.5991H13.8406L15.2063 15.4173L13.8661 13.2354H14.9123Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_38275_36545">
          <rect width={width} height={height} fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const XLSIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_38275_36570)">
        <path
          d="M4.69922 2C4.69922 1.17157 5.37079 0.5 6.19922 0.5H16.1992C16.2587 0.5 16.3157 0.523628 16.3578 0.565686L22.8335 7.04142C22.8756 7.08348 22.8992 7.14052 22.8992 7.2V22C22.8992 22.8284 22.2276 23.5 21.3992 23.5H6.19922C5.37079 23.5 4.69922 22.8284 4.69922 22V2Z"
          stroke={fill}
        />
        <path
          d="M16.1992 0.300781V5.20078C16.1992 6.30535 17.0946 7.20078 18.1992 7.20078H23.0992"
          stroke={fill}
        />
        <path
          d="M14 10.5996H2C1.44772 10.5996 1 11.0473 1 11.5996V19.5996C1 20.1519 1.44772 20.5996 2 20.5996H14C14.5523 20.5996 15 20.1519 15 19.5996V11.5996C15 11.0473 14.5523 10.5996 14 10.5996Z"
          fill="#079455"
        />
        <path
          d="M3.43184 13.2354L4.31181 14.7227H4.3459L5.23013 13.2354H6.27204L4.94036 15.4173L6.30187 17.5991H5.24079L4.3459 16.1097H4.31181L3.41692 17.5991H2.36011L3.72587 15.4173L2.38567 13.2354H3.43184ZM6.85584 17.5991V13.2354H7.77843V16.8384H9.64917V17.5991H6.85584ZM12.6273 14.4904C12.6103 14.3185 12.5371 14.185 12.4078 14.0898C12.2786 13.9947 12.1032 13.9471 11.8816 13.9471C11.731 13.9471 11.6039 13.9684 11.5002 14.011C11.3965 14.0522 11.3169 14.1097 11.2615 14.1836C11.2076 14.2575 11.1806 14.3413 11.1806 14.435C11.1777 14.5131 11.1941 14.5813 11.2296 14.6396C11.2665 14.6978 11.3169 14.7482 11.3809 14.7908C11.4448 14.832 11.5186 14.8683 11.6025 14.8995C11.6863 14.9293 11.7757 14.9549 11.8709 14.9762L12.263 15.07C12.4533 15.1126 12.628 15.1694 12.7871 15.2404C12.9462 15.3114 13.084 15.3988 13.2005 15.5025C13.3169 15.6062 13.4071 15.7283 13.4711 15.869C13.5364 16.0096 13.5698 16.1708 13.5712 16.3526C13.5698 16.6197 13.5016 16.8512 13.3667 17.0472C13.2331 17.2418 13.04 17.3931 12.7871 17.5011C12.5357 17.6076 12.2324 17.6609 11.8773 17.6609C11.525 17.6609 11.2182 17.6069 10.9569 17.4989C10.6969 17.391 10.4938 17.2312 10.3475 17.0195C10.2026 16.8065 10.1266 16.543 10.1195 16.229H11.0123C11.0222 16.3754 11.0641 16.4975 11.138 16.5955C11.2132 16.6921 11.3134 16.7653 11.4384 16.815C11.5648 16.8633 11.7076 16.8874 11.8667 16.8874C12.0229 16.8874 12.1586 16.8647 12.2736 16.8192C12.3901 16.7738 12.4803 16.7106 12.5442 16.6296C12.6081 16.5486 12.6401 16.4556 12.6401 16.3505C12.6401 16.2525 12.611 16.1701 12.5527 16.1033C12.4959 16.0366 12.4121 15.9798 12.3013 15.9329C12.1919 15.886 12.0577 15.8434 11.8986 15.805L11.4235 15.6857C11.0556 15.5962 10.7651 15.4563 10.552 15.266C10.339 15.0756 10.2331 14.8192 10.2346 14.4968C10.2331 14.2326 10.3034 14.0018 10.4455 13.8043C10.589 13.6069 10.7857 13.4528 11.0357 13.342C11.2857 13.2312 11.5698 13.1758 11.888 13.1758C12.2118 13.1758 12.4945 13.2312 12.736 13.342C12.9789 13.4528 13.1678 13.6069 13.3027 13.8043C13.4377 14.0018 13.5073 14.2305 13.5115 14.4904H12.6273Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_38275_36570">
          <rect width={width} height={height} fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const DOCIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_38275_36589)">
        <path
          d="M4.69922 2C4.69922 1.17157 5.37079 0.5 6.19922 0.5H16.1992C16.2587 0.5 16.3157 0.523628 16.3578 0.565686L22.8335 7.04142C22.8756 7.08348 22.8992 7.14052 22.8992 7.2V22C22.8992 22.8284 22.2276 23.5 21.3992 23.5H6.19922C5.37079 23.5 4.69922 22.8284 4.69922 22V2Z"
          stroke={fill}
        />
        <path
          d="M16.1992 0.300781V5.20078C16.1992 6.30535 17.0946 7.20078 18.1992 7.20078H23.0992"
          stroke={fill}
        />
        <path
          d="M16 10.5996H2C1.44772 10.5996 1 11.0473 1 11.5996V19.5996C1 20.1519 1.44772 20.5996 2 20.5996H16C16.5523 20.5996 17 20.1519 17 19.5996V11.5996C17 11.0473 16.5523 10.5996 16 10.5996Z"
          fill="#155EEF"
        />
        <path
          d="M4.14099 17.5991H2.59412V13.2354H4.15377C4.59269 13.2354 4.97054 13.3228 5.2873 13.4975C5.60406 13.6708 5.84767 13.9201 6.01812 14.2454C6.19 14.5707 6.27593 14.9599 6.27593 15.413C6.27593 15.8675 6.19 16.2582 6.01812 16.5849C5.84767 16.9116 5.60264 17.1623 5.28304 17.337C4.96485 17.5117 4.58417 17.5991 4.14099 17.5991ZM3.5167 16.8086H4.10264C4.37537 16.8086 4.60477 16.7603 4.79085 16.6637C4.97835 16.5657 5.11897 16.4144 5.21272 16.2099C5.30789 16.0039 5.35548 15.7383 5.35548 15.413C5.35548 15.0906 5.30789 14.8271 5.21272 14.6225C5.11897 14.418 4.97906 14.2674 4.79298 14.1708C4.6069 14.0742 4.3775 14.0259 4.10477 14.0259H3.5167V16.8086ZM10.9693 15.4173C10.9693 15.8931 10.8791 16.2979 10.6987 16.6317C10.5197 16.9656 10.2754 17.2205 9.96574 17.3967C9.6575 17.5714 9.31091 17.6587 8.92597 17.6587C8.53818 17.6587 8.19017 17.5707 7.88193 17.3945C7.5737 17.2184 7.33009 16.9634 7.15111 16.6296C6.97213 16.2958 6.88264 15.8917 6.88264 15.4173C6.88264 14.9414 6.97213 14.5366 7.15111 14.2028C7.33009 13.869 7.5737 13.6147 7.88193 13.44C8.19017 13.2638 8.53818 13.1758 8.92597 13.1758C9.31091 13.1758 9.6575 13.2638 9.96574 13.44C10.2754 13.6147 10.5197 13.869 10.6987 14.2028C10.8791 14.5366 10.9693 14.9414 10.9693 15.4173ZM10.0339 15.4173C10.0339 15.109 9.98776 14.8491 9.89543 14.6374C9.80452 14.4258 9.67597 14.2653 9.50978 14.1559C9.34358 14.0465 9.14898 13.9918 8.92597 13.9918C8.70296 13.9918 8.50836 14.0465 8.34216 14.1559C8.17597 14.2653 8.04671 14.4258 7.95438 14.6374C7.86347 14.8491 7.81801 15.109 7.81801 15.4173C7.81801 15.7255 7.86347 15.9854 7.95438 16.1971C8.04671 16.4087 8.17597 16.5692 8.34216 16.6786C8.50836 16.788 8.70296 16.8427 8.92597 16.8427C9.14898 16.8427 9.34358 16.788 9.50978 16.6786C9.67597 16.5692 9.80452 16.4087 9.89543 16.1971C9.98776 15.9854 10.0339 15.7255 10.0339 15.4173ZM15.5028 14.7631H14.5696C14.5526 14.6424 14.5178 14.5352 14.4652 14.4414C14.4126 14.3462 14.3452 14.2653 14.2628 14.1985C14.1804 14.1317 14.0852 14.0806 13.9773 14.0451C13.8707 14.0096 13.755 13.9918 13.63 13.9918C13.4041 13.9918 13.2074 14.0479 13.0398 14.1602C12.8722 14.271 12.7422 14.4329 12.6499 14.646C12.5575 14.8576 12.5114 15.1147 12.5114 15.4173C12.5114 15.7283 12.5575 15.9897 12.6499 16.2013C12.7436 16.413 12.8743 16.5728 13.0419 16.6808C13.2095 16.7887 13.4034 16.8427 13.6236 16.8427C13.7472 16.8427 13.8615 16.8263 13.9666 16.7937C14.0732 16.761 14.1676 16.7134 14.25 16.6509C14.3324 16.587 14.4006 16.5096 14.4545 16.4187C14.5099 16.3278 14.5483 16.2241 14.5696 16.1076L15.5028 16.1119C15.4787 16.3121 15.4183 16.5053 15.3217 16.6914C15.2266 16.8761 15.098 17.0415 14.9361 17.1879C14.7756 17.3327 14.5838 17.4478 14.3608 17.533C14.1392 17.6168 13.8885 17.6587 13.6087 17.6587C13.2195 17.6587 12.8714 17.5707 12.5646 17.3945C12.2592 17.2184 12.0178 16.9634 11.8402 16.6296C11.6641 16.2958 11.576 15.8917 11.576 15.4173C11.576 14.9414 11.6655 14.5366 11.8445 14.2028C12.0234 13.869 12.2663 13.6147 12.5732 13.44C12.88 13.2638 13.2251 13.1758 13.6087 13.1758C13.8615 13.1758 14.0959 13.2113 14.3118 13.2823C14.5291 13.3533 14.7216 13.457 14.8892 13.5934C15.0568 13.7283 15.1932 13.8938 15.2983 14.0898C15.4048 14.2859 15.473 14.5103 15.5028 14.7631Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_38275_36589">
          <rect width={width} height={height} fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const DOCXIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_38275_36608)">
        <path
          d="M4.69922 2C4.69922 1.17157 5.37079 0.5 6.19922 0.5H16.1992C16.2587 0.5 16.3157 0.523628 16.3578 0.565686L22.8335 7.04142C22.8756 7.08348 22.8992 7.14052 22.8992 7.2V22C22.8992 22.8284 22.2276 23.5 21.3992 23.5H6.19922C5.37079 23.5 4.69922 22.8284 4.69922 22V2Z"
          stroke={fill}
        />
        <path
          d="M16.1992 0.300781V5.20078C16.1992 6.30535 17.0946 7.20078 18.1992 7.20078H23.0992"
          stroke={fill}
        />
        <path
          d="M20 10.5996H2C1.44772 10.5996 1 11.0473 1 11.5996V19.5996C1 20.1519 1.44772 20.5996 2 20.5996H20C20.5523 20.5996 21 20.1519 21 19.5996V11.5996C21 11.0473 20.5523 10.5996 20 10.5996Z"
          fill="#155EEF"
        />
        <path
          d="M4.04333 17.5991H2.49646V13.2354H4.05612C4.49504 13.2354 4.87288 13.3228 5.18964 13.4975C5.5064 13.6708 5.75001 13.9201 5.92046 14.2454C6.09234 14.5707 6.17828 14.9599 6.17828 15.413C6.17828 15.8675 6.09234 16.2582 5.92046 16.5849C5.75001 16.9116 5.50498 17.1623 5.18538 17.337C4.8672 17.5117 4.48652 17.5991 4.04333 17.5991ZM3.41904 16.8086H4.00498C4.27771 16.8086 4.50711 16.7603 4.69319 16.6637C4.88069 16.5657 5.02132 16.4144 5.11507 16.2099C5.21024 16.0039 5.25782 15.7383 5.25782 15.413C5.25782 15.0906 5.21024 14.8271 5.11507 14.6225C5.02132 14.418 4.8814 14.2674 4.69532 14.1708C4.50924 14.0742 4.27984 14.0259 4.00711 14.0259H3.41904V16.8086ZM10.8716 15.4173C10.8716 15.8931 10.7814 16.2979 10.601 16.6317C10.4221 16.9656 10.1777 17.2205 9.86809 17.3967C9.55985 17.5714 9.21326 17.6587 8.82831 17.6587C8.44053 17.6587 8.09252 17.5707 7.78428 17.3945C7.47604 17.2184 7.23243 16.9634 7.05345 16.6296C6.87448 16.2958 6.78499 15.8917 6.78499 15.4173C6.78499 14.9414 6.87448 14.5366 7.05345 14.2028C7.23243 13.869 7.47604 13.6147 7.78428 13.44C8.09252 13.2638 8.44053 13.1758 8.82831 13.1758C9.21326 13.1758 9.55985 13.2638 9.86809 13.44C10.1777 13.6147 10.4221 13.869 10.601 14.2028C10.7814 14.5366 10.8716 14.9414 10.8716 15.4173ZM9.93627 15.4173C9.93627 15.109 9.8901 14.8491 9.79777 14.6374C9.70686 14.4258 9.57831 14.2653 9.41212 14.1559C9.24593 14.0465 9.05132 13.9918 8.82831 13.9918C8.6053 13.9918 8.4107 14.0465 8.24451 14.1559C8.07831 14.2653 7.94905 14.4258 7.85672 14.6374C7.76581 14.8491 7.72036 15.109 7.72036 15.4173C7.72036 15.7255 7.76581 15.9854 7.85672 16.1971C7.94905 16.4087 8.07831 16.5692 8.24451 16.6786C8.4107 16.788 8.6053 16.8427 8.82831 16.8427C9.05132 16.8427 9.24593 16.788 9.41212 16.6786C9.57831 16.5692 9.70686 16.4087 9.79777 16.1971C9.8901 15.9854 9.93627 15.7255 9.93627 15.4173ZM15.4052 14.7631H14.4719C14.4549 14.6424 14.4201 14.5352 14.3675 14.4414C14.315 14.3462 14.2475 14.2653 14.1651 14.1985C14.0827 14.1317 13.9876 14.0806 13.8796 14.0451C13.7731 14.0096 13.6573 13.9918 13.5323 13.9918C13.3065 13.9918 13.1097 14.0479 12.9421 14.1602C12.7745 14.271 12.6445 14.4329 12.5522 14.646C12.4599 14.8576 12.4137 15.1147 12.4137 15.4173C12.4137 15.7283 12.4599 15.9897 12.5522 16.2013C12.646 16.413 12.7766 16.5728 12.9442 16.6808C13.1119 16.7887 13.3058 16.8427 13.5259 16.8427C13.6495 16.8427 13.7638 16.8263 13.869 16.7937C13.9755 16.761 14.07 16.7134 14.1523 16.6509C14.2347 16.587 14.3029 16.5096 14.3569 16.4187C14.4123 16.3278 14.4506 16.2241 14.4719 16.1076L15.4052 16.1119C15.381 16.3121 15.3207 16.5053 15.2241 16.6914C15.1289 16.8761 15.0004 17.0415 14.8384 17.1879C14.6779 17.3327 14.4862 17.4478 14.2631 17.533C14.0415 17.6168 13.7908 17.6587 13.511 17.6587C13.1218 17.6587 12.7738 17.5707 12.467 17.3945C12.1616 17.2184 11.9201 16.9634 11.7425 16.6296C11.5664 16.2958 11.4783 15.8917 11.4783 15.4173C11.4783 14.9414 11.5678 14.5366 11.7468 14.2028C11.9258 13.869 12.1687 13.6147 12.4755 13.44C12.7823 13.2638 13.1275 13.1758 13.511 13.1758C13.7638 13.1758 13.9982 13.2113 14.2141 13.2823C14.4315 13.3533 14.6239 13.457 14.7915 13.5934C14.9592 13.7283 15.0955 13.8938 15.2006 14.0898C15.3072 14.2859 15.3754 14.5103 15.4052 14.7631ZM16.8381 13.2354L17.718 14.7227H17.7521L18.6364 13.2354H19.6783L18.3466 15.4173L19.7081 17.5991H18.647L17.7521 16.1097H17.718L16.8232 17.5991H15.7663L17.1321 15.4173L15.7919 13.2354H16.8381Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_38275_36608">
          <rect width={width} height={height} fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const JSONIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_38275_36634)">
        <path
          d="M4.69922 2C4.69922 1.17157 5.37079 0.5 6.19922 0.5H16.1992C16.2587 0.5 16.3157 0.523628 16.3578 0.565686L22.8335 7.04142C22.8756 7.08348 22.8992 7.14052 22.8992 7.2V22C22.8992 22.8284 22.2276 23.5 21.3992 23.5H6.19922C5.37079 23.5 4.69922 22.8284 4.69922 22V2Z"
          stroke={fill}
        />
        <path
          d="M16.1992 0.300781V5.20078C16.1992 6.30535 17.0946 7.20078 18.1992 7.20078H23.0992"
          stroke={fill}
        />
        <path
          d="M19 10.5996H2C1.44772 10.5996 1 11.0473 1 11.5996V19.5996C1 20.1519 1.44772 20.5996 2 20.5996H19C19.5523 20.5996 20 20.1519 20 19.5996V11.5996C20 11.0473 19.5523 10.5996 19 10.5996Z"
          fill="#FBC02D"
        />
        <path
          d="M4.39696 13.2354H5.30889V16.2781C5.30889 16.5593 5.24568 16.8036 5.11926 17.011C4.99426 17.2184 4.82025 17.3782 4.59724 17.4904C4.37423 17.6026 4.115 17.6587 3.81954 17.6587C3.55676 17.6587 3.31812 17.6126 3.10363 17.5202C2.89057 17.4265 2.72153 17.2844 2.59653 17.0941C2.47153 16.9023 2.40974 16.6616 2.41116 16.3718H3.32949C3.33233 16.4869 3.35577 16.5856 3.3998 16.668C3.44525 16.7489 3.50704 16.8114 3.58517 16.8555C3.66471 16.8981 3.75846 16.9194 3.86642 16.9194C3.98005 16.9194 4.07594 16.8952 4.15406 16.8469C4.23361 16.7972 4.29398 16.7248 4.33517 16.6296C4.37636 16.5344 4.39696 16.4173 4.39696 16.2781V13.2354ZM8.43886 14.4904C8.42182 14.3185 8.34866 14.185 8.2194 14.0898C8.09014 13.9947 7.91471 13.9471 7.69312 13.9471C7.54255 13.9471 7.41542 13.9684 7.31173 14.011C7.20804 14.0522 7.12849 14.1097 7.07309 14.1836C7.01912 14.2575 6.99213 14.3413 6.99213 14.435C6.98929 14.5131 7.00562 14.5813 7.04113 14.6396C7.07807 14.6978 7.12849 14.7482 7.19241 14.7908C7.25633 14.832 7.3302 14.8683 7.414 14.8995C7.49781 14.9293 7.5873 14.9549 7.68247 14.9762L8.07452 15.07C8.26486 15.1126 8.43957 15.1694 8.59866 15.2404C8.75775 15.3114 8.89554 15.3988 9.01202 15.5025C9.12849 15.6062 9.21869 15.7283 9.28261 15.869C9.34795 16.0096 9.38133 16.1708 9.38275 16.3526C9.38133 16.6197 9.31315 16.8512 9.17821 17.0472C9.04469 17.2418 8.8515 17.3931 8.59866 17.5011C8.34724 17.6076 8.04398 17.6609 7.68886 17.6609C7.33659 17.6609 7.02977 17.6069 6.76841 17.4989C6.50846 17.391 6.30534 17.2312 6.15903 17.0195C6.01415 16.8065 5.93815 16.543 5.93105 16.229H6.8238C6.83375 16.3754 6.87565 16.4975 6.94952 16.5955C7.0248 16.6921 7.12494 16.7653 7.24994 16.815C7.37636 16.8633 7.51912 16.8874 7.67821 16.8874C7.83446 16.8874 7.97011 16.8647 8.08517 16.8192C8.20165 16.7738 8.29184 16.7106 8.35577 16.6296C8.41969 16.5486 8.45165 16.4556 8.45165 16.3505C8.45165 16.2525 8.42253 16.1701 8.36429 16.1033C8.30747 16.0366 8.22366 15.9798 8.11287 15.9329C8.00349 15.886 7.86926 15.8434 7.71017 15.805L7.23503 15.6857C6.86713 15.5962 6.57665 15.4563 6.36358 15.266C6.15051 15.0756 6.04469 14.8192 6.04611 14.4968C6.04469 14.2326 6.115 14.0018 6.25704 13.8043C6.40051 13.6069 6.59724 13.4528 6.84724 13.342C7.09724 13.2312 7.38133 13.1758 7.69952 13.1758C8.02338 13.1758 8.30605 13.2312 8.54753 13.342C8.79042 13.4528 8.97934 13.6069 9.11429 13.8043C9.24923 14.0018 9.31883 14.2305 9.32309 14.4904H8.43886ZM14.0132 15.4173C14.0132 15.8931 13.923 16.2979 13.7426 16.6317C13.5637 16.9656 13.3193 17.2205 13.0097 17.3967C12.7014 17.5714 12.3548 17.6587 11.9699 17.6587C11.5821 17.6587 11.2341 17.5707 10.9259 17.3945C10.6176 17.2184 10.374 16.9634 10.195 16.6296C10.0161 16.2958 9.92661 15.8917 9.92661 15.4173C9.92661 14.9414 10.0161 14.5366 10.195 14.2028C10.374 13.869 10.6176 13.6147 10.9259 13.44C11.2341 13.2638 11.5821 13.1758 11.9699 13.1758C12.3548 13.1758 12.7014 13.2638 13.0097 13.44C13.3193 13.6147 13.5637 13.869 13.7426 14.2028C13.923 14.5366 14.0132 14.9414 14.0132 15.4173ZM13.0779 15.4173C13.0779 15.109 13.0317 14.8491 12.9394 14.6374C12.8485 14.4258 12.7199 14.2653 12.5537 14.1559C12.3875 14.0465 12.1929 13.9918 11.9699 13.9918C11.7469 13.9918 11.5523 14.0465 11.3861 14.1559C11.2199 14.2653 11.0906 14.4258 10.9983 14.6374C10.9074 14.8491 10.8619 15.109 10.8619 15.4173C10.8619 15.7255 10.9074 15.9854 10.9983 16.1971C11.0906 16.4087 11.2199 16.5692 11.3861 16.6786C11.5523 16.788 11.7469 16.8427 11.9699 16.8427C12.1929 16.8427 12.3875 16.788 12.5537 16.6786C12.7199 16.5692 12.8485 16.4087 12.9394 16.1971C13.0317 15.9854 13.0779 15.7255 13.0779 15.4173ZM18.3465 13.2354V17.5991H17.5496L15.6512 14.8526H15.6192V17.5991H14.6966V13.2354H15.5063L17.3898 15.9798H17.4282V13.2354H18.3465Z"
          fill="#0F1D2B"
        />
      </g>
      <defs>
        <clipPath id="clip0_38275_36634">
          <rect width={width} height={height} fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const PPTIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_38275_36665)">
        <path
          d="M4.69922 2C4.69922 1.17157 5.37079 0.5 6.19922 0.5H16.1992C16.2587 0.5 16.3157 0.523628 16.3578 0.565686L22.8335 7.04142C22.8756 7.08348 22.8992 7.14052 22.8992 7.2V22C22.8992 22.8284 22.2276 23.5 21.3992 23.5H6.19922C5.37079 23.5 4.69922 22.8284 4.69922 22V2Z"
          stroke={fill}
        />
        <path
          d="M16.1992 0.300781V5.20078C16.1992 6.30535 17.0946 7.20078 18.1992 7.20078H23.0992"
          stroke={fill}
        />
        <path
          d="M14 10.5996H2C1.44772 10.5996 1 11.0473 1 11.5996V19.5996C1 20.1519 1.44772 20.5996 2 20.5996H14C14.5523 20.5996 15 20.1519 15 19.5996V11.5996C15 11.0473 14.5523 10.5996 14 10.5996Z"
          fill="#D35230"
        />
        <path
          d="M2.4906 17.6V13.2363H4.21219C4.54316 13.2363 4.82512 13.2995 5.05807 13.426C5.29103 13.551 5.46858 13.725 5.59074 13.948C5.71432 14.1696 5.77611 14.4253 5.77611 14.715C5.77611 15.0048 5.71361 15.2605 5.58861 15.4821C5.46361 15.7037 5.2825 15.8763 5.04529 15.9998C4.80949 16.1234 4.52398 16.1852 4.18875 16.1852H3.09145V15.4459H4.03961C4.21716 15.4459 4.36347 15.4153 4.47853 15.3542C4.595 15.2917 4.68165 15.2058 4.73847 15.0964C4.79671 14.9856 4.82583 14.8585 4.82583 14.715C4.82583 14.5701 4.79671 14.4437 4.73847 14.3358C4.68165 14.2264 4.595 14.1419 4.47853 14.0822C4.36205 14.0211 4.21432 13.9906 4.03534 13.9906H3.41318V17.6H2.4906ZM6.37537 17.6V13.2363H8.09696C8.42792 13.2363 8.70988 13.2995 8.94284 13.426C9.17579 13.551 9.35335 13.725 9.47551 13.948C9.59909 14.1696 9.66088 14.4253 9.66088 14.715C9.66088 15.0048 9.59838 15.2605 9.47338 15.4821C9.34838 15.7037 9.16727 15.8763 8.93005 15.9998C8.69426 16.1234 8.40875 16.1852 8.07352 16.1852H6.97622V15.4459H7.92437C8.10193 15.4459 8.24823 15.4153 8.36329 15.3542C8.47977 15.2917 8.56642 15.2058 8.62323 15.0964C8.68147 14.9856 8.71059 14.8585 8.71059 14.715C8.71059 14.5701 8.68147 14.4437 8.62323 14.3358C8.56642 14.2264 8.47977 14.1419 8.36329 14.0822C8.24681 14.0211 8.09909 13.9906 7.92011 13.9906H7.29795V17.6H6.37537ZM10.0939 13.997V13.2363H13.6777V13.997H12.3418V17.6H11.4299V13.997H10.0939Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_38275_36665">
          <rect width={width} height={height} fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const PPTXIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_38275_36684)">
        <path
          d="M4.69922 2C4.69922 1.17157 5.37079 0.5 6.19922 0.5H16.1992C16.2587 0.5 16.3157 0.523628 16.3578 0.565686L22.8335 7.04142C22.8756 7.08348 22.8992 7.14052 22.8992 7.2V22C22.8992 22.8284 22.2276 23.5 21.3992 23.5H6.19922C5.37079 23.5 4.69922 22.8284 4.69922 22V2Z"
          stroke={fill}
        />
        <path
          d="M16.1992 0.300781V5.20078C16.1992 6.30535 17.0946 7.20078 18.1992 7.20078H23.0992"
          stroke={fill}
        />
        <path
          d="M19 10.5996H2C1.44772 10.5996 1 11.0473 1 11.5996V19.5996C1 20.1519 1.44772 20.5996 2 20.5996H19C19.5523 20.5996 20 20.1519 20 19.5996V11.5996C20 11.0473 19.5523 10.5996 19 10.5996Z"
          fill="#D35230"
        />
        <path
          d="M2.84607 17.6V13.2363H4.56766C4.89863 13.2363 5.18059 13.2995 5.41354 13.426C5.64649 13.551 5.82405 13.725 5.94621 13.948C6.06979 14.1696 6.13158 14.4253 6.13158 14.715C6.13158 15.0048 6.06908 15.2605 5.94408 15.4821C5.81908 15.7037 5.63797 15.8763 5.40076 15.9998C5.16496 16.1234 4.87945 16.1852 4.54422 16.1852H3.44692V15.4459H4.39507C4.57263 15.4459 4.71894 15.4153 4.83399 15.3542C4.95047 15.2917 5.03712 15.2058 5.09394 15.0964C5.15218 14.9856 5.1813 14.8585 5.1813 14.715C5.1813 14.5701 5.15218 14.4437 5.09394 14.3358C5.03712 14.2264 4.95047 14.1419 4.83399 14.0822C4.71752 14.0211 4.56979 13.9906 4.39081 13.9906H3.76865V17.6H2.84607ZM6.73083 17.6V13.2363H8.45242C8.78339 13.2363 9.06535 13.2995 9.29831 13.426C9.53126 13.551 9.70882 13.725 9.83098 13.948C9.95456 14.1696 10.0163 14.4253 10.0163 14.715C10.0163 15.0048 9.95385 15.2605 9.82885 15.4821C9.70385 15.7037 9.52274 15.8763 9.28552 15.9998C9.04973 16.1234 8.76421 16.1852 8.42899 16.1852H7.33169V15.4459H8.27984C8.4574 15.4459 8.6037 15.4153 8.71876 15.3542C8.83524 15.2917 8.92189 15.2058 8.9787 15.0964C9.03694 14.9856 9.06606 14.8585 9.06606 14.715C9.06606 14.5701 9.03694 14.4437 8.9787 14.3358C8.92189 14.2264 8.83524 14.1419 8.71876 14.0822C8.60228 14.0211 8.45456 13.9906 8.27558 13.9906H7.65342V17.6H6.73083ZM10.4494 13.997V13.2363H14.0332V13.997H12.6973V17.6H11.7853V13.997H10.4494ZM15.4885 13.2363L16.3684 14.7236H16.4025L17.2868 13.2363H18.3287L16.997 15.4182L18.3585 17.6H17.2974L16.4025 16.1106H16.3684L15.4735 17.6H14.4167L15.7825 15.4182L14.4423 13.2363H15.4885Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_38275_36684">
          <rect width={width} height={height} fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const TXTIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_38275_36703)">
        <path
          d="M4.69922 2C4.69922 1.17157 5.37079 0.5 6.19922 0.5H16.1992C16.2587 0.5 16.3157 0.523628 16.3578 0.565686L22.8335 7.04142C22.8756 7.08348 22.8992 7.14052 22.8992 7.2V22C22.8992 22.8284 22.2276 23.5 21.3992 23.5H6.19922C5.37079 23.5 4.69922 22.8284 4.69922 22V2Z"
          stroke={fill}
        />
        <path
          d="M16.1992 0.300781V5.20078C16.1992 6.30535 17.0946 7.20078 18.1992 7.20078H23.0992"
          stroke={fill}
        />
        <path
          d="M15 10.5996H2C1.44772 10.5996 1 11.0473 1 11.5996V19.5996C1 20.1519 1.44772 20.5996 2 20.5996H15C15.5523 20.5996 16 20.1519 16 19.5996V11.5996C16 11.0473 15.5523 10.5996 15 10.5996Z"
          fill="#3F4A55"
        />
        <path
          d="M2.56073 13.997V13.2363H6.14454V13.997H4.8086V17.6H3.89667V13.997H2.56073ZM7.5998 13.2363L8.47977 14.7236H8.51386L9.39809 13.2363H10.44L9.10832 15.4182L10.4698 17.6H9.40875L8.51386 16.1106H8.47977L7.58488 17.6H6.52806L7.89383 15.4182L6.55363 13.2363H7.5998ZM10.8576 13.997V13.2363H14.4414V13.997H13.1055V17.6H12.1935V13.997H10.8576Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_38275_36703">
          <rect width={width} height={height} fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const RTFIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_38354_39675)">
        <path
          d="M21.3992 23.5H6.19922C5.37079 23.5 4.69922 22.8284 4.69922 22V2C4.69922 1.17157 5.37079 0.5 6.19922 0.5H16.1992C16.2587 0.5 16.3157 0.523628 16.3578 0.565686L22.8335 7.04142C22.8756 7.08348 22.8992 7.14052 22.8992 7.2V22C22.8992 22.8284 22.2276 23.5 21.3992 23.5Z"
          stroke={fill}
        />
        <path
          d="M16.1992 0.300781V5.20078C16.1992 6.30535 17.0946 7.20078 18.1992 7.20078H23.0992"
          stroke={fill}
        />
        <path
          d="M14 10.5996H2C1.44772 10.5996 1 11.0473 1 11.5996V19.5996C1 20.1519 1.44772 20.5996 2 20.5996H14C14.5523 20.5996 15 20.1519 15 19.5996V11.5996C15 11.0473 14.5523 10.5996 14 10.5996Z"
          fill="#3F4A55"
        />
        <path
          d="M2.65173 17.6V13.2363H4.37332C4.70287 13.2363 4.98412 13.2953 5.21707 13.4132C5.45145 13.5297 5.62972 13.6951 5.75187 13.9096C5.87545 14.1227 5.93724 14.3734 5.93724 14.6618C5.93724 14.9515 5.87474 15.2008 5.74974 15.4096C5.62474 15.617 5.44364 15.7761 5.20642 15.8869C4.97062 15.9977 4.68511 16.0531 4.34989 16.0531H3.19719V15.3116H4.20074C4.37687 15.3116 4.52318 15.2875 4.63966 15.2392C4.75614 15.1909 4.84278 15.1184 4.8996 15.0219C4.95784 14.9253 4.98696 14.8052 4.98696 14.6618C4.98696 14.5169 4.95784 14.3947 4.8996 14.2953C4.84278 14.1959 4.75543 14.1206 4.63753 14.0694C4.52105 14.0169 4.37403 13.9906 4.19648 13.9906H3.57432V17.6H2.65173ZM5.00827 15.6142L6.09278 17.6H5.07432L4.01324 15.6142H5.00827ZM6.42304 13.997V13.2363H10.0068V13.997H8.67091V17.6H7.75898V13.997H6.42304ZM10.597 17.6V13.2363H13.4862V13.997H11.5196V15.0368H13.2945V15.7974H11.5196V17.6H10.597Z"
          fill="white"
        />
      </g>
      <defs>
        <clipPath id="clip0_38354_39675">
          <rect width={width} height={height} fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const MagicDocIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M3.5 3C3.5 2.17157 4.17157 1.5 5 1.5H15.5C15.6417 1.5 15.7768 1.56016 15.8716 1.66552L20.3716 6.66552C20.4543 6.75733 20.5 6.87648 20.5 7V22C20.5 22.2761 20.2761 22.5 20 22.5H5C4.17158 22.5 3.5 21.8284 3.5 21V3ZM5 2.5C4.72386 2.5 4.5 2.72386 4.5 3V21C4.5 21.2762 4.72385 21.5 5 21.5H19.5V7.5H15C14.7239 7.5 14.5 7.27614 14.5 7V2.5H5ZM15.5 2.74742V6.5H18.8773L15.5 2.74742ZM9.30708 9.768C9.39339 9.60324 9.56401 9.5 9.75 9.5H14.25C14.4414 9.5 14.616 9.60928 14.6997 9.78143C14.7834 9.95358 14.7614 10.1584 14.6432 10.3089L12.5287 13H15.25C15.4486 13 15.6284 13.1175 15.708 13.2995C15.7877 13.4814 15.7521 13.6932 15.6174 13.8391L9.6174 20.3391C9.46166 20.5079 9.21103 20.5488 9.00969 20.4385C8.80834 20.3281 8.70803 20.0948 8.76646 19.8728L9.8514 15.75H7C6.82503 15.75 6.66279 15.6585 6.57221 15.5088C6.48164 15.3591 6.4759 15.173 6.55708 15.018L9.30708 9.768ZM10.0525 10.5L7.82635 14.75H10.5C10.6551 14.75 10.8013 14.8219 10.896 14.9447C10.9907 15.0675 11.023 15.2273 10.9835 15.3772L10.2445 18.1854L14.108 14H11.5C11.3086 14 11.134 13.8907 11.0503 13.7186C10.9666 13.5464 10.9886 13.3416 11.1068 13.1911L13.2213 10.5H10.0525Z"
        fill={fill}
      />
    </svg>
  );
};

export const ImageIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M6.77779 2.5H17.2222C18.0435 2.5 18.6905 2.49999 19.2111 2.54253C19.7423 2.58593 20.1859 2.67609 20.589 2.88148C21.2475 3.21703 21.783 3.75247 22.1185 4.41103C22.3239 4.81414 22.4141 5.25771 22.4575 5.78889C22.5 6.30954 22.5 6.95651 22.5 7.77778V16.2222C22.5 17.0435 22.5 17.6905 22.4575 18.2111C22.4141 18.7423 22.3239 19.1859 22.1185 19.589C21.783 20.2475 21.2475 20.783 20.589 21.1185C20.1859 21.3239 19.7423 21.4141 19.2111 21.4575C18.6905 21.5 18.0435 21.5 17.2222 21.5H6.77778C5.95651 21.5 5.30954 21.5 4.78889 21.4575C4.25771 21.4141 3.81414 21.3239 3.41103 21.1185C2.75247 20.783 2.21703 20.2475 1.88148 19.589C1.67609 19.1859 1.58593 18.7423 1.54253 18.2111C1.49999 17.6905 1.5 17.0435 1.5 16.2222V7.77779C1.5 6.95652 1.49999 6.30955 1.54253 5.78889C1.58593 5.25771 1.67609 4.81414 1.88148 4.41103C2.21703 3.75247 2.75247 3.21703 3.41103 2.88148C3.81414 2.67609 4.25771 2.58593 4.78889 2.54253C5.30955 2.49999 5.95652 2.5 6.77779 2.5ZM4.87032 3.53921C4.40099 3.57756 4.10366 3.65089 3.86502 3.77248C3.39462 4.01217 3.01217 4.39462 2.77248 4.86502C2.65089 5.10366 2.57756 5.40099 2.53921 5.87032C2.50039 6.34549 2.5 6.95167 2.5 7.8V16.2C2.5 17.0483 2.50039 17.6545 2.53921 18.1297C2.57756 18.599 2.65089 18.8963 2.77248 19.135C3.01217 19.6054 3.39462 19.9878 3.86502 20.2275C3.90886 20.2499 3.95468 20.2706 4.00317 20.2897L10.5287 13.7642C10.7151 13.5778 10.8759 13.417 11.0191 13.2954C11.1703 13.1671 11.3345 13.0532 11.5365 12.9876C11.8377 12.8897 12.1623 12.8897 12.4635 12.9876C12.6655 13.0532 12.8297 13.1671 12.9809 13.2954C13.1242 13.417 13.285 13.5778 13.4713 13.7642L14 14.2929L16.5287 11.7642C16.7151 11.5778 16.8759 11.417 17.0191 11.2954C17.1703 11.1671 17.3345 11.0532 17.5365 10.9876C17.8377 10.8897 18.1623 10.8897 18.4635 10.9876C18.6655 11.0532 18.8297 11.1671 18.9809 11.2954C19.1241 11.417 19.2849 11.5778 19.4713 11.7642L21.5 13.7929V7.8C21.5 6.95167 21.4996 6.34549 21.4608 5.87032C21.4224 5.40099 21.3491 5.10366 21.2275 4.86502C20.9878 4.39462 20.6054 4.01217 20.135 3.77248C19.8963 3.65089 19.599 3.57756 19.1297 3.53921C18.6545 3.50039 18.0483 3.5 17.2 3.5H6.8C5.95167 3.5 5.34549 3.50039 4.87032 3.53921ZM21.5 15.2071L18.7778 12.4849C18.574 12.2811 18.4421 12.1497 18.3337 12.0577C18.2298 11.9695 18.1809 11.9473 18.1545 11.9387C18.0541 11.9061 17.9459 11.9061 17.8455 11.9387C17.8191 11.9473 17.7702 11.9695 17.6663 12.0577C17.5579 12.1497 17.426 12.2811 17.2222 12.4849L14.7071 15L19.9968 20.2897C20.0453 20.2706 20.0911 20.2499 20.135 20.2275C20.6054 19.9878 20.9878 19.6054 21.2275 19.135C21.3491 18.8963 21.4224 18.599 21.4608 18.1297C21.4996 17.6545 21.5 17.0483 21.5 16.2V15.2071ZM18.775 20.4821L12.7778 14.4849C12.574 14.2811 12.4421 14.1497 12.3337 14.0577C12.2298 13.9695 12.1809 13.9473 12.1545 13.9387C12.0541 13.9061 11.9459 13.9061 11.8455 13.9387C11.8191 13.9473 11.7702 13.9695 11.6663 14.0577C11.5579 14.1497 11.426 14.2811 11.2222 14.4849L5.22497 20.4821C5.64092 20.4998 6.14921 20.5 6.8 20.5H17.2C17.8508 20.5 18.3591 20.4998 18.775 20.4821ZM8 7.5C7.17157 7.5 6.5 8.17157 6.5 9C6.5 9.82843 7.17157 10.5 8 10.5C8.82843 10.5 9.5 9.82843 9.5 9C9.5 8.17157 8.82843 7.5 8 7.5ZM5.5 9C5.5 7.61929 6.61929 6.5 8 6.5C9.38071 6.5 10.5 7.61929 10.5 9C10.5 10.3807 9.38071 11.5 8 11.5C6.61929 11.5 5.5 10.3807 5.5 9Z"
        fill={fill}
      />
    </svg>
  );
};

export const UnLockIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.successIconColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13 7.352H3a.667.667 0 0 0-.667.666v6c0 .368.298.667.667.667h10a.667.667 0 0 0 .666-.667v-6A.667.667 0 0 0 13 7.352z"
        stroke={fill}
        strokeLinejoin="round"
      />
      <path
        d="M4.667 7.336V4.67c-.002-1.712 1.308-3.146 3.029-3.318a3.385 3.385 0 0 1 2.359.65M8 10v2"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const LockIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.errorColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13.333 7.333H2.667A.667.667 0 0 0 2 8v6c0 .368.298.667.667.667h10.666A.667.667 0 0 0 14 14V8a.667.667 0 0 0-.667-.667z"
        stroke={fill}
        strokeLinejoin="round"
      />
      <path
        d="M4.667 7.333V4.667a3.333 3.333 0 0 1 6.667 0v2.666M8 10v2"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const PersonalCollectionIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle
        cx="24"
        cy="11"
        r="7"
        fill="none"
        stroke={fill}
        strokeWidth="2"
        strokeLinecap="butt"
        strokeLinejoin="miter"
      />
      <path
        d="M4 41C4 32.1634 12.0589 25 22 25"
        stroke={fill}
        strokeWidth="1"
        strokeLinecap="butt"
        strokeLinejoin="miter"
      />
      <path
        d="M31.85 28C29.7237 28 28 30.0086 28 32.4864C28 36.9727 32.55 41.0513 35 42C37.45 41.0513 42 36.9727 42 32.4864C42 30.0086 40.2763 28 38.15 28C36.8479 28 35.6967 28.7533 35 29.9062C34.3033 28.7533 33.1521 28 31.85 28Z"
        fill="none"
        stroke={fill}
        strokeWidth="1"
        strokeLinecap="butt"
        strokeLinejoin="miter"
      />{" "}
    </svg>
  );
};

export const ExternalLinkIcon = ({ width = 18, height = 18 }: IIconProps) => {
  const theme = useTheme();
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 30 30`}
      width={width}
      height={height}
      fill={theme.primaryLinkColor}
    >
      <path d="M 25.980469 2.9902344 A 1.0001 1.0001 0 0 0 25.869141 3 L 20 3 A 1.0001 1.0001 0 1 0 20 5 L 23.585938 5 L 13.292969 15.292969 A 1.0001 1.0001 0 1 0 14.707031 16.707031 L 25 6.4140625 L 25 10 A 1.0001 1.0001 0 1 0 27 10 L 27 4.1269531 A 1.0001 1.0001 0 0 0 25.980469 2.9902344 z M 6 7 C 4.9069372 7 4 7.9069372 4 9 L 4 24 C 4 25.093063 4.9069372 26 6 26 L 21 26 C 22.093063 26 23 25.093063 23 24 L 23 14 L 23 11.421875 L 21 13.421875 L 21 16 L 21 24 L 6 24 L 6 9 L 14 9 L 16 9 L 16.578125 9 L 18.578125 7 L 16 7 L 14 7 L 6 7 z" />
    </svg>
  );
};

export const ReorderIcon = ({ width = 14, height = 14, fill }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M4.724 4.462a1.281 1.281 0 1 0 0-2.563 1.281 1.281 0 0 0 0 2.563zm6.523 0a1.282 1.282 0 1 0 0-2.563 1.282 1.282 0 0 0 0 2.563zm-5.241 3.93a1.281 1.281 0 1 1-2.563.001 1.281 1.281 0 0 1 2.563 0zm5.24 1.282a1.282 1.282 0 1 0 .001-2.563 1.282 1.282 0 0 0 0 2.563zm-5.24 3.93a1.282 1.282 0 1 1-2.563.001 1.282 1.282 0 0 1 2.563 0zm5.24 1.283a1.282 1.282 0 1 0 .001-2.563 1.282 1.282 0 0 0 0 2.563z"
        fill={fill}
      />
    </svg>
  );
};

export const DashboardIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M8.55778 16.2726C8.17184 16.2726 7.83824 15.9382 7.83824 15.5531C7.83824 15.1421 8.17262 14.8335 8.55778 14.8335H11.411C11.8219 14.8335 12.1305 15.1421 12.1305 15.5531C12.1305 15.939 11.8219 16.2726 11.411 16.2726H8.55778ZM17.8382 10.7202C17.4522 10.7202 17.1186 10.3858 17.1186 10.0007C17.1186 9.58972 17.453 9.28112 17.8382 9.28112H18.5319C18.4546 8.22722 18.1718 7.25072 17.7351 6.35072L17.1437 6.6851C16.8093 6.89056 16.3726 6.76244 16.1671 6.42806C15.9874 6.09368 16.0898 5.65696 16.4241 5.4515L17.0413 5.11712C16.7327 4.6804 16.3984 4.29446 16.039 3.9343C15.6788 3.57414 15.2937 3.24054 14.8819 2.95774L14.5218 3.54914C14.3421 3.88352 13.9046 4.01164 13.571 3.80618C13.2108 3.62649 13.1085 3.189 13.314 2.82962L13.6483 2.23822C12.7483 1.82728 11.746 1.54446 10.7179 1.46712V2.13588C10.7179 2.54682 10.3836 2.85542 9.9984 2.85542C9.58746 2.85542 9.27886 2.54682 9.27886 2.13588V1.46712C8.22496 1.54446 7.24846 1.82728 6.34846 2.23822L6.68284 2.82962C6.8883 3.18978 6.76018 3.6265 6.4258 3.80618C6.09142 4.01164 5.6547 3.88352 5.44924 3.54914L5.11486 2.95774C4.67814 3.24056 4.2922 3.57492 3.93204 3.9343C3.57188 4.29446 3.23828 4.67962 2.95548 5.11712L3.54688 5.4515C3.88126 5.65696 4.00938 6.09446 3.80392 6.42806C3.62423 6.76244 3.18674 6.89056 2.82736 6.6851L2.23596 6.35072C1.82502 7.25072 1.5422 8.22728 1.46486 9.28112H2.13362C2.54456 9.28112 2.85316 9.58972 2.85316 10.0007C2.85316 10.3866 2.54456 10.7202 2.13362 10.7202H1.46486C1.49064 11.2343 1.59377 11.7225 1.7219 12.2108C1.85081 12.7249 2.0305 13.1874 2.23596 13.6507L2.82736 13.3163C3.18752 13.1108 3.62424 13.2132 3.80392 13.5733C4.00938 13.9077 3.88126 14.3444 3.54688 14.5241L2.31328 15.2436C1.9789 15.4491 1.54218 15.321 1.33672 14.9866C0.9 14.2413 0.56562 13.4444 0.33438 12.596C0.12892 11.7476 0 10.8991 0 10C0 7.2234 1.13126 4.7296 2.9304 2.9304C4.72962 1.13118 7.2234 0 10 0C12.7508 0 15.2446 1.13124 17.0696 2.9304C18.8688 4.72962 20 7.2234 20 10C20 10.9 19.8711 11.7484 19.6398 12.596C19.4086 13.4444 19.0742 14.2413 18.6375 14.9866C18.4578 15.321 18.0203 15.4491 17.6867 15.2436L16.4531 14.5241C16.093 14.3444 15.9906 13.9069 16.1703 13.5733C16.3758 13.2132 16.8133 13.1108 17.1469 13.3163L17.7383 13.6507C17.9695 13.1882 18.1492 12.7249 18.2781 12.2108C18.407 11.7225 18.4836 11.2343 18.5351 10.7202H17.8382ZM9.99758 7.8412C10.332 7.8412 10.6405 7.91854 10.9234 8.07244L12.7741 6.22166C13.057 5.93884 13.4937 5.93884 13.7765 6.22166C14.0593 6.4787 14.0593 6.9412 13.7765 7.224L11.9257 9.07478C12.0546 9.3576 12.1312 9.66618 12.1312 10.0006C12.1312 10.592 11.8999 11.1318 11.514 11.517C11.128 11.9029 10.5882 12.1341 9.99758 12.1341C9.40618 12.1341 8.86634 11.9029 8.48118 11.517C8.09524 11.131 7.83822 10.5912 7.83822 10.0006C7.83822 9.40916 8.09526 8.86932 8.48118 8.48416C8.86712 8.09822 9.40696 7.8412 9.99758 7.8412ZM10.4859 9.48652C10.357 9.35761 10.1773 9.28106 9.99758 9.28106C9.79212 9.28106 9.61164 9.3584 9.48352 9.48652C9.35461 9.61543 9.27806 9.79512 9.27806 10.0006C9.27806 10.1803 9.3554 10.3607 9.48352 10.4889C9.61243 10.6178 9.79212 10.7201 9.99758 10.7201C10.1773 10.7201 10.3577 10.617 10.4859 10.4889C10.6148 10.36 10.7171 10.1803 10.7171 10.0006C10.7171 9.79512 10.614 9.61464 10.4859 9.48652Z"
        fill={fill}
      />
    </svg>
  );
};

export const RearrangeIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M17.5002 2.9974H12.5002V6.33073H17.5002V2.9974ZM12.5002 2.16406C12.0399 2.16406 11.6668 2.53716 11.6668 2.9974V6.33073C11.6668 6.79097 12.0399 7.16406 12.5002 7.16406H17.5002C17.9604 7.16406 18.3335 6.79097 18.3335 6.33073V2.9974C18.3335 2.53716 17.9604 2.16406 17.5002 2.16406H12.5002Z"
        fill={fill}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M17.5002 8.83073H12.5002V12.1641H17.5002V8.83073ZM12.5002 7.9974C12.0399 7.9974 11.6668 8.37049 11.6668 8.83073V12.1641C11.6668 12.6243 12.0399 12.9974 12.5002 12.9974H17.5002C17.9604 12.9974 18.3335 12.6243 18.3335 12.1641V8.83073C18.3335 8.37049 17.9604 7.9974 17.5002 7.9974H12.5002Z"
        fill={fill}
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M2.0835 5.4974C2.0835 4.80704 2.64314 4.2474 3.3335 4.2474H9.16683C9.39695 4.2474 9.5835 4.43394 9.5835 4.66406C9.5835 4.89418 9.39695 5.08073 9.16683 5.08073H3.3335C3.10338 5.08073 2.91683 5.26728 2.91683 5.4974V15.4974C2.91683 15.7275 3.10338 15.9141 3.3335 15.9141H8.16091L6.22055 13.9737C6.05783 13.811 6.05783 13.5472 6.22055 13.3845C6.38327 13.2217 6.64709 13.2217 6.80981 13.3845L9.46146 16.0361C9.62418 16.1988 9.62418 16.4626 9.46146 16.6254L6.80981 19.277C6.64709 19.4397 6.38327 19.4397 6.22055 19.277C6.05783 19.1143 6.05783 18.8505 6.22055 18.6878L8.16091 16.7474H3.3335C2.64314 16.7474 2.0835 16.1878 2.0835 15.4974V5.4974Z"
        fill="#0F1D2B"
      />
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M17.5002 14.6641H12.5002V17.9974H17.5002V14.6641ZM12.5002 13.8307C12.0399 13.8307 11.6668 14.2038 11.6668 14.6641V17.9974C11.6668 18.4576 12.0399 18.8307 12.5002 18.8307H17.5002C17.9604 18.8307 18.3335 18.4576 18.3335 17.9974V14.6641C18.3335 14.2038 17.9604 13.8307 17.5002 13.8307H12.5002Z"
        fill={fill}
      />
    </svg>
  );
};

// This intentionally doesn't have props
export const IonicPIMLogo = () => {
  return (
    <svg
      width="30"
      height="30"
      viewBox="0 0 30 30"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_1128_32537)">
        <path
          d="M12.0515 4.94794C12.0515 5.00868 12.1122 5.00868 12.1122 5.06941C12.5981 5.67679 13.2662 6.04121 14.1165 6.04121C15.2098 6.04121 16.1208 5.3731 16.4852 4.4013C19.8864 5.13015 22.8018 7.3167 24.4417 10.2928C24.8668 11.0824 25.5956 11.5683 26.5067 11.6898C24.9883 7.37744 21.2834 4.03688 16.6674 3.12581C16.4852 1.91106 15.4527 1 14.1773 1C13.0233 1 12.0515 1.78959 11.7478 2.88286V3.06508C11.6871 3.24729 11.6871 3.4295 11.6871 3.61171C11.5656 3.97614 11.7478 4.46204 12.0515 4.94794C12.0515 4.8872 11.9908 4.8872 12.0515 4.94794Z"
          fill="#3968FF"
        />
        <path
          d="M12.2342 11.9935V20.7397C12.2342 21.7115 13.0238 22.5011 13.9956 22.5011C14.9673 22.5011 15.7569 21.7115 15.7569 20.7397V11.9935C15.7569 11.0217 14.9673 10.2321 13.9956 10.2321C13.0238 10.2321 12.2342 11.0217 12.2342 11.9935Z"
          fill="#3968FF"
        />
        <path
          d="M27.7829 13.2083L27.7222 13.1475C27.297 12.9046 26.8111 12.7224 26.2645 12.7224C25.4749 12.7224 24.8068 13.0868 24.3209 13.6334C23.9565 14.0586 23.7136 14.666 23.7136 15.2733C23.7136 16.4881 24.5639 17.5206 25.7179 17.7636C24.8068 23.4122 19.9479 27.7245 14.1173 27.7245C11.9308 27.7245 9.86575 27.1172 8.10441 26.0239C7.43631 25.6595 6.64674 25.7202 6.10012 26.2061C8.34735 27.9675 11.1412 29 14.178 29C20.7982 29 26.2645 24.0803 27.1148 17.7029C28.1473 17.3992 28.8761 16.4274 28.8761 15.2733C28.8154 14.423 28.3903 13.6334 27.7829 13.2083Z"
          fill="#3968FF"
        />
        <path
          d="M6.52503 21.833C6.09988 20.9826 5.24957 20.3753 4.21706 20.3753C3.91338 20.3753 3.54896 20.436 3.24528 20.5575C2.63792 19.1605 2.2735 17.5813 2.2735 15.9414C2.2735 11.3254 4.94589 7.3167 8.833 5.3731C9.62257 5.00868 10.1692 4.27983 10.2907 3.4295C4.94589 4.94794 0.998047 9.92842 0.998047 15.8807C0.998047 17.8243 1.4232 19.6464 2.21277 21.3471C1.84835 21.7722 1.66614 22.2581 1.66614 22.8655C1.66614 24.2625 2.82013 25.4165 4.21706 25.4165H4.64221C4.70295 25.4165 4.76368 25.4165 4.76368 25.3557H4.82442C4.86491 25.3557 4.9054 25.3355 4.94589 25.295C5.97841 24.9306 6.70724 23.9588 6.70724 22.8655C6.76798 22.5011 6.6465 22.1367 6.52503 21.833Z"
          fill="#3968FF"
        />
      </g>
      <defs>
        <clipPath id="clip0_1128_32537">
          <rect
            width="27.8779"
            height="28"
            fill="white"
            transform="translate(1 1)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};

export const GridIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M19.5 3H4.5C3.67157 3 3 3.67157 3 4.5V19.5C3 20.3284 3.67157 21 4.5 21H19.5C20.3284 21 21 20.3284 21 19.5V4.5C21 3.67157 20.3284 3 19.5 3Z"
        stroke={fill}
        strokeLinejoin="round"
      />
      <path
        d="M10.5 6.5H6.5V10.5H10.5V6.5Z"
        stroke={fill}
        strokeLinejoin="round"
      />
      <path
        d="M17.5 6.5H13.5V10.5H17.5V6.5Z"
        stroke={fill}
        strokeLinejoin="round"
      />
      <path
        d="M10.5 13.5H6.5V17.5H10.5V13.5Z"
        stroke={fill}
        strokeLinejoin="round"
      />
      <path
        d="M17.5 13.5H13.5V17.5H17.5V13.5Z"
        stroke={fill}
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const PortfolioIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M9 3H3V7H9V3Z" stroke={fill} strokeLinejoin="round" />
      <path d="M21 3H12V21H21V3Z" stroke={fill} strokeLinejoin="round" />
      <path d="M9 10H3V14H9V10Z" stroke={fill} strokeLinejoin="round" />
      <path d="M9 17H3V21H9V17Z" stroke={fill} strokeLinejoin="round" />
    </svg>
  );
};

export const TeamsIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 17"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_6710_4211)">
        <path
          d="M10.6666 14.3333V12.9999C10.6666 12.2927 10.3856 11.6144 9.88554 11.1143C9.38544 10.6142 8.70716 10.3333 7.99992 10.3333H3.99992C3.29267 10.3333 2.6144 10.6142 2.1143 11.1143C1.6142 11.6144 1.33325 12.2927 1.33325 12.9999V14.3333M12.6666 5.66659V9.66659M14.6666 7.66659H10.6666M8.66659 4.99992C8.66659 6.47268 7.47268 7.66659 5.99992 7.66659C4.52716 7.66659 3.33325 6.47268 3.33325 4.99992C3.33325 3.52716 4.52716 2.33325 5.99992 2.33325C7.47268 2.33325 8.66659 3.52716 8.66659 4.99992Z"
          stroke={fill}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_6710_4211">
          <rect
            width="16"
            height="16"
            fill="white"
            transform="translate(0 0.333252)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};

export const DownloadDocumentIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.primaryIconColor;
  return (
    <svg
      style={{
        width: width,
        height: height,
        verticalAlign: "middle",
        fill: fill,
        overflow: "hidden",
      }}
      viewBox="0 0 1024 1024"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M275.681468 968.553688h182.140477a27.525872 27.525872 0 0 0 27.441321-27.441321 27.516477 27.516477 0 0 0-27.441321-27.441321H275.681468c-58.997431 0-107.022092-48.024661-107.022092-107.031486V195.010936c0-58.997431 48.015266-107.022092 107.022092-107.022092h487.424c58.997431 0 107.022092 48.015266 107.022092 107.022092v224.312367a27.525872 27.525872 0 0 0 27.441321 27.441321 27.516477 27.516477 0 0 0 27.441321-27.441321V195.010936c0-89.181945-72.713394-161.904734-161.89534-161.904734H275.681468c-89.181945 0-161.904734 72.722789-161.904734 161.904734v611.628624c0.685798 89.181945 72.722789 161.914128 161.904734 161.914128z" />
      <path d="M290.017468 294.141651h398.23266a27.525872 27.525872 0 0 0 27.441322-27.441321 27.516477 27.516477 0 0 0-27.441322-27.441321h-398.23266a27.525872 27.525872 0 0 0-27.441321 27.441321 27.525872 27.525872 0 0 0 27.441321 27.441321zM546.928734 483.985615a27.525872 27.525872 0 0 0-27.450716-27.441321H290.017468c-15.08756 0-27.441321 12.344367-27.441321 27.441321s12.344367 27.441321 27.441321 27.441321h228.784147c15.773358-0.009394 28.127119-12.353761 28.127119-27.441321zM290.017468 673.838972c-15.08756 0-27.441321 12.344367-27.441321 27.441322s12.344367 27.441321 27.441321 27.441321h70.317798c15.08756 0 27.441321-12.344367 27.441321-27.441321s-12.344367-27.441321-27.441321-27.441322h-70.317798zM715.353248 514.893505c-119.34767 0-217.125578 97.204844-217.125578 217.125578 0 119.920734 97.214239 217.134972 217.125578 217.134972 119.338275 0 217.134972-97.223633 217.134972-217.134972 0-119.338275-97.223633-217.125578-217.134972-217.125578z m0 383.643009c-91.756037 0-165.953761-74.18833-165.953762-165.953762 0-91.756037 74.761394-165.953761 165.953762-165.953761s165.953761 74.197725 165.953761 165.953761-74.207119 165.953761-165.953761 165.953762z" />
      <path d="M704.371083 832.108037c0.685798 0 0.685798 0 0 0l1.371596 1.371596 0.685798 0.676404s0.676404 0 0.676404 0.685798c0 0 0.685798 0 0.685798 0.685798 0 0 0.685798 0 0.685798 0.676404 0 0 0.676404 0 0.676404 0.685798 0 0 0.695193 0 0.695192 0.685798h12.344367s0.676404 0 0.676404-0.685798c0 0 0.685798 0 0.685798-0.685798 0 0 0.685798 0 0.685798-0.676404 0 0 0.676404 0 0.676404-0.685798 0 0 0.685798 0 0.685798-0.685798l0.685798-0.676404 0.685799-0.695193 0.685798-0.676403 93.982532-93.306129c6.857982-6.857982 6.857982-18.525945 0-25.383926-6.848587-6.848587-18.525945-6.848587-25.383927 0l-63.11222 63.121614V644.302679c0-9.601174-8.238972-17.840147-17.830752-17.840147-9.601174 0-17.840147 8.238972-17.840147 17.840147v132.236917L634.363303 713.417982c-6.857982-6.848587-18.525945-6.848587-25.383927 0-6.857982 6.857982-6.857982 18.525945 0 25.383926l95.391707 93.306129z" />
    </svg>
  );
};

export const SendIcon = () => (
  <svg
    width="20"
    height="21"
    viewBox="0 0 20 21"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g id="Icon / Action / Send">
      <path
        id="Fill 1"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M18.6677 9.63876L2.4519 2.59599C1.71504 2.26862 0.937127 2.96442 1.16906 3.74234L2.64335 8.76551C2.68441 8.94306 2.84809 9.07902 3.02564 9.12008L7.83083 10.0755C8.30857 10.1715 8.2947 10.8401 7.83084 10.9223L3.02523 11.8773C2.84768 11.9184 2.69732 12.041 2.64294 12.2319L1.16865 17.2551C0.936719 18.033 1.72796 18.7155 2.45149 18.4014L18.6677 11.3591C19.4318 11.0311 19.4318 9.96691 18.6677 9.63899L18.6677 9.63876Z"
        fill="#0F1D2B"
      />
    </g>
  </svg>
);

export const SendUpIcon = ({ width = 16, height = 16, stroke }: IIconProps) => {
  const theme = useTheme();
  const finalStroke = stroke ?? theme.primaryButtonTextColor;

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M8 13V3M8 3L4 7M8 3L12 7"
        stroke={finalStroke}
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const CollapseIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g id="Frame" clipPath="url(#clip0_4996_3888)">
      <path
        id="Vector"
        d="M1.5013 11.8333H8.16797V18.5"
        stroke="#0F1D2B"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        id="Vector_2"
        d="M18.5 8.16602H11.8333V1.49935"
        stroke="#0F1D2B"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
    <defs>
      <clipPath id="clip0_4996_3888">
        <rect width="20" height="20" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

export const ExpandIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g id="Frame">
      <path
        id="Vector"
        d="M9.16667 17.5004H2.5V10.8337"
        stroke="#0F1D2B"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        id="Vector_2"
        d="M10.8325 2.5H17.4992V9.16667"
        stroke="#0F1D2B"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  </svg>
);

export const ChatIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g id="Icon">
      <path
        id="Vector (Stroke)"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M1 3.5C1 2.94772 1.44772 2.5 2 2.5H22C22.5523 2.5 23 2.94772 23 3.5V18.5C23 19.0523 22.5523 19.5 22 19.5H12.7051L8.09578 22.9279C7.74846 23.1862 7.2744 23.1917 6.92115 22.9416C6.56791 22.6915 6.41567 22.2425 6.54395 21.8291L7.26668 19.5H2C1.44772 19.5 1 19.0523 1 18.5V3.5ZM3 4.5V17.5H8.62402C8.94179 17.5 9.24064 17.651 9.42912 17.9069C9.6176 18.1627 9.67327 18.4929 9.5791 18.7964L9.36289 19.4931L11.7773 17.6976C11.9498 17.5693 12.159 17.5 12.374 17.5H21V4.5H3Z"
        fill="#0F1D2B"
      />
    </g>
  </svg>
);

export const NewXIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g id="Icon / Action / Close">
      <path
        id="Combined Shape"
        fillRule="evenodd"
        clipRule="evenodd"
        d="M15.6196 4.3804C15.3345 4.09537 14.8729 4.09537 14.5879 4.3804L10.0001 8.96817L5.41211 4.3804C5.38836 4.35665 5.36338 4.33487 5.33738 4.31508C5.05137 4.09735 4.64173 4.11912 4.38045 4.3804C4.09541 4.66519 4.09541 5.12728 4.38045 5.41207L8.96846 9.99984L4.38045 14.5876C4.35669 14.6113 4.33492 14.6363 4.31513 14.6623C4.09739 14.9482 4.11917 15.3582 4.38045 15.6193C4.52296 15.7615 4.70974 15.8328 4.89628 15.8328C5.08282 15.8328 5.2696 15.7615 5.41211 15.619L10.0001 11.0313L14.5879 15.619C14.6117 15.6428 14.6366 15.6646 14.6626 15.6844C14.7927 15.7833 14.9483 15.8328 15.1037 15.8328C15.2903 15.8328 15.477 15.7615 15.6196 15.619C15.9046 15.3342 15.9046 14.8722 15.6196 14.5874L11.0318 9.99984L15.6196 5.41207C15.6433 5.38834 15.6651 5.36337 15.6849 5.33739C15.6849 5.33738 15.6849 5.33738 15.6849 5.33738C15.9026 5.05149 15.8808 4.64146 15.6196 4.3804Z"
        fill="#0F1D2B"
      />
    </g>
  </svg>
);

export const StopIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  const color = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="Icon">
        <path
          id="Stop"
          d="M6 4C5.44772 4 5 4.44772 5 5V19C5 19.5523 5.44772 20 6 20H18C18.5523 20 19 19.5523 19 19V5C19 4.44772 18.5523 4 18 4H6Z"
          fill={color}
        />
      </g>
    </svg>
  );
};

export const SelectAllIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useTheme();
  const color = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_1091_8620)">
        <path d="M24 0H0V24H24V0Z" fill="white" fillOpacity="0.01" />
        <path
          d="M17 2.5H4C3.17158 2.5 2.5 3.17158 2.5 4V17C2.5 17.8285 3.17158 18.5 4 18.5H17C17.8285 18.5 18.5 17.8285 18.5 17V4C18.5 3.17158 17.8285 2.5 17 2.5Z"
          stroke={color}
          strokeLinejoin="round"
        />
        <path
          d="M21.9999 6.50098V21C21.9999 21.5523 21.5522 22 20.9999 22H6.50171"
          stroke={color}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_1091_8620">
          <rect width={width} height={height} fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const DeSelectAllIcon = ({
  fill,
  width = 24,
  height = 24,
}: IIconProps) => {
  const theme = useTheme();
  const color = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clipPath="url(#clip0_1091_8634)">
        <path d="M24 0H0V24H24V0Z" fill="white" fillOpacity="0.01" />

        <path
          d="M17 2.5H4C3.17158 2.5 2.5 3.17158 2.5 4V17C2.5 17.8285 3.17158 18.5 4 18.5H17C17.8285 18.5 18.5 17.8285 18.5 17V4C18.5 3.17158 17.8285 2.5 17 2.5Z"
          stroke={color}
          strokeLinejoin="round"
        />
        <path
          d="M21.9999 6.50098V21C21.9999 21.5523 21.5522 22 20.9999 22H6.50171"
          stroke={color}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6.5 10.2429L9.49985 13.0055L14.5 7.85962"
          stroke={color}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_1091_8634">
          <rect width="24" height="24" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export const ImagePlaceHolderIcon = ({
  width = 32,
  height = 32,
}: IIconProps) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 33 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
    >
      <rect
        x="0.5"
        width="32"
        height="32"
        rx="4"
        fill="url(#pattern0_1610_261)"
        fillOpacity="0.5"
      />
      <defs>
        <pattern
          id="pattern0_1610_261"
          patternContentUnits="objectBoundingBox"
          width="1"
          height="1"
        >
          <use xlinkHref="#image0_1610_261" transform="scale(0.00125)" />
        </pattern>
        <image
          id="image0_1610_261"
          width="800"
          height="800"
          xlinkHref="data:image/png;base64,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"
        />
      </defs>
    </svg>
  );
};

export const NotApplicableIcon = ({
  width = 24,
  height = 24,
  fill,
}: IIconProps) => {
  const theme = useTheme();
  const color = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M12 21.9c5.468 0 9.9-4.432 9.9-9.9S17.468 2.1 12 2.1 2.1 6.532 2.1 12s4.432 9.9 9.9 9.9zm0 1.1c6.075 0 11-4.925 11-11S18.075 1 12 1 1 5.925 1 12s4.925 11 11 11z"
        fill={color}
      />
      <path
        d="M9.967 8.958v5.513h-.785l-2.876-4.043h-.052v4.043H5.4V8.958h.79l2.879 4.049h.052V8.958h.846zM13.251 8.7l-1.823 6.6h-.738l1.824-6.6h.737zM14.448 14.471h-.907l2.037-5.513h.986l2.036 5.513h-.906l-1.6-4.511h-.044l-1.602 4.511zm.152-2.159h2.94v.7H14.6v-.7z"
        fill={color}
      />
    </svg>
  );
};

export const ThreeDotContextMenuIcon = ({
  fill,
  width,
  height,
}: IIconProps) => {
  const theme = useTheme();
  const color = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      xmlns="http://www.w3.org/2000/svg"
      fill={color}
    >
      <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
    </svg>
  );
};

export const MailIcon = ({ fill, width = 24, height = 24 }: IIconProps) => {
  const theme = useTheme();
  const stroke = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g id="SVGRepo_bgCarrier" stroke-width="0"></g>
      <g
        id="SVGRepo_tracerCarrier"
        strokeLinecap="round"
        strokeLinejoin="round"
      ></g>
      <g id="SVGRepo_iconCarrier">
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M3.75 5.25L3 6V18L3.75 18.75H20.25L21 18V6L20.25 5.25H3.75ZM4.5 7.6955V17.25H19.5V7.69525L11.9999 14.5136L4.5 7.6955ZM18.3099 6.75H5.68986L11.9999 12.4864L18.3099 6.75Z"
          fill={stroke}
        ></path>
      </g>
    </svg>
  );
};

export const ViewIcon = ({ width = 32, height = 32, fill }: IIconProps) => {
  const theme = useTheme();
  const color = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1.66602 3.33325H18.3327M12.916 7.49992L14.166 8.74992L12.916 9.99992M7.08268 9.99992L5.83268 8.74992L7.08268 7.49992M6.66602 17.4999L9.99935 14.1666L13.3327 17.4999M3.33268 3.33325H16.666V14.1666H3.33268V3.33325Z"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const BellIcon = ({ fill, width, height }: IIconProps) => {
  const theme = useTheme();
  const color = fill ?? theme.primaryIconColor;
  return (
    <svg
      width={width ?? 20}
      height={height ?? 20}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M8.55672 17.5007C8.70301 17.7541 8.9134 17.9645 9.16676 18.1107C9.42011 18.257 9.70751 18.334 10.0001 18.334C10.2926 18.334 10.58 18.257 10.8334 18.1107C11.0867 17.9645 11.2971 17.7541 11.4434 17.5007M12.5001 6.6674H17.5001M15.0001 4.1674V9.1674M16.6684 12.0541C16.858 12.3065 17.0635 12.5467 17.2834 12.7732C17.3921 12.8927 17.4637 13.0411 17.4896 13.2006C17.5155 13.36 17.4945 13.5235 17.4291 13.6712C17.3638 13.8189 17.257 13.9444 17.1216 14.0325C16.9863 14.1206 16.8282 14.1675 16.6667 14.1674H3.33339C3.17189 14.1675 3.01385 14.1206 2.8785 14.0325C2.74315 13.9444 2.63631 13.8189 2.57098 13.6712C2.50565 13.5235 2.48465 13.36 2.51052 13.2006C2.53639 13.0411 2.60803 12.8927 2.71672 12.7732C3.82505 11.6307 5.00005 10.4166 5.00005 6.6674C5.00015 5.80988 5.22079 4.9668 5.64077 4.21917C6.06074 3.47154 6.66594 2.84448 7.39821 2.39826C8.13048 1.95203 8.96521 1.70164 9.82219 1.67113C10.6792 1.64063 11.5296 1.83104 12.2917 2.22407"
        stroke={color}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const BookmarkPlusIcon = ({ width = 24, height = 24 }: IIconProps) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.6673 14L8.00065 11.3333L3.33398 14V3.33333C3.33398 2.97971 3.47446 2.64057 3.72451 2.39052C3.97456 2.14048 4.3137 2 4.66732 2H11.334C11.6876 2 12.0267 2.14048 12.2768 2.39052C12.5268 2.64057 12.6673 2.97971 12.6673 3.33333V14Z"
        stroke="#3F4A55"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M8 4.66699V8.66699"
        stroke="#3F4A55"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 6.66699H6"
        stroke="#3F4A55"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const BookmarkIcon = ({ width = 24, height = 24 }: IIconProps) => {
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M12.6654 14L7.9987 11.3333L3.33203 14V3.33333C3.33203 2.97971 3.47251 2.64057 3.72256 2.39052C3.9726 2.14048 4.31174 2 4.66536 2H11.332C11.6857 2 12.0248 2.14048 12.2748 2.39052C12.5249 2.64057 12.6654 2.97971 12.6654 3.33333V14Z"
        stroke="#3F4A55"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const CardCalendarIcon = ({
  stroke,
  width = 12,
  height = 13,
}: IIconProps) => {
  const theme = useTheme();
  const strokeColor = stroke || theme.resolve("color_cardProduct_icon");

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 12 13"
      fill="none"
    >
      <g clipPath="url(#clip0_calendar_icon)">
        <path
          d="M4 1.8125V3.8125"
          stroke={strokeColor}
          strokeWidth="0.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M8 1.8125V3.8125"
          stroke={strokeColor}
          strokeWidth="0.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M9.5 2.8125H2.5C1.94772 2.8125 1.5 3.26022 1.5 3.8125V10.8125C1.5 11.3648 1.94772 11.8125 2.5 11.8125H9.5C10.0523 11.8125 10.5 11.3648 10.5 10.8125V3.8125C10.5 3.26022 10.0523 2.8125 9.5 2.8125Z"
          stroke={strokeColor}
          strokeWidth="0.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M1.5 5.8125H10.5"
          stroke={strokeColor}
          strokeWidth="0.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_calendar_icon">
          <rect
            width="12"
            height="12"
            fill="white"
            transform="translate(0 0.8125)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};

export const CardCompletionBadgeIcon = ({
  stroke,
  width = 12,
  height = 13,
}: IIconProps) => {
  const theme = useTheme();
  const strokeColor = stroke || theme.resolve("color_cardProduct_icon");

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 12 13"
      fill="none"
    >
      <g clipPath="url(#clip0_completion_badge)">
        <path
          d="M4.5001 6.81255L5.5001 7.81255L7.5001 5.81255M1.9251 5.12255C1.85212 4.79381 1.86332 4.45197 1.95767 4.12872C2.05203 3.80547 2.22647 3.51127 2.46483 3.27341C2.70319 3.03555 2.99775 2.86172 3.3212 2.76805C3.64465 2.67437 3.98651 2.66388 4.3151 2.73755C4.49595 2.4547 4.7451 2.22193 5.03957 2.06069C5.33405 1.89945 5.66437 1.81494 6.0001 1.81494C6.33582 1.81494 6.66615 1.89945 6.96062 2.06069C7.25509 2.22193 7.50424 2.4547 7.6851 2.73755C8.01418 2.66356 8.35663 2.674 8.6806 2.76791C9.00456 2.86181 9.29951 3.03612 9.53802 3.27463C9.77653 3.51313 9.95084 3.80809 10.0447 4.13205C10.1386 4.45601 10.1491 4.79846 10.0751 5.12755C10.3579 5.3084 10.5907 5.55755 10.752 5.85202C10.9132 6.1465 10.9977 6.47682 10.9977 6.81255C10.9977 7.14827 10.9132 7.4786 10.752 7.77307C10.5907 8.06755 10.3579 8.31669 10.0751 8.49755C10.1488 8.82613 10.1383 9.168 10.0446 9.49145C9.95092 9.81489 9.7771 10.1095 9.53924 10.3478C9.30137 10.5862 9.00718 10.7606 8.68393 10.855C8.36067 10.9493 8.01883 10.9605 7.6901 10.8875C7.50948 11.1715 7.26014 11.4053 6.96516 11.5672C6.67018 11.7292 6.33911 11.8141 6.0026 11.8141C5.66608 11.8141 5.33501 11.7292 5.04003 11.5672C4.74506 11.4053 4.49572 11.1715 4.3151 10.8875C3.98651 10.9612 3.64465 10.9507 3.3212 10.8571C2.99775 10.7634 2.70319 10.5896 2.46483 10.3517C2.22647 10.1138 2.05203 9.81963 1.95767 9.49638C1.86332 9.17313 1.85212 8.83129 1.9251 8.50255C1.64007 8.32217 1.4053 8.07264 1.24262 7.77716C1.07994 7.48168 0.994629 7.14985 0.994629 6.81255C0.994629 6.47525 1.07994 6.14342 1.24262 5.84794C1.4053 5.55246 1.64007 5.30293 1.9251 5.12255Z"
          stroke={strokeColor}
          strokeWidth="0.75"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id="clip0_completion_badge">
          <rect
            width="12"
            height="12"
            fill="white"
            transform="translate(0 0.8125)"
          />
        </clipPath>
      </defs>
    </svg>
  );
};

export const VariantProductIcon = ({
  width = 16,
  height = 16,
  fill,
}: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.resolve("color_icon_secondary");
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        d="M7.67623 14.4868L7.43341 14.9239L7.43342 14.9239L7.67623 14.4868ZM2.3429 11.5238L2.58573 11.0867L2.5857 11.0867L2.3429 11.5238ZM2.34291 4.47615L2.5857 4.91324L2.58573 4.91323L2.34291 4.47615ZM7.67623 1.51319L7.43343 1.0761L7.43341 1.07611L7.67623 1.51319ZM8.32377 1.51319L8.56659 1.07611L8.56657 1.0761L8.32377 1.51319ZM13.6571 4.47615L13.4143 4.91323L13.4143 4.91324L13.6571 4.47615ZM13.6571 11.5238L13.4143 11.0867L13.4143 11.0867L13.6571 11.5238ZM8.32377 14.4868L8.56658 14.9239L8.56659 14.9239L8.32377 14.4868ZM7.67623 14.4868L7.91906 14.0497L2.58573 11.0867L2.3429 11.5238L2.10008 11.9609L7.43341 14.9239L7.67623 14.4868ZM2.3429 11.5238L2.5857 11.0867C2.53283 11.0574 2.5 11.0016 2.5 10.9411H2H1.5C1.5 11.3647 1.72969 11.7551 2.1001 11.9609L2.3429 11.5238ZM2 10.9411H2.5V5.05892H2H1.5V10.9411H2ZM2 5.05892H2.5C2.5 4.99839 2.53283 4.94262 2.5857 4.91324L2.34291 4.47615L2.10011 4.03906C1.72969 4.24482 1.5 4.63525 1.5 5.05892H2ZM2.34291 4.47615L2.58573 4.91323L7.91906 1.95026L7.67623 1.51319L7.43341 1.07611L2.10008 4.03907L2.34291 4.47615ZM7.67623 1.51319L7.91904 1.95027C7.9694 1.9223 8.0306 1.9223 8.08096 1.95027L8.32377 1.51319L8.56657 1.0761C8.2142 0.880355 7.7858 0.880355 7.43343 1.0761L7.67623 1.51319ZM8.32377 1.51319L8.08095 1.95026L13.4143 4.91323L13.6571 4.47615L13.8999 4.03907L8.56659 1.07611L8.32377 1.51319ZM13.6571 4.47615L13.4143 4.91324C13.4672 4.94261 13.5 4.99839 13.5 5.05892H14H14.5C14.5 4.63525 14.2703 4.24482 13.8999 4.03907L13.6571 4.47615ZM14 5.05892H13.5V10.9411H14H14.5V5.05892H14ZM14 10.9411H13.5C13.5 11.0016 13.4672 11.0574 13.4143 11.0867L13.6571 11.5238L13.8999 11.9609C14.2703 11.7551 14.5 11.3647 14.5 10.9411H14ZM13.6571 11.5238L13.4143 11.0867L8.08095 14.0497L8.32377 14.4868L8.56659 14.9239L13.8999 11.9609L13.6571 11.5238ZM8.32377 14.4868L8.08095 14.0497C8.03059 14.0777 7.96941 14.0777 7.91905 14.0497L7.67623 14.4868L7.43342 14.9239C7.78579 15.1196 8.21421 15.1196 8.56658 14.9239L8.32377 14.4868ZM10 7.99999H9.5C9.5 8.82841 8.82843 9.49999 8 9.49999V9.99999V10.5C9.38071 10.5 10.5 9.3807 10.5 7.99999H10ZM8 9.99999V9.49999C7.17157 9.49999 6.5 8.82841 6.5 7.99999H6H5.5C5.5 9.3807 6.61929 10.5 8 10.5V9.99999ZM6 7.99999H6.5C6.5 7.17156 7.17157 6.49999 8 6.49999V5.99999V5.49999C6.61929 5.49999 5.5 6.61927 5.5 7.99999H6ZM8 5.99999V6.49999C8.82843 6.49999 9.5 7.17156 9.5 7.99999H10H10.5C10.5 6.61927 9.38071 5.49999 8 5.49999V5.99999Z"
        fill={fill}
      />
    </svg>
  );
};

export const MasterProductIcon = ({
  width = 16,
  height = 16,
  fill,
}: IIconProps) => {
  const theme = useTheme();
  fill = fill ?? theme.resolve("color_icon_secondary");
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        d="M8.68606 13.7737C8.48728 13.8828 8.26417 13.94 8.03741 13.94C7.81064 13.94 7.58754 13.8828 7.38876 13.7737L3.37926 11.2373C3.17146 11.1237 2.99798 10.9564 2.87698 10.7529C2.75597 10.5493 2.69188 10.317 2.69141 10.0802V5.7529C2.69141 5.27235 2.95455 4.82982 3.37926 4.59638L7.38876 2.23226C7.59341 2.11942 7.82331 2.06024 8.05701 2.06024C8.29071 2.06024 8.5206 2.11942 8.72526 2.23226L12.7348 4.59638H12.7152C13.131 4.83279 13.3864 5.27532 13.3834 5.75349V8.297"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10.4121 12.158H13.9761"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M12.1953 10.3759V13.9399"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.9725 10.16C9.16544 10.16 10.1325 9.19293 10.1325 8C10.1325 6.80706 9.16544 5.84 7.9725 5.84C6.77956 5.84 5.8125 6.80706 5.8125 8C5.8125 9.19293 6.77956 10.16 7.9725 10.16Z"
        stroke={fill}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const CircleCheckIcon = ({
  fill,
  width = 20,
  height = 20,
}: IIconProps) => {
  const strokeColor = fill ?? "#1FB45A";
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.0001 18.3337C14.6025 18.3337 18.3334 14.6027 18.3334 10.0003C18.3334 5.39795 14.6025 1.66699 10.0001 1.66699C5.39771 1.66699 1.66675 5.39795 1.66675 10.0003C1.66675 14.6027 5.39771 18.3337 10.0001 18.3337Z"
        stroke={strokeColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M7.5 9.99967L9.16667 11.6663L12.5 8.33301"
        stroke={strokeColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const CompanyIconV2 = ({
  fill,
  width = 20,
  height = 20,
}: IIconProps) => {
  const theme = useTheme();
  const strokeColor = fill ?? theme.primaryTextColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.00008 18.3337V3.33366C5.00008 2.89163 5.17568 2.46771 5.48824 2.15515C5.8008 1.84259 6.22472 1.66699 6.66675 1.66699H13.3334C13.7754 1.66699 14.1994 1.84259 14.5119 2.15515C14.8245 2.46771 15.0001 2.89163 15.0001 3.33366V18.3337M5.00008 18.3337H15.0001M5.00008 18.3337H3.33341C2.89139 18.3337 2.46746 18.1581 2.1549 17.8455C1.84234 17.5329 1.66675 17.109 1.66675 16.667V11.667C1.66675 11.225 1.84234 10.801 2.1549 10.4885C2.46746 10.1759 2.89139 10.0003 3.33341 10.0003H5.00008M15.0001 18.3337H16.6667C17.1088 18.3337 17.5327 18.1581 17.8453 17.8455C18.1578 17.5329 18.3334 17.109 18.3334 16.667V9.16699C18.3334 8.72497 18.1578 8.30104 17.8453 7.98848C17.5327 7.67592 17.1088 7.50033 16.6667 7.50033H15.0001M8.33342 5.00033H11.6667M8.33342 8.33366H11.6667M8.33342 11.667H11.6667M8.33342 15.0003H11.6667"
        stroke={strokeColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const InfoCircleIcon = ({
  fill,
  width = 20,
  height = 20,
}: IIconProps) => {
  const strokeColor = fill ?? "#0090E3";
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M10.0001 18.3337C14.6025 18.3337 18.3334 14.6027 18.3334 10.0003C18.3334 5.39795 14.6025 1.66699 10.0001 1.66699C5.39771 1.66699 1.66675 5.39795 1.66675 10.0003C1.66675 14.6027 5.39771 18.3337 10.0001 18.3337Z"
        stroke={strokeColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 13.3333V10"
        stroke={strokeColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M10 6.66699H10.0083"
        stroke={strokeColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const ProductsIconV2 = ({
  fill,
  width = 16,
  height = 16,
}: IIconProps) => {
  const theme = useTheme();
  const fillColor = fill ?? theme.secondaryTextColor;
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.67623 14.4872L7.43341 14.9243L7.43342 14.9243L7.67623 14.4872ZM2.3429 11.5242L2.58573 11.0871L2.5857 11.0871L2.3429 11.5242ZM2.34291 4.47655L2.5857 4.91364L2.58573 4.91363L2.34291 4.47655ZM7.67623 1.51358L7.43343 1.07649L7.43341 1.0765L7.67623 1.51358ZM8.32377 1.51358L8.56659 1.0765L8.56657 1.07649L8.32377 1.51358ZM13.6571 4.47655L13.4143 4.91363L13.4143 4.91363L13.6571 4.47655ZM13.6571 11.5242L13.4143 11.0871L13.4143 11.0871L13.6571 11.5242ZM8.32377 14.4872L8.56658 14.9243L8.56659 14.9243L8.32377 14.4872ZM7.67623 14.4872L7.91906 14.0501L2.58573 11.0871L2.3429 11.5242L2.10008 11.9613L7.43341 14.9243L7.67623 14.4872ZM2.3429 11.5242L2.5857 11.0871C2.53283 11.0578 2.5 11.002 2.5 10.9415H2H1.5C1.5 11.3651 1.72969 11.7555 2.1001 11.9613L2.3429 11.5242ZM2 10.9415H2.5V5.05932H2H1.5V10.9415H2ZM2 5.05932H2.5C2.5 4.99878 2.53283 4.94301 2.5857 4.91364L2.34291 4.47655L2.10011 4.03946C1.72969 4.24522 1.5 4.63565 1.5 5.05932H2ZM2.34291 4.47655L2.58573 4.91363L7.91906 1.95066L7.67623 1.51358L7.43341 1.0765L2.10008 4.03947L2.34291 4.47655ZM7.67623 1.51358L7.91904 1.95067C7.9694 1.92269 8.0306 1.92269 8.08096 1.95067L8.32377 1.51358L8.56657 1.07649C8.2142 0.880752 7.7858 0.880752 7.43343 1.07649L7.67623 1.51358ZM8.32377 1.51358L8.08095 1.95066L13.4143 4.91363L13.6571 4.47655L13.8999 4.03947L8.56659 1.0765L8.32377 1.51358ZM13.6571 4.47655L13.4143 4.91363C13.4672 4.94301 13.5 4.99879 13.5 5.05932H14H14.5C14.5 4.63564 14.2703 4.24522 13.8999 4.03946L13.6571 4.47655ZM14 5.05932H13.5V10.9415H14H14.5V5.05932H14ZM14 10.9415H13.5C13.5 11.002 13.4672 11.0578 13.4143 11.0871L13.6571 11.5242L13.8999 11.9613C14.2703 11.7555 14.5 11.3651 14.5 10.9415H14ZM13.6571 11.5242L13.4143 11.0871L8.08095 14.0501L8.32377 14.4872L8.56659 14.9243L13.8999 11.9613L13.6571 11.5242ZM8.32377 14.4872L8.08095 14.0501C8.03059 14.0781 7.96941 14.0781 7.91905 14.0501L7.67623 14.4872L7.43342 14.9243C7.78579 15.12 8.21421 15.12 8.56658 14.9243L8.32377 14.4872ZM10 8.00038H9.5C9.5 8.82881 8.82843 9.50038 8 9.50038V10.0004V10.5004C9.38071 10.5004 10.5 9.38109 10.5 8.00038H10ZM8 10.0004V9.50038C7.17157 9.50038 6.5 8.82881 6.5 8.00038H6H5.5C5.5 9.38109 6.61929 10.5004 8 10.5004V10.0004ZM6 8.00038H6.5C6.5 7.17196 7.17157 6.50038 8 6.50038V6.00038V5.50038C6.61929 5.50038 5.5 6.61967 5.5 8.00038H6ZM8 6.00038V6.50038C8.82843 6.50038 9.5 7.17196 9.5 8.00038H10H10.5C10.5 6.61967 9.38071 5.50038 8 5.50038V6.00038Z"
        fill={fillColor}
      />
    </svg>
  );
};

export const FlagIcon = ({ fill, width = 16, height = 16 }: IIconProps) => {
  const theme = useTheme();
  const strokeColor = fill ?? theme.resolve("color_icon_brand");
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M3.3335 9.33372C3.95659 8.72296 4.79432 8.38086 5.66683 8.38086C6.53934 8.38086 7.37707 8.72296 8.00016 9.33372C8.62326 9.94447 9.46099 10.2866 10.3335 10.2866C11.206 10.2866 12.0437 9.94447 12.6668 9.33372V3.33372C12.0437 3.94447 11.206 4.28657 10.3335 4.28657C9.46099 4.28657 8.62326 3.94447 8.00016 3.33372C7.37707 2.72296 6.53934 2.38086 5.66683 2.38086C4.79432 2.38086 3.95659 2.72296 3.3335 3.33372V9.33372ZM3.3335 9.33372V14.0004"
        stroke={strokeColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const FlagXIcon = ({ fill, width = 16, height = 16 }: IIconProps) => {
  const theme = useTheme();
  const strokeColor = fill ?? theme.resolve("color_icon_error");
  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M9.02265 10.0191C8.64232 9.85617 8.29572 9.62375 8.00065 9.33372C7.37755 8.72296 6.53983 8.38086 5.66732 8.38086C4.79481 8.38086 3.95708 8.72296 3.33398 9.33372M3.33398 9.33372V3.33372C3.95708 2.72296 4.79481 2.38086 5.66732 2.38086C6.53983 2.38086 7.37755 2.72296 8.00065 3.33372C8.62375 3.94447 9.46147 4.28657 10.334 4.28657C11.2065 4.28657 12.0442 3.94447 12.6673 3.33372V9.00038M3.33398 9.33372V14.0004M14.6673 14.667L11.334 11.3337M11.334 14.667L14.6673 11.3337"
        stroke={strokeColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const CloneIcon = ({ fill, width = 16, height = 16 }: IIconProps) => {
  const theme = useTheme();
  const strokeColor = fill ?? theme.resolve("color_icon_primary");
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        d="M2.67467 11.158C2.47 11.0417 2.29977 10.8733 2.18127 10.6699C2.06277 10.4665 2.00023 10.2354 2 10V3.33333C2 2.6 2.6 2 3.33333 2H10C10.5 2 10.772 2.25667 11 2.66667M7.33333 9.33333H11.3333M9.33333 7.33333V11.3333M4.66667 6.44467C4.66667 5.97311 4.85399 5.52087 5.18743 5.18743C5.52087 4.85399 5.97311 4.66667 6.44467 4.66667H12.222C12.4555 4.66667 12.6867 4.71266 12.9024 4.80201C13.1181 4.89136 13.3141 5.02233 13.4792 5.18743C13.6443 5.35253 13.7753 5.54854 13.8647 5.76426C13.954 5.97997 14 6.21118 14 6.44467V12.222C14 12.4555 13.954 12.6867 13.8647 12.9024C13.7753 13.1181 13.6443 13.3141 13.4792 13.4792C13.3141 13.6443 13.1181 13.7753 12.9024 13.8647C12.6867 13.954 12.4555 14 12.222 14H6.44467C6.21118 14 5.97997 13.954 5.76426 13.8647C5.54854 13.7753 5.35253 13.6443 5.18743 13.4792C5.02233 13.3141 4.89136 13.1181 4.80201 12.9024C4.71266 12.6867 4.66667 12.4555 4.66667 12.222V6.44467Z"
        stroke={strokeColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const ResubmitApprovalIcon = ({
  fill,
  width = 16,
  height = 16,
}: IIconProps) => {
  const theme = useTheme();
  const strokeColor = fill ?? theme.resolve("color_icon_primary");
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 16 16"
      fill="none"
    >
      <path
        d="M3.33333 14H12.6667M14 11.9H2C2 9.2 2.94733 9.2 4.52667 9.2C8 9.2 5.33333 6.14 5.33333 4.66667C5.33333 3.95942 5.61428 3.28115 6.11438 2.78105C6.61448 2.28095 7.29276 2 8 2C8.70724 2 9.38552 2.28095 9.88562 2.78105C10.3857 3.28115 10.6667 3.95942 10.6667 4.66667C10.6667 6.14 8 9.2 11.4733 9.2C13.0527 9.2 14 9.2 14 11.9Z"
        stroke={strokeColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const BoltIcon = ({ fill, width = 16, height = 16 }: IIconProps) => {
  const theme = useTheme();
  const strokeColor = fill ?? theme.resolve("color_icon_success");
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M13 3V10H19L11 21V14H5L13 3Z"
        stroke={strokeColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

export const ResyncIcon = ({ fill, width = 16, height = 16 }: IIconProps) => {
  const theme = useTheme();
  const strokeColor = fill ?? theme.resolve("color_icon_primary");
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={width}
      height={height}
      viewBox="0 0 20 20"
      fill="none"
    >
      <path
        d="M16.6666 9.16658C16.4628 7.7001 15.7825 6.3413 14.7304 5.2995C13.6784 4.25769 12.313 3.59068 10.8446 3.40121C9.37624 3.21174 7.88627 3.51032 6.60425 4.25096C5.32224 4.99159 4.31929 6.1332 3.74992 7.49992M3.33325 4.16658V7.49992H6.66659M3.33325 10.8333C3.53705 12.2997 4.21736 13.6585 5.26939 14.7003C6.32141 15.7421 7.68679 16.4092 9.15519 16.5986C10.6236 16.7881 12.1136 16.4895 13.3956 15.7489C14.6776 15.0082 15.6805 13.8666 16.2499 12.4999M16.6666 15.8333V12.4999H13.3333"
        stroke={strokeColor}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};
